/**
 * KB-1869 (TL-5) — Review Inbox service: the query, the verdict callable client, and the pure
 * card/verdict helpers. Design law #1 (ADR §Operator UX): trust data is ROUTING, not decoration — this
 * layer surfaces only what a card needs to render the change + judge it; NO tier/agent/stats data.
 */
import {
  collection,
  doc,
  documentId,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  sum,
  where,
  type Firestore,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db as defaultDb } from "@/services/firebaseService";
import { fetchJsonArtifact } from "@/components/admin/camp/camp-data/reviewAccept/runArtifacts";
import { isAppliableOfferField } from "@/services/campDataChangeSet";
import { actorUidOrNull } from "@/services/campDataFieldState";
import { withoutPatternIdParam } from "@/services/reviewPatternLinks";
import { readPriceLevel, recordsPriceLevel, type PriceLevel } from "@/services/campDataPriceLevel";
import type { CampDataReviewFlag, CampDataReviewPatternDocV1 } from "@/types/campData";

export const REVIEW_PATTERNS_COLLECTION = "campDataReviewPatterns";
export const RECORD_VERDICT_CALLABLE = "recordCampDataPatternVerdict";
// KB-2179 — the server-side reachability probe backing the inspection gate (see its callable core
// for the honest limit: it proves the URL serves a document, not what rendered in the tab).
export const PROBE_URL_REACHABILITY_CALLABLE = "probeCampDataUrlReachability";
// KB-2181 — the judge's Q1 verdict, fetched only after the operator commits (the reveal rule).
export const JUDGE_VERDICT_CALLABLE = "getCampDataJudgeVerdict";
/**
 * The region every camp-data callable is deployed to, and the ONE home for it on the browser side.
 *
 * Exported since KB-3198 so `campDataProblemReports.ts` reaches this value rather than declaring a
 * second `"us-east4"`. Two literals for one region is the drift `OMISSION_NOTE_MAX`'s docblock
 * describes, with a worse failure: `getFunctions` on the wrong region does not error, it resolves a
 * URL for a function that is not there, so the operator gets a generic `internal` from a control that
 * looks correctly wired. `REGION` in `recordPatternVerdictCore.ts` is the server-side counterpart.
 */
export const FUNCTIONS_REGION = "us-east4";
/** The reconcile review-queue artifact the pattern's items expand from (KB-1867 itemsArtifact target). */
// KB-1890 — the full run audit (proposedNew ∪ review.proposals + changedOffers) so "Judge individually"
// enriches change rows with title/dates/venue/links (the review-queue alone carries only docId/from/to).
const AUDIT_ARTIFACT = "reconcile/observed_to_offers_audit.json";

export interface ReviewPattern extends CampDataReviewPatternDocV1 {
  /** Firestore doc id = sha1(`${campSlug}|${changeGroupKey}`). */
  id: string;
}

/**
 * KB-2692 — the collapse this file was promised.
 *
 * KB-2655 declared an `AgeBand` here while its PR was open, saying "when KB-2658 merges, this
 * declaration and that one collapse into one — they are deliberately identical, so the merge is a
 * deletion." Both landed, neither deleted, and they were NOT identical: this one spelled the grade
 * pair `minGradeKey`/`minGradeValue`, the one below `minAgeGradeKey`/`minAgeGradeValue`. Two
 * `export interface AgeBand` declarations in one module do not collide — TypeScript merges them —
 * so the mismatch compiled green while `gradeBandOf` (which reads the `minAge*` spelling) saw
 * nothing on any row the full-N callable produced, and grade annotations silently vanished from the
 * "Judge individually" list while still rendering on sample rows.
 *
 * One declaration now, below, in the pipeline's own `variationData` spelling. `readObservedBand`
 * normalises the callable's wire spelling into it at the boundary.
 */

/**
 * KB-3317 — the standing of the sampled item's field value, mirrored off `CampDataReviewPatternDocV1`'s
 * per-sample `provenance` block (KB-3301). The type lives here rather than in `campData.ts` so the card
 * reads a sample without a cast and this file owns the shape it renders; `canonical` is the current
 * winner, `basis` the value the last verdict judged. `basis`'s display fields (`value`/`runId`/
 * `vendorType`/`observedAt`) come from the basis contribution and are each independently optional —
 * `value` is ABSENT (never an empty string) when the contribution is missing or spilled to GCS.
 */
export interface ItemBasis {
  cid: string | null;
  valueHash: string | null;
  judgedAt: string | null;
  value?: string;
  runId?: string;
  vendorType?: string;
  observedAt?: string;
}
export interface ItemProvenance {
  policy: string | null;
  canonical: {
    valueHash: string | null;
    sourceClass: string | null;
    actorId: string | null;
    at: string | null;
    cid: string | null;
  };
  basis?: ItemBasis;
}

/** A card sample / exploded item — the thin display shape shared by sampleItems and artifact rows. */
export interface InboxItem {
  /** Stable per-item key used for inspection tracking + single-mode verdicts (observedId preferred). */
  itemId: string;
  offerId?: string;
  observedId?: string;
  title?: string;
  startDateIso?: string;
  endDateIso?: string;
  locationId?: string;
  locationText?: string; // KB-1890 — human venue text (shown instead of the raw locationId)
  url?: string;
  regUrl?: string;
  variationKey?: string; // KB-1894 — reviewer info-density
  price?: number; // KB-1894 — creation: preview.priceRegular; change: changedOffers.priceRegular
  /**
   * KB-2874 — the adapter's "a human should look at this row" flags (holiday-dated, spans a date the
   * camp is believed closed). Carried from the observe layer, never recomputed here.
   *
   * This is the field that makes the pattern card's "Open the items to see which" a true sentence.
   * Round 1 finding 1: every item projection dropped it, so an operator told "4 of 500 flagged"
   * clicked through and got 500 rows rendering identically — the ticket's own defect, one layer down.
   *
   * ⚠️ THE COUNT OF PROJECTIONS HAS BEEN WRONG TWICE. Round 1 said "3 of 3" and missed two RENDER
   * sites on the card itself; round 2 fixed those and still said "3", and round 3 found a FOURTH
   * projection — `assembleProposedNewSessionRows` (`services/campDataChangeSet/proposedNewSessions.ts`),
   * feeding a bulk-adopt table with an "Adopt all" button. The evidence originally offered for
   * completeness was `grep -rn reviewFlags`, which is structurally incapable of finding a surface that
   * DROPS the field: an allowlist projection never names what it omits. If you add a consumer of
   * `audit.proposedNew[]`, enumerate the allowlists — do not grep for this symbol and conclude.
   */
  reviewFlags?: CampDataReviewFlag[];
  /**
   * KB-2658 — what the VENDOR PAGE said about this session's eligibility. Absent when the source
   * stated nothing, which is a real and common state (autism-society's Winter Camp rows).
   */
  observedBand?: AgeBand;
  /**
   * KB-2658 — the band the created/updated offer actually carries: `preview.variationData` on a
   * creation row (post-merge, so `allowSessionAgeOverride` is already applied), the stored offer's
   * band on a change row. Age lives on the VARIATION, not the session, so this is NOT derivable
   * from the row's own fields — it must be carried.
   */
  effectiveBand?: AgeBand;
  /**
   * The variation document's OWN asserted band — the only band that is independent of the observed
   * row, and therefore the only one a comparison can mean anything against.
   *
   * `effectiveBand` cannot serve as the comparison whenever the variation defers: it comes from
   * `preview.variationData`, which `offerComputeCore.cjs:170-179` overwrites with the observed age
   * when `allowSessionAgeOverride` is set — so comparing the two compares a value against itself and
   * printed "the source and the variation agree" on this ticket's own motivating case. Only the
   * full-N callable can populate this (it reads `campLocations.variations[]`, where the pre-override
   * band survives); sample and raw artifact rows carry no band of their own.
   */
  variationBand?: AgeBand;
  /**
   * Whether the assigned variation defers to the session's own age.
   *
   * KB-2692 — unlike `variationBand`, this is available on EVERY row, not just the full-N ones:
   * `offerComputeCore` builds `variationData` by spreading the entire matched variation
   * (`const { colorScheme, ...sanitized } = v`), so while the pre-override band does not survive the
   * overwrite, the flag itself rides through untouched on both settings. That is what lets a sample
   * row tell "this comparison was real" from "this comparison was a value against itself" without a
   * variation-document read it has no way to perform.
   */
  variationAllowsAgeOverride?: boolean;
  /**
   * KB-2655 scope addition — carried for the per-row metadata report, not the row's own rendering.
   * `numDays` is what makes a two-week session mis-proposed as one week visible; `observedPriceRegular`
   * is the scraped price beside the computed `price`, separating an extraction defect from a parse one.
   * (`priceText` was the first cut and does not exist on a `proposedNew` row — see the callable.)
   */
  numDays?: number;
  observedPriceRegular?: number;
  /**
   * KB-3718 — where the price on this row was stated (session / programme / none / unknown). Set on every
   * row that shows a price or records a level: a price on screen always carries its level, and a row whose
   * source does not record one reads "price level unknown", never session-stated. See `campDataPriceLevel.ts`.
   */
  priceLevel?: PriceLevel;
  /**
   * KB-3317 — the value's standing, for the card's sub-line. Carried ONLY when the row's source carries
   * it: pattern `sampleItems` and full-page (`getPatternItemsCore`) rows do; a row exploded from the GCS
   * audit artifact (`loadPatternItems`) and any old pre-KB-3301 doc do not — and those render NO
   * sub-line, never a fabricated "no prior judgement". `readProvenance` returns `undefined` for anything
   * malformed, so a bad block degrades to absence rather than throwing inside a list render.
   */
  provenance?: ItemProvenance;
  from?: unknown;
  to?: unknown;
}

export type VerdictMode = "batch" | "single";
export type Verdict = "approve" | "reject";
/**
 * KB-2659 — what the operator may submit: the decisive pair, plus "I could not judge this".
 *
 * Kept as a SEPARATE type rather than widening `Verdict`, mirroring the same decision on the server
 * (`recordPatternVerdictCore.WireVerdict`). `Verdict` is what the card's Q1 state and its
 * `existenceAnswer === "approve" ? … : …` renderers are built around; widening it would make an
 * abstention render as a rejection in each of them.
 */
export type WireVerdict = Verdict | "abstain";
// KB-2052 / KB-2554 — the WIRE-legal channels a client may claim (the server's authoritative enum is
// `VERDICT_CHANNELS` in `recordPatternVerdictCore.ts`; kept loose here rather than imported since the
// client bundle does not depend on `functions/`). Omitted from a payload, the server defaults to
// "reviewer-ui" — every pre-KB-2554 caller's behavior is unchanged.
export type VerdictChannel = "reviewer-ui" | "reviewer-ui-runs-history";

/** KB-2181 — the operator's answer to Q2 ("all fields look good"). `null` = not answered yet. */
export type FieldsOk = boolean | null;

export interface RecordVerdictPayload {
  patternDocId: string;
  verdict: WireVerdict;
  mode: VerdictMode;
  // KB-2659 — travels only on an abstention; the server refuses it on a decisive verdict, and refuses
  // an abstention without it.
  abstainReason?: string;
  itemIds?: string[];
  /**
   * KB-2655 — BATCH-only: the per-item exceptions to the batch verdict. Omitted entirely by every
   * pre-existing caller, so an unmixed approve/reject payload is byte-identical to what it was.
   */
  itemOutcomes?: Record<string, Verdict>;
  inspectedItemIds: string[];
  // KB-2181 — Q2. Omitted entirely when the question was never answered, so a payload from a surface
  // that does not ask it stays exactly what it was before this ticket.
  fieldsOk?: FieldsOk;
  fieldIssueField?: string | null;
  fieldIssueNote?: string | null;
  revisedAfterReveal?: boolean;
  // KB-2554 — omitted by every pre-existing caller (Inbox), so the server default ("reviewer-ui")
  // applies exactly as before. Set explicitly by a surface that isn't the Inbox tab.
  channel?: VerdictChannel;
  /**
   * KB-3317 — the director lock. The server (`recordPatternVerdictCore.ts`) refuses `lock: true` on any
   * verdict other than `reject` (`lock-requires-reject`), and only fans it out on a CLEAN BATCH reject of
   * an apply-mode pattern (`recordPatternVerdict.ts`). `buildVerdictPayload` sets it only under exactly
   * those conditions, so a checked box that no longer applies (the answer flipped to Approve, a mixed
   * ruling, a single-mode reject) rides nothing — never a lock the server would refuse or silently ignore.
   */
  lock?: boolean;
  /**
   * KB-3597 — the org chosen on a sender card. The server refuses it on anything but a batch approve
   * (`sender-selection-requires-approve`) and refuses an org the card does not offer
   * (`sender-selection-not-a-candidate`), so `buildVerdictPayload` attaches it only to a batch approve.
   */
  senderSelection?: { orgId: string };
}

export interface RecordVerdictResult {
  alreadyRecorded: boolean;
  verdictId: string;
  status: string;
  mode: VerdictMode;
  /**
   * KB-2744 — the ruling was recorded but the pattern was deliberately NOT closed.
   *
   * Distinct from a thrown error (which would mean the ruling was discarded) and from a plain
   * `status: "open"` (which just means coverage is incomplete). Present only when every item is judged
   * and a gate withheld the terminal transition, so the card can tell the operator what is missing
   * rather than leaving a fully-judged pattern silently open — this ticket's original defect.
   *
   * ⚠️ Mirrors `RecordVerdictResult` in `functions/src/camp-data/recordPatternVerdictCore.ts`. The two
   * are hand-kept in step across the callable boundary — the frontend `tsconfig` excludes `functions/`,
   * so nothing type-checks them against each other and a field added on only one side compiles
   * cleanly on both.
   */
  closeBlocked?: { code: string; required: number; inspected: number };
}

// ── pure helpers (unit-tested; no Firestore / no network) ─────────────────────────────────────────

// KB-3317 / KB-3306 — `actorUidOrNull` (the `users/<uid>` parser) is KB-3306's, imported above and
// re-exported here so the inbox card's own consumers (itemPresentation, the tests) resolve it through
// this service. One resolver, one implementation — the only definition lives in campDataFieldState.
export { actorUidOrNull };

/**
 * KB-3317 — the human-readable author of a value, keyed on `sourceClass` ONLY, per DEC-064 ("trust is
 * routing, not decoration") and the dispatcher rulings. A NAME is rendered only for a `users/<uid>` staff
 * actor; every other class gets a fixed label, and NO branch ever returns a raw id.
 *
 *  - `staff` + `users/<uid>` → the resolved first name, or `(unknown editor)` when the lookup has not
 *    resolved or was denied (KB-2291 narrows who may read a user doc). A bare, non-namespaced staff actor
 *    (a legacy `"nicholas"` from the pre-KB-3475 reject fan-out) → `KidsBooked`, never the raw name.
 *  - `pipeline` → `Vendor`, `director` → `Camp` (the canonical block carries neither a vendorType nor a
 *    camp name, so the ticket's parenthetical is unsourceable and dropped — recorded in the PR).
 *  - `agent` → `Campy` (the card SUPPRESSES this pre-commit; see `describeItemStanding`).
 *  - `script` → `KidsBooked`, `report` → `public report` (a reporter's uid is NEVER resolved).
 *  - `legacy` → `(no history)`, `unknown` / anything else → `(unknown editor)`.
 */
export function describeActor(sourceClass: string | null | undefined, actorId: string | null | undefined, resolvedName?: string | null): string {
  switch (sourceClass) {
    case "pipeline":
      return "Vendor";
    case "staff": {
      if (actorUidOrNull(actorId ?? undefined)) return (resolvedName ?? "").trim() || "(unknown editor)";
      return "KidsBooked"; // a bare, un-namespaced staff actor — never rendered as a raw id
    }
    case "director":
      return "Camp";
    case "agent":
      return "Campy";
    case "script":
      return "KidsBooked";
    case "report":
      return "public report";
    case "legacy":
      return "(no history)";
    case "unknown":
    default:
      return "(unknown editor)";
  }
}

/**
 * KB-3317 — is this a pattern whose reject the server actually FANS OUT and can lock?
 *
 * A hand-kept mirror of `decidePatternApply` (`functions/src/camp-data/applyPatternChangesCore.ts`), which
 * the client bundle cannot import. Apply-mode = a field-change pattern: availability and deletion have
 * their own write paths, and creation is verdict-only — none of them honour a director lock or the
 * reject-and-remember fan-out (`recordPatternVerdict.ts`). A blank `fieldClass` is apply-mode there too, so
 * this mirrors that rather than diverging: the "won't be proposed again" copy and the "Also lock" checkbox
 * appear exactly where the server would act on them.
 */
const APPLY_REFUSED_FIELD_CLASSES: ReadonlySet<string> = new Set(["availability", "deletion"]);
export function isApplyModePattern(fieldClass: string | null | undefined): boolean {
  const fc = String(fieldClass ?? "").trim();
  if (APPLY_REFUSED_FIELD_CLASSES.has(fc)) return false;
  if (fc === "creation") return false;
  return true;
}

/**
 * KB-3317 — the card's muted "standing" sub-line, built from a present `provenance` block. The caller
 * renders NO sub-line when `item.provenance` is absent; this function assumes it is present.
 *
 * Variants (dispatcher rulings, 2026-09-13):
 *  - basis WITH a value → `set by <actor> <date> against vendor <value> · <policy> · <vendorType> run <run date>`
 *  - basis with NO value → `set by <actor> <date> · judged against an earlier vendor value · <policy>` (never invents one)
 *  - no basis          → `set by <actor> <date> · no prior judgement · <policy>`
 *  - a `legacy` winner  → `(no history) · <policy>`
 *  - DEC-064: an `agent` winner reads `set by KidsBooked …` PRE-COMMIT (pass `revealAgent` to show `Campy`
 *    after the reviewer has committed), so the card never surfaces an agent's prior decision during judging.
 *
 * Every optional segment (policy, the set-by date, vendorType, run date) collapses cleanly when absent —
 * a missing piece drops its ` · ` separator rather than rendering `· undefined`.
 */
export function describeItemStanding(prov: ItemProvenance, opts?: { resolvedName?: string | null; revealAgent?: boolean }): string {
  const sc = prov.canonical?.sourceClass ?? null;
  const policy = typeof prov.policy === "string" && prov.policy ? prov.policy : null;
  const policySuffix = policy ? ` · ${policy}` : "";

  if (sc === "legacy") return `(no history)${policySuffix}`;

  const actor = sc === "agent" && !opts?.revealAgent ? "KidsBooked" : describeActor(sc, prov.canonical?.actorId ?? null, opts?.resolvedName);
  const setDateRaw = prov.canonical?.at ? formatRunDate(prov.canonical.at) : null;
  const setDate = setDateRaw && setDateRaw !== "date unknown" ? setDateRaw : null;
  const head = `set by ${actor}${setDate ? ` ${setDate}` : ""}`;

  const basis = prov.basis;
  if (!basis) return `${head} · no prior judgement${policySuffix}`;

  if (typeof basis.value === "string" && basis.value) {
    const runIso = basis.runId ? runIdToIso(basis.runId) : null;
    const runDateRaw = runIso ? formatRunDate(runIso) : basis.observedAt ? formatRunDate(basis.observedAt) : null;
    const runDate = runDateRaw && runDateRaw !== "date unknown" ? runDateRaw : null;
    const runLabel = basis.vendorType && runDate ? `${basis.vendorType} run ${runDate}` : runDate ? `run ${runDate}` : basis.vendorType || null;
    const tail = [policy, runLabel].filter(Boolean).join(" · ");
    return `${head} against vendor ${basis.value}${tail ? ` · ${tail}` : ""}`;
  }
  return `${head} · judged against an earlier vendor value${policySuffix}`;
}


/**
 * Order patterns for the Inbox: fresh (non-stale) first, then severity HIGH-first (severityRank asc),
 * then most items first (counts.items desc). `staleAsOfRunId`-stamped docs sink to the BOTTOM (the ADR:
 * de-emphasize, never delete). The Firestore query already applies the severity/count order; this pure
 * re-sort applies the stale partition the composite query can't express and is the AC3 test surface.
 *
 * KB-3036 — SINKING is one of three de-emphasis channels, and the only one that keeps the pattern in
 * the action count. Shelving (`splitShelvedPatterns`, below) is the second and the one that removes
 * it. They are orthogonal: a stale doc still sinks INSIDE the shelf. The rule this docblock has always
 * cited was not actually written in the ADR until KB-3036 put it there — see
 * `kb-ff:docs/architecture/camp-data-trust-ladder.md`, "De-emphasis has THREE channels".
 */
export function orderPatternsForInbox(patterns: ReviewPattern[]): ReviewPattern[] {
  /**
   * KB-2764 — a frozen calibration member is NEVER sunk, even when stamped stale.
   *
   * De-emphasis has two channels and removing only one fixes nothing: the card's `opacity-60` was the
   * visible half, and THIS is the other. Position in a queue changes how carefully an item is read just
   * as dimming does, so sinking 7 of 68 members to the bottom reproduces the same cross-rater asymmetry
   * KB-2587's freeze exists to prevent — one rater works down a list in a different order from the other.
   * Ordinary Inbox patterns still sink, which is the ADR's intent and harms no comparability claim.
   */
  const isStale = (p: ReviewPattern) => Boolean(p.staleAsOfRunId) && !p.calibrationSetId;
  return [...patterns].sort((a, b) => {
    if (isStale(a) !== isStale(b)) return isStale(a) ? 1 : -1; // stale to the bottom
    if (a.severityRank !== b.severityRank) return a.severityRank - b.severityRank; // HIGH (0) before LOW (1)
    return (b.counts?.items ?? 0) - (a.counts?.items ?? 0); // more items first
  });
}

// ── KB-1872 (TL-8) — the tier-b veto window section ───────────────────────────────────────────────

/** `scheduleStatus` values that mean a window is still in flight. Only "pending" is actionable. */
export const SCHEDULE_PENDING = "pending";

export interface InboxSections {
  /** "Applying at ⟨time⟩ unless you veto" — soonest apply time first (least time to act). */
  applyingUnlessVetoed: ReviewPattern[];
  /** KB-1875 — "🎲 Audit sample": drawn, not yet answered. HIGH severity first, as the draw ordered it. */
  auditSample: ReviewPattern[];
  /** "Needs your eyes" — everything else, audit disagreements pinned to the top. */
  needsReview: ReviewPattern[];
}

/**
 * KB-1875 — statuses meaning the pattern has been decided.
 *
 * Mirrors `recordPatternVerdictCore.TERMINAL_STATUSES`; re-declared because `functions/` is a separate
 * package the bundle does not reach, and pinned against it by a parity test.
 */
export const TERMINAL_PATTERN_STATUSES = ["approved", "rejected", "suppressed"] as const;

export function isTerminalPatternStatus(status: string | undefined | null): boolean {
  return (TERMINAL_PATTERN_STATUSES as readonly string[]).includes(String(status || ""));
}

/**
 * KB-3034 — THE shelving predicate, mirrored from `isShelvedPattern` in
 * `scripts/camp-data/_shared/derivePatternDocs.cjs` and pinned against it in BOTH directions by a
 * parity test. Re-declared for the same reason `TERMINAL_PATTERN_STATUSES` above is: the frontend
 * bundle cannot reach `functions/`, and the frontend `tsconfig` excludes it, so nothing type-checks
 * the two against each other. The test is the only thing that does.
 *
 * ⛔ The calibration carve-out is INSIDE this function on purpose — do not lift it into callers.
 * KB-2764: sinking, dimming or hiding a frozen calibration member reproduces the cross-rater
 * asymmetry the KB-2587 freeze exists to prevent, and a carve-out repeated in N call sites is one the
 * N+1th forgets. Callers ask this question; they do not answer it.
 *
 * Absence reads false: a pattern doc written before `expiry` existed is "not shelvable", never
 * "shelvable by default". Those docs get their stamp from the KB-3035 backfill; until then their
 * silence is not a verdict.
 *
 * NOTE: this predicate answers "may this be shelved?" only. It performs no status change, no
 * ordering, and no filtering — the Inbox shelf that consumes it is KB-3036.
 */
export function isShelvedPattern(
  doc: Pick<ReviewPattern, "expiry" | "calibrationSetId"> | null | undefined,
): boolean {
  if (!doc) return false;
  if (doc.calibrationSetId) return false;
  return doc.expiry?.allEnded === true;
}

/**
 * KB-3036 round 2 (B5) — does the SERVER's `expiredCount` aggregate count this doc?
 *
 * A DIFFERENT question from `isShelvedPattern`, and the gap between them is the whole finding.
 * `fetchReviewInboxTotals` counts `status == "open" AND expiry.allEnded == true` and nothing else —
 * there is no calibration clause, because the carve-out cannot be expressed server-side (a `!=`
 * clause skips every doc where the field is absent, i.e. every live doc). So a frozen calibration
 * member carrying a stamp IS counted by the server while `isShelvedPattern` keeps it off the shelf.
 *
 * `onResolved` decrements a SERVER number, so it has to ask the SERVER's question. Neither of the
 * two predicates it previously used answers it:
 *
 *   - "was it rendered inside the shelf" under-reports, because section precedence holds several
 *     expired rows elsewhere — a live veto deadline, an outstanding audit vote, a pinned audit
 *     disagreement, a deep-link account card. (That form was a component-local ref, now deleted; it
 *     is described here rather than named, because the service must not point at component internals.)
 *   - `isShelvedPattern` over-corrects the other way, skipping the decrement for a judged calibration
 *     member that the server did count.
 *
 * The `status` clause is load-bearing and is why this form is safe without settling a disputed
 * question: reviewers disagreed about whether a pinned audit disagreement is still `open`. Mirroring
 * the aggregate's own predicate answers correctly per row, whichever is true of any given doc, so
 * the count follows the server rather than following anyone's belief about the server.
 */
export function isCountedAsExpiredByServer(
  doc: Pick<ReviewPattern, "status" | "expiry"> | null | undefined,
): boolean {
  if (!doc) return false;
  if (doc.status !== "open") return false;
  return doc.expiry?.allEnded === true;
}

/**
 * KB-3452 — mirrored from `FULL_POPULATION_EXPIRY_SOURCES` in `derivePatternDocs.cjs`: the `expiry`
 * sources counted over every row, so their `newestEndDay` is the population's. `harvest` is sample-only.
 */
export const FULL_POPULATION_EXPIRY_SOURCES: readonly string[] = ["deriver", "backfill-doc", "backfill-artifact"];

/** KB-3452 — mirrored from `isRealCalendarDay` in `derivePatternDocs.cjs`. Untrimmed on purpose, as there. */
export function isRealCalendarDay(day: unknown): day is string {
  if (typeof day !== "string" || !BARE_CALENDAR_DAY_RE.test(day)) return false;
  const [y, m, d] = day.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

type ReadTimeShelfDoc = Pick<
  ReviewPattern,
  "expiry" | "calibrationSetId" | "sampleItems" | "counts" | "staleAsOfRunId" | "lastSeenAtIso" | "latestRunId" | "fieldClass" | "scopeKey"
>;

/**
 * KB-4311 — the one fact about a camp the stale arm needs: its newest SUCCESSFUL nightly. The shape
 * `CampRunLiveness` already carries (`campDataRunLiveness.ts`), so a liveness read is passed straight in.
 * Null fields mean the camp is unmeasured, and an unmeasured camp shelves nothing.
 */
export interface ShelfCampRun {
  lastSuccessRunId: string | null;
  lastSuccessIso: string | null;
}

/** KB-4311 — why the stale arm shelved a card: the newest successful run that stamped it. */
export type StaleShelfReason = { kind: "stale"; runId: string };

/** Mirrors `HARVEST_FIELD_CLASSES` in `changeShape.cjs` and `isHarvestScopeKey` in `trustLib.cjs`. */
function isHarvestCard(doc: Partial<ReadTimeShelfDoc>): boolean {
  const fieldClass = (doc as { fieldClass?: unknown }).fieldClass;
  if (fieldClass === "announcement" || fieldClass === "sender") return true;
  const scopeKey = (doc as { scopeKey?: unknown }).scopeKey;
  return String(scopeKey == null ? "" : scopeKey).split(":")[0] === "harvest";
}

/**
 * KB-4311 — could the stale arm shelve this card once its camp's newest success is known? The card's
 * own half of `staleShelfReason`, so the Inbox reads liveness only for camps where the answer matters.
 * A superset by construction: every card `staleShelfReason` shelves passes this first.
 */
export function isStaleShelfCandidate(doc: Partial<ReadTimeShelfDoc> | null | undefined): boolean {
  if (!doc || typeof doc !== "object") return false;
  if (doc.calibrationSetId) return false;
  if (isHarvestCard(doc)) return false;
  return typeof doc.staleAsOfRunId === "string" && doc.staleAsOfRunId.trim() !== "";
}

/**
 * KB-4311 — has the nightly stopped proposing this card, MEASURED by the camp's newest successful run?
 * Null when it has not, or when that cannot be shown. Mirrored inline in `isShelvedAtReadTime` in
 * `derivePatternDocs.cjs`; `campDataReviewInbox.kb4311.test.ts` runs both over one table.
 *
 * Ruling (Nicholas, 2026-10-06; narrowed by the dispatcher's ruling on card 6335207c): a stale card
 * leaves To act on when `staleAsOfRunId` IS the camp's newest successful run. The stamp is re-written
 * by EVERY run that measures the camp (`deriveStalePatch`), so on a healthy camp it names last night's
 * run, and only then did the newest success itself find the card absent. A stamp naming any other run
 * — a failed run after the success (a dead camp's zero-session run stamps everything), or an older run
 * on a camp whose later successes never visited the card — is not that measurement.
 *
 * ⛔ There is deliberately no "unseen for N days" arm. A never-stamped card last seen before the newest
 * success was never visited by it (every visit refreshes, re-observes or stamps), so age plus a later
 * success shelves exactly the cards no run checked. Round 1 B1 found five calibration-frozen creation
 * cards it would have shelved after 40+ successes had skipped them.
 *
 * Never: a calibration member (KB-2764), a harvest card (the nightly does not raise it, so its
 * silence says nothing), or a card the newest successful run re-raised.
 */
export function staleShelfReason(
  doc: Partial<ReadTimeShelfDoc> | null | undefined,
  campRun: ShelfCampRun | null | undefined,
): StaleShelfReason | null {
  if (!doc || !isStaleShelfCandidate(doc)) return null;
  if (!campRun || typeof campRun !== "object") return null;
  const newest = typeof campRun.lastSuccessRunId === "string" ? campRun.lastSuccessRunId.trim() : "";
  if (!newest) return null;
  if (doc.latestRunId === newest) return null;
  return doc.staleAsOfRunId === newest ? { kind: "stale", runId: newest } : null;
}

/**
 * KB-3452 — the day on or before which every row of the pattern has provably ended, or "". Mirrored
 * from `provenAllEndedByDay` in `derivePatternDocs.cjs`, whose docblock carries the two sources and why
 * each is a proof rather than a guess; `campDataReviewInbox.kb3452.test.ts` runs both over one table.
 */
export function provenAllEndedByDay(doc: Partial<ReadTimeShelfDoc> | null | undefined): string {
  if (!doc || typeof doc !== "object") return "";
  const expiry = (doc as { expiry?: unknown }).expiry as Record<string, unknown> | null | undefined;
  if (expiry && typeof expiry === "object") {
    if (!FULL_POPULATION_EXPIRY_SOURCES.includes(expiry.source as string)) return "";
    if (!Number.isInteger(expiry.totalItems) || (expiry.totalItems as number) <= 0) return "";
    if (expiry.undatedItems !== 0) return "";
    return isRealCalendarDay(expiry.newestEndDay) ? expiry.newestEndDay : "";
  }
  if (expiry !== undefined && expiry !== null) return "";
  const sample: unknown[] = Array.isArray(doc.sampleItems) ? doc.sampleItems : [];
  const counts = doc.counts as { items?: unknown } | null | undefined;
  const items = counts && typeof counts === "object" ? counts.items : undefined;
  if (!Number.isInteger(items) || (items as number) <= 0 || sample.length < (items as number)) return "";
  let newest = "";
  for (const row of sample) {
    const raw = row && typeof row === "object" ? (row as { endDateIso?: unknown }).endDateIso : undefined;
    const end = typeof raw === "string" ? raw.trim() : "";
    if (!isRealCalendarDay(end)) return "";
    if (end > newest) newest = end;
  }
  return newest;
}

/**
 * KB-3452 — THE Inbox's shelf question: "may this pattern be shelved, judged on `todayEtDay`?"
 * Mirrored from `isShelvedAtReadTime` in `derivePatternDocs.cjs` and pinned against it behaviourally.
 *
 * The stamp is written during a run, so a camp whose runs fail keeps its ended cards in To-act-on
 * forever. This is the stamp (`isShelvedPattern`, unchanged, and never overridden) OR a read-time test
 * on the card's own evidence. `todayEtDay` is `etCalendarDayOfInstant(now)`, supplied by the caller —
 * there is no clock in here, and anything that is not a real bare day switches the read-time arm off.
 *
 * ⛔ Deliberately NOT a change to `isShelvedPattern`: that predicate also feeds the QC shadow, the
 * Campy lease loop, the approval queue and the producer-liveness denominator, which is a server count
 * that cannot express a read-time rule. Only the Inbox and `/inbox-docket` ask this question.
 *
 * ⚠️ The server's `expiredCount` still counts the stamp alone. `isCountedAsExpiredByServer` is the
 * question that tells the two apart, and `describeInboxTabCounts` adds the difference back for the
 * loaded page.
 *
 * KB-4311 — `campRun` adds the stale arm (`staleShelfReason`). Omitted, the answer is KB-3452's.
 */
export function isShelvedAtReadTime(
  doc: Partial<ReadTimeShelfDoc> | null | undefined,
  todayEtDay: string | null | undefined,
  campRun?: ShelfCampRun | null,
): boolean {
  if (!doc || typeof doc !== "object") return false;
  if (doc.calibrationSetId) return false;
  if (isShelvedPattern(doc as Pick<ReviewPattern, "expiry" | "calibrationSetId">)) return true;
  if (staleShelfReason(doc, campRun) !== null) return true;
  if (!isRealCalendarDay(todayEtDay)) return false;
  const day = provenAllEndedByDay(doc);
  return day !== "" && day < todayEtDay;
}

/** A bare `YYYY-MM-DD` — the shape `expiry.newestEndDay` and `expiry.asOfDay` are stored in. */
const BARE_CALENDAR_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Mirrored from `ISO_INSTANT_RE` in `scripts/camp-data/_shared/derivePatternDocs.cjs`. A zone
 * designator is MANDATORY: a timestamp without one names no single instant, and `new Date()` reads it
 * as local time, so the answer would move with the host.
 *
 * This is an ALLOWLIST on purpose. The two implementations have to answer identically, and the only
 * way to guarantee that is to share the rule rather than to pick two that happen to agree on the
 * inputs someone thought to sample.
 */
const ISO_INSTANT_RE = /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:?\d{2})$/;

let _etDayFormatter: Intl.DateTimeFormat | null = null;
function etDayFormatter(): Intl.DateTimeFormat {
  if (!_etDayFormatter) {
    _etDayFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }
  return _etDayFormatter;
}

/**
 * KB-3036 — the ET calendar day of a MOMENT. Mirrored from `etCalendarDayOfInstant` in
 * `scripts/camp-data/_shared/derivePatternDocs.cjs` and pinned against it, for exactly the reason
 * `isShelvedPattern` above is mirrored: the frontend bundle cannot reach that file and the frontend
 * `tsconfig` excludes it, so nothing type-checks the two against each other.
 *
 * ⛔ THIS IS A DIFFERENT QUESTION FROM READING A STORED DATE, and conflating the two was the KB-3034
 * round-2 defect. A STORED value at exactly `T00:00:00.000Z` is a calendar day encoded as an instant
 * (KB-2102) and reads as the UTC day; a CLOCK at `00:00Z` is 20:00 the PREVIOUS ET day. Only the
 * second question is asked here.
 *
 * It REFUSES a bare `YYYY-MM-DD` (returns ""), and that refusal is the point rather than an omission:
 * `newestEndDay` never had a zone, so re-projecting it through one would invent a day boundary the
 * value does not carry. Comparing days is `calendarDaysBetweenDays`'s job, on two bare strings. The
 * refusal falls out of `ISO_INSTANT_RE` — the deriver's own allowlist, mirrored — rather than out of a
 * bare-day special case, so anything else lacking a zone is refused on the same grounds.
 *
 * `formatToParts`, not `format()`: an assembled string can be reordered by a locale/ICU difference
 * between this Mac, CI and a browser, and a reordered day string compares wrong in exactly one
 * direction and never throws.
 */
export function etCalendarDayOfInstant(instant: Date | string | null | undefined): string {
  let d: Date | null = null;
  if (instant instanceof Date) d = instant;
  else if (typeof instant === "string") {
    const trimmed = instant.trim();
    // The deriver's rule, not a compatible one. It subsumes the bare-day refusal (a bare day has no
    // `T` and no zone, so it fails here) and it also refuses the shapes a denylist would have waved
    // through: `2026-08-22T12:00:00` read as "" in the deriver and as a real day here, and read as
    // LOCAL time, so it moved with the host too. Every case the parity pin sampled agreed until those
    // rows were added, which is precisely why the rule is shared rather than paralleled.
    if (!ISO_INSTANT_RE.test(trimmed)) return "";
    d = new Date(trimmed);
  }
  if (!d || Number.isNaN(d.getTime())) return "";
  const parts = etDayFormatter().formatToParts(d);
  const at = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const y = at("year");
  const m = at("month");
  const day = at("day");
  return y && m && day ? `${y}-${m}-${day}` : "";
}

/**
 * The UTC-midnight instant a bare `YYYY-MM-DD` names, or null when the string is not one.
 *
 * The round-trip check is not defensive padding: `Date.UTC(2026, 1, 30)` ROLLS OVER to March 2nd, so
 * `2026-02-30` — a shape the regex accepts — would otherwise become a real instant and yield a
 * confident, wrong "ended N days ago". A day that cannot exist must read as unparseable.
 */
function utcMidnightOfBareDay(day: string | null | undefined): number | null {
  if (typeof day !== "string") return null;
  const trimmed = day.trim();
  if (!BARE_CALENDAR_DAY_RE.test(trimmed)) return null;
  const [y, m, d] = trimmed.split("-").map(Number);
  const ms = Date.UTC(y, m - 1, d);
  if (!Number.isFinite(ms)) return null;
  const back = new Date(ms);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return ms;
}

/**
 * Whole calendar days from `fromDay` to `toDay`, both bare `YYYY-MM-DD`. Null when either is not one.
 *
 * Both are anchored at UTC midnight and subtracted, so no zone is ever applied to a value that never
 * had one — the arithmetic is over calendar days, not over instants. DST cannot perturb it for the
 * same reason: two UTC midnights are always an exact multiple of 86.4e6 ms apart.
 */
export function calendarDaysBetweenDays(fromDay: string | null | undefined, toDay: string | null | undefined): number | null {
  const a = utcMidnightOfBareDay(fromDay);
  const b = utcMidnightOfBareDay(toDay);
  if (a === null || b === null) return null;
  return Math.round((b - a) / 86400000);
}

/** The shelf row's reason line — what the operator reads instead of a bare "expired" badge. */
export interface ShelfExpiryLine {
  text: string;
  /**
   * True when this is a DEGRADED reading: the end day, the clock, or the two together could not be
   * resolved into a confident "N days ago". The caller must render it as such — a shelf row that
   * cannot say WHY it is shelved is a claim the operator should be able to see the weakness of.
   */
  degraded: boolean;
}

/**
 * KB-3036 — "ended ⟨day⟩ — N days ago" for a shelved row.
 *
 * §6a-bis: every branch that cannot produce the full sentence says what is actually unknown rather
 * than dropping to a plausible default. In particular there is no "0 days ago" fallback — a shelved
 * pattern whose newest end day is TODAY contradicts the writer's own rule (`allEnded` requires every
 * row to end strictly BEFORE `asOfDay`), so it is surfaced as a stale stamp, not smoothed over.
 *
 * `newestEndDay` is null-able by contract (`z.union([z.string(), z.null()])`) and, per the writer,
 * unreachable while `allEnded` is true — `allEnded` requires `undatedItems === 0`, so every row
 * resolved a day. Handled anyway: an unreachable state that renders a confident lie is worse than
 * one that renders an honest gap, and the KB-3035 backfill widens who writes this block.
 */
export function describeShelfExpiry(
  p: Partial<Pick<ReviewPattern, "expiry" | "sampleItems" | "counts">>,
  nowIso: Date | string,
): ShelfExpiryLine {
  const raw = p.expiry?.newestEndDay;
  let day = typeof raw === "string" ? raw.trim() : "";
  // KB-3452 — a row shelved at read time from its own sample has no stamp to read a day from.
  if (!BARE_CALENDAR_DAY_RE.test(day)) day = provenAllEndedByDay(p);
  if (!BARE_CALENDAR_DAY_RE.test(day)) {
    return { text: "every session has ended — the end date was not recorded", degraded: true };
  }
  const today = etCalendarDayOfInstant(nowIso);
  const days = today ? calendarDaysBetweenDays(day, today) : null;
  if (days === null) return { text: `last session ended ${day}`, degraded: true };
  if (days < 0) return { text: `last session ended ${day} — a date still in the future, so this stamp is stale`, degraded: true };
  if (days === 0) return { text: `last session ended ${day} — today, so this stamp is stale`, degraded: true };
  /**
   * KB-3452 — say when the SHELF, not a run, decided this. Two such rows are reachable: a stamp that
   * read `allEnded: false` on its `asOfDay` and was never refreshed, and a card no run ever stamped.
   * Both usually mean the camp's runs are failing, which is worth a glance before a verdict (a frozen
   * card's evidence is as old as its last successful run).
   */
  const basis =
    p.expiry?.allEnded === true
      ? ""
      : !p.expiry
        ? " · no run has ever checked its end dates"
        : typeof p.expiry.asOfDay === "string" && p.expiry.asOfDay
          ? ` · no run has re-checked it since ${p.expiry.asOfDay}`
          : " · no run has re-checked it since its last stamp";
  return { text: `last session ended ${day} — ${days.toLocaleString()} day${days === 1 ? "" : "s"} ago${basis}`, degraded: false };
}

/**
 * KB-4311 — the shelf row's reason line, whichever arm shelved it. A card whose sessions ended keeps
 * KB-3452's line (the stronger reason, and the one a verdict on it has to reckon with); only a card
 * shelved by the stale arm alone reads the stale line, so a human can still act on it.
 */
export function describeShelfReason(
  p: Partial<Pick<ReviewPattern, "expiry" | "sampleItems" | "counts" | "calibrationSetId">> & Partial<ReadTimeShelfDoc>,
  nowIso: Date | string,
  campRun: ShelfCampRun | null | undefined,
): ShelfExpiryLine {
  const today = etCalendarDayOfInstant(nowIso);
  const endedArm = isShelvedAtReadTime(p, today);
  const reason = endedArm ? null : staleShelfReason(p, campRun);
  if (!reason) return describeShelfExpiry(p, nowIso);
  return { text: `no longer proposed — stale since run ${reason.runId}, the camp's newest successful nightly`, degraded: false };
}

/** KB-1875 — has this pattern's requested audit been answered? */
export function isAuditAnswered(p: ReviewPattern): boolean {
  return !!p.auditCompleted || !!p.auditDisagreement;
}

/** KB-1875 — is this pattern awaiting a blind audit vote? */
export function isAwaitingAudit(p: ReviewPattern): boolean {
  return !!p.auditRequested && !isAuditAnswered(p);
}

/**
 * KB-1875 — is this doc a real pattern, or a stub carrying only an audit stamp?
 *
 * The draw stamps `auditRequested` with `set(..., { merge: true })` in a BATCH, so its read of the
 * pattern doc and its write are not atomic. If a pattern were deleted in that window the merge-write
 * would resurrect a ghost carrying ONLY the stamp — the upsert hazard the pro checklist calls out —
 * and that ghost would then satisfy the audit query and render as a card with no change, no count and
 * no links. Cheap to exclude on read; the card has nothing to show without these.
 */
export function isRenderablePattern(p: ReviewPattern): boolean {
  return !!p.campSlug && !!p.changeGroupKey;
}

/**
 * Split the open patterns into the veto-window section and the plain review queue.
 *
 * ⚠️ A SCHEDULED PATTERN MUST APPEAR IN EXACTLY ONE SECTION. Its `status` is still "open" (nobody has
 * judged it), so it comes back from the same `status == "open"` query as everything else — meaning without
 * this partition it would render in "needs your eyes" AND in the veto section. The ADR's design law is
 * that the SECTION an item sits in IS its trust level, so showing one item in two sections doesn't just
 * duplicate a card, it makes two contradictory claims about what happens if the operator does nothing.
 *
 * Ordered by `scheduledApplyAtIso` ASCENDING — soonest deadline first. This is the opposite of the
 * severity-first order the rest of the Inbox uses, deliberately: in this section the scarce resource is
 * the operator's remaining TIME to object, not the change's severity. A pattern applying in 40 minutes
 * outranks a HIGH-severity one applying tomorrow, because only one of them can still be stopped.
 *
 * Reuses the existing query and index — no new Firestore index (KB-1869's composite still serves it).
 */
export function partitionInboxSections(patterns: ReviewPattern[], auditSamplePatterns: ReviewPattern[] = []): InboxSections {
  const applyingUnlessVetoed: ReviewPattern[] = [];
  const auditSample: ReviewPattern[] = [];
  const needsReview: ReviewPattern[] = [];
  const seen = new Set<string>();

  for (const p of patterns) {
    seen.add(p.id);
    // The veto window keeps precedence over the audit section: only one of the two has a DEADLINE, and
    // an item the operator can still stop outranks one being re-checked after the fact.
    if (p.scheduleStatus === SCHEDULE_PENDING && p.scheduledApplyAtIso) applyingUnlessVetoed.push(p);
    else if (isAwaitingAudit(p)) auditSample.push(p);
    else needsReview.push(p);
  }

  /**
   * The separately-fetched audit patterns are the DELEGATE-era half: the agent decided and applied
   * them, so their status is terminal and the `status == "open"` inbox query never returns them. They
   * are de-duped against the open set because a pattern can legitimately be in both (open AND drawn).
   */
  for (const p of auditSamplePatterns) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    if (!isRenderablePattern(p)) continue;
    if (p.auditDisagreement) needsReview.push(p);
    else if (isAwaitingAudit(p)) auditSample.push(p);
  }

  applyingUnlessVetoed.sort((a, b) => String(a.scheduledApplyAtIso).localeCompare(String(b.scheduledApplyAtIso)));
  // The draw is HIGH-first; the section shows it that way rather than re-deriving an order.
  auditSample.sort((a, b) => a.severityRank - b.severityRank || (b.counts?.items ?? 0) - (a.counts?.items ?? 0));
  /**
   * Audit disagreements pin to the TOP of "Needs your eyes".
   *
   * The ADR calls disagreements "the highest-signal items in the system", and the amendment makes a
   * confirmed HIGH-severity one halt publishing. A disagreement that is merely RECORDED — a ledger row
   * nobody opens — is the failure mode this pin exists to prevent; the requirement is that it be
   * impossible to miss.
   */
  needsReview.sort((a, b) => Number(!!b.auditDisagreement) - Number(!!a.auditDisagreement));
  return { applyingUnlessVetoed, auditSample, needsReview };
}

/** KB-3036 — the shelf split, layered OVER `partitionInboxSections`. */
export interface ShelfSplit {
  /** The action queue: what "N to act on" counts and what the operator is asked to work. */
  needsReview: ReviewPattern[];
  /** Expired-and-not-frozen patterns. Out of the action count; visible and judgeable on demand. */
  shelved: ReviewPattern[];
}

/**
 * KB-3036 — split the shelf out of "Needs your eyes".
 *
 * ⚠️ A FILTER LAYERED OVER THE PARTITION, NOT A FOURTH SECTION ARM. `partitionInboxSections` routes
 * by what the pipeline is about to DO with a pattern (a deadline, an audit draw, or neither); expiry
 * is orthogonal to all three, and pro#1229 (KB-2945) is about to split `needsReview` again by the
 * Campy's recorded verdict. Adding expiry as a fourth arm would make the section stop being a
 * function of that routing, which is the property KB-2945's AC-2 (one deriver, one source) rests on.
 * Layering keeps both splits total and independent, and keeps #1229's rebase mechanical.
 *
 * ⛔ APPLIED TO `needsReview` ONLY, and the omission is the design. A pattern applying-unless-vetoed
 * has a DEADLINE — not clicking is a decision (KB-1872) — and one awaiting a blind audit vote is part
 * of the sample the agreement statistic is measured on (KB-1875). Shelving either would take it out
 * of the action count while the clock kept running, or drop it out of the audit floor's measured
 * population. Those two sections keep precedence over expiry, whatever the stamp says.
 *
 * ⛔ AND SO DOES A PINNED AUDIT DISAGREEMENT — the THIRD route, missed until round 1 (B3). Applying
 * the split to `needsReview` covers the first two because they are separate sections, but
 * `partitionInboxSections` folds disagreement rows INTO `needsReview` and then pins them to its top,
 * precisely because the ADR calls them the highest-signal item in the system and requires that they
 * be impossible to miss. This split runs after that pin, so without the carve-out below a
 * disagreement whose sessions have ended is pinned to the top of the queue and then moved inside a
 * default-collapsed accordion — one click from invisible, which is the one thing it must never be.
 *
 * ⚠️ This is a SECTION-PRECEDENCE rule, not a second opinion about expiry. It says which section wins
 * when a row qualifies for two, exactly as the `needsReview`-only scoping does for the other two
 * routes. It does not read `expiry` and does not re-derive the calibration carve-out — that question
 * stays `isShelvedAtReadTime`'s alone (KB-2764).
 *
 * ⛔ The shelf question is `isShelvedAtReadTime`'s alone (KB-3452 — the stamp OR the card's own
 * evidence judged on `todayEtDay`). This never reads `expiry` and never re-derives the calibration
 * carve-out — KB-2764's whole finding is that a carve-out repeated in N call sites is one the N+1th
 * forgets. `todayEtDay` is REQUIRED: a caller that forgot it would silently fall back to the stamp,
 * which is the bug KB-3452 fixes.
 *
 * Order is PRESERVED on both sides, not re-derived. Every fetch already returns
 * `orderPatternsForInbox`'d rows (see `fetchOpenReviewPatterns`, `fetchOpenReviewPatternsForCamp`,
 * `fetchCalibrationSetMembers`) and a stable partition inherits that, so stale docs still sink to the
 * bottom INSIDE the shelf exactly as they do inside the action queue. Re-sorting here would be a
 * second ordering authority for the same list.
 *
 * KB-4311 — `campRuns` (camp slug → newest successful run) turns on the stale arm. A camp missing from
 * it — liveness not yet read, or the read failed — is unmeasured, and its cards stay in the queue.
 */
export function splitShelvedPatterns(
  needsReview: ReviewPattern[],
  todayEtDay: string | null,
  campRuns?: ReadonlyMap<string, ShelfCampRun>,
): ShelfSplit {
  const active: ReviewPattern[] = [];
  const shelved: ReviewPattern[] = [];
  for (const p of needsReview) {
    if (isShelvedAtReadTime(p, todayEtDay, campRuns?.get(p.campSlug)) && !p.auditDisagreement) shelved.push(p);
    else active.push(p);
  }
  return { needsReview: active, shelved };
}

/**
 * The countdown line for a veto card: how long the operator still has.
 *
 * Returns `{ text, overdue }`. `overdue` is true once the apply time has passed but the hourly scheduler
 * hasn't run yet — a real, reachable state (up to ~1h wide) that must NOT render as a negative countdown
 * or a cheerful "0 minutes left". It reads "applying now" because that is what is about to happen, and a
 * veto in that window may genuinely lose the race.
 *
 * Deliberately coarse (days / hours / minutes, no seconds): a ticking seconds display would imply a
 * precision the hourly scheduler does not have.
 */
export function formatVetoCountdown(scheduledApplyAtIso: string, nowIso: string): { text: string; overdue: boolean } {
  const at = Date.parse(scheduledApplyAtIso);
  const now = Date.parse(nowIso);
  if (!isFinite(at) || !isFinite(now)) return { text: "apply time unknown", overdue: false };
  const ms = at - now;
  if (ms <= 0) return { text: "applying now", overdue: true };
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return { text: `${minutes} min left to veto`, overdue: false };
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return { text: `${hours}h left to veto`, overdue: false };
  return { text: `${Math.floor(hours / 24)}d left to veto`, overdue: false };
}

/** The apply time as a local-ET wall-clock label for the section header ("Applying at 6:00 AM"). */
export function formatApplyAtLabel(scheduledApplyAtIso: string): string {
  const at = Date.parse(scheduledApplyAtIso);
  if (!isFinite(at)) return "an unknown time";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(at));
}

/**
 * The veto action's payload. A veto IS a batch reject — the same verdict of record the reviewer's ✗
 * records, through the same callable, writing the same ledger row.
 *
 * `inspectedItemIds: []` is correct rather than lazy: the operator vetoed from the card, and
 * `minInspectionRequired` imposes no floor on a reject (rejecting is the cheap direction), so no
 * inspection is claimed that didn't happen.
 */
export function buildVetoPayload(patternDocId: string): RecordVerdictPayload {
  return buildVerdictPayload({ patternDocId, verdict: "reject", mode: "batch", inspectedItemIds: [] });
}

/** The human-readable change label for a card header. `creation` reads "new sessions"; a scalar change
 * shows its literal (e.g. "5→") + the shape kind; a long/unlabelled change shows just the shape kind. */
export function describePatternChange(p: Pick<ReviewPattern, "fieldClass" | "changeShape">): string {
  if (p.fieldClass === "creation") return "new sessions";
  const literal = p.changeShape?.literal || "";
  const kind = p.changeShape?.shapeKind || "";
  const pretty = literal ? literal.replace("→", " → ") : "";
  const kindLabel = kind ? `(${kind})` : "";
  return [p.fieldClass, pretty, kindLabel].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
}

// ── KB-2538 — what the approve actually writes ────────────────────────────────────────────────────

/** The `fieldClass` a whole-offer creation pattern carries (`proposedNew` → "creation"). */
export const CREATION_FIELD_CLASS = "creation";

/**
 * KB-2538 — the ONE deriver for the raw offer field a pattern writes (KB-1566: one derivation).
 *
 * A pattern is scoped to exactly one field: `deriveChangeGroupKey` hashes
 * `"<campSlug>|<fieldClass>|<field>:<shapeKind>[|<literal>]"`, and `planPatternApplies` writes strictly
 * that field per row. The header cannot stand in for it — `fieldClass` is a COARSER taxonomy, so a
 * `priceRegular` pattern reads "price" and a `numDaysCalc` one reads "dates".
 *
 * Read out of `groupKeyText` — the pre-hash text the pipeline already persists on every pattern doc —
 * rather than a dedicated `field` key, because no such key exists: `refreshableFrom`
 * (`scripts/camp-data/_shared/derivePatternDocs.cjs`) does not write one and
 * `CampDataReviewPatternDocV1Schema` does not declare one. Adding it would be a schema + pipeline
 * change that leaves every pattern already open — the ones this ticket exists to make reviewable —
 * blank until a re-run. `shapePrefixKeyFromGroupKeyText` splits the same text the same way.
 *
 * Returns null for a creation pattern (no per-field shape for a create) and for a doc whose
 * `groupKeyText` is absent or malformed — the caller renders nothing rather than a guess.
 */
export function patternFieldOf(p: Pick<ReviewPattern, "fieldClass" | "groupKeyText">): string | null {
  if (p.fieldClass === CREATION_FIELD_CLASS) return null;
  const parts = String(p.groupKeyText || "").split("|");
  if (parts.length < 3) return null;
  const segment = parts[2];
  const cut = segment.lastIndexOf(":");
  const field = (cut >= 0 ? segment.slice(0, cut) : segment).trim();
  return field || null;
}

/**
 * KB-3718 round 3, finding 4 — does this pattern's rows show where their price was stated? Only a creation
 * pattern and a `priceRegular` change pattern: that is the price `observedPriceLevel` describes. A title or
 * date pattern's rows carry a price for context, and an amber "price level unknown" on each of them (every
 * sample row derived before this shipped) would bury the signal the chip exists to give.
 */
export function patternShowsPriceLevel(p: Pick<ReviewPattern, "fieldClass" | "groupKeyText">): boolean {
  return p.fieldClass === CREATION_FIELD_CLASS || patternFieldOf(p) === "priceRegular";
}

/** KB-2538 AC1 — the card's field-scope line ("edits regUrl only"), or null when there is nothing
 * trustworthy to say (creation pattern, or a doc with no parseable `groupKeyText`). */
export function describeFieldScope(p: Pick<ReviewPattern, "fieldClass" | "groupKeyText">): string | null {
  const field = patternFieldOf(p);
  return field ? `edits ${field} only` : null;
}

/**
 * KB-3317 — the header's locked-excluded suffix, or null when it must not appear.
 *
 * Shows ONLY when `provenanceSummary.lockedExcluded` is a number strictly greater than 0. An ABSENT
 * summary means the gate did not run (KB-3301) — rendering "0 locked" over a gate that never ran is the
 * confident-absence this project exists to remove, so absence yields null, never a reassuring zero.
 * Field- and run-scoped per the dispatcher ruling: KB-3301's `lockedExcluded` counts this run's locked
 * values for the pattern's field across the whole run, not per pattern.
 */
export function describeLockedExcludedHeader(p: Pick<ReviewPattern, "fieldClass" | "groupKeyText" | "provenanceSummary">): string | null {
  const n = p.provenanceSummary?.lockedExcluded;
  if (typeof n !== "number" || !(n > 0)) return null;
  const field = patternFieldOf(p);
  return field ? `· ${n} ${field} values locked this run, not proposed` : `· ${n} values locked this run, not proposed`;
}

// ── KB-2538 — the per-item from→to line ───────────────────────────────────────────────────────────

/** Shown in place of an empty side (a `set`'s from, a `cleared`'s to) — a blank would read as "unknown". */
export const FROM_TO_EMPTY_LABEL = "(empty)";
/** Below this many shared leading characters, collapsing costs more than it saves. */
export const FROM_TO_MIN_SHARED_PREFIX = 16;
/** Hard cap on each rendered segment — a `description` change is prose, and the card is a list row. */
export const FROM_TO_MAX_SEGMENT = 120;
/** Hard cap on the rendered shared prefix; the END of the prefix is what abuts the difference, so a
 * clamped prefix keeps its tail. */
export const FROM_TO_MAX_PREFIX = 48;

export interface FromToDisplay {
  /** The shared leading run, rendered ONCE and de-emphasised. "" when nothing was collapsed. */
  prefixText: string;
  /** The differing tail of `from` (or the whole value when nothing collapsed). */
  fromText: string;
  /** The differing tail of `to` (or the whole value when nothing collapsed). */
  toText: string;
  /** The full stringified values, for the hover title — a clamp must never hide the real value. */
  fromTitle: string;
  toTitle: string;
  /** True when a shared prefix was factored out; the `to` side then needs a leading ellipsis. */
  collapsed: boolean;
}

/** Stringify one side of a change for display. Mirrors the pipeline's own `stringifyForLiteral`
 * intent (scalars plainly, structures as JSON) without importing a CJS pipeline module into the app. */
function stringifyFromToValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

const PREFIX_BOUNDARY = /[/?&=_\-.:]/;

/**
 * The shared leading run of two values, cut back to a separator so a collapse never slices a token in
 * half ("…nspark.com/enrol" is worse than no collapse at all). Returns "" — meaning "render both sides
 * in full" — when the run is too short to be worth hiding, or when factoring it out would leave either
 * differing tail EMPTY (one value being a strict prefix of the other).
 */
function boundedSharedPrefix(a: string, b: string): string {
  // Two sides that stringify identically have no differing segment to point the eye at, and
  // collapsing them renders "…/x → …x" — a change that reads as though something moved. Show both
  // whole instead, so a normalization-only change (a Timestamp against its own ISO string, say) is
  // visibly a no-op rather than a mystery.
  if (a === b) return "";
  const limit = Math.min(a.length, b.length);
  let raw = 0;
  while (raw < limit && a[raw] === b[raw]) raw += 1;
  if (raw < FROM_TO_MIN_SHARED_PREFIX) return "";
  let cut = -1;
  for (let i = raw - 1; i >= 0; i -= 1) {
    if (PREFIX_BOUNDARY.test(a[i])) {
      cut = i + 1;
      break;
    }
  }
  if (cut < FROM_TO_MIN_SHARED_PREFIX) return "";
  if (cut >= a.length || cut >= b.length) return "";
  return a.slice(0, cut);
}

function clampSegment(s: string): string {
  return s.length <= FROM_TO_MAX_SEGMENT ? s : `${s.slice(0, FROM_TO_MAX_SEGMENT - 1)}…`;
}

function clampPrefix(s: string): string {
  return s.length <= FROM_TO_MAX_PREFIX ? s : `…${s.slice(-(FROM_TO_MAX_PREFIX - 1))}`;
}

/**
 * KB-2538 AC2 — the display shape of one item's `from` → `to`.
 *
 * Returns **null** when the item carries neither key, which is exactly a creation row: the card must
 * render no node at all there, not an empty one (AC3 — an always-present empty div shifts every
 * creation card). A `set` (empty → value) and a `cleared` (value → empty) DO carry one side each and
 * render, with `FROM_TO_EMPTY_LABEL` standing in for the empty half.
 */
export function describeFromTo(from: unknown, to: unknown): FromToDisplay | null {
  if (from === undefined && to === undefined) return null;
  const fromFull = stringifyFromToValue(from);
  const toFull = stringifyFromToValue(to);
  // A row that carries a key but nothing on either side is not a change the diff layer emits
  // (both-empty is a non-change). Rendering "(empty) → (empty)" for one would be pure noise, so it
  // takes the same no-node path as a creation row.
  if (fromFull === "" && toFull === "") return null;
  const prefix = boundedSharedPrefix(fromFull, toFull);
  return {
    prefixText: prefix ? clampPrefix(prefix) : "",
    fromText: fromFull === "" ? FROM_TO_EMPTY_LABEL : clampSegment(fromFull.slice(prefix.length)),
    toText: toFull === "" ? FROM_TO_EMPTY_LABEL : clampSegment(toFull.slice(prefix.length)),
    fromTitle: fromFull === "" ? FROM_TO_EMPTY_LABEL : fromFull,
    toTitle: toFull === "" ? FROM_TO_EMPTY_LABEL : toFull,
    collapsed: prefix.length > 0,
  };
}

// ── KB-2658 — the age/grade band on a proposed row ────────────────────────────────────────────────

/**
 * One eligibility band, in the shape the pipeline already speaks.
 *
 * Field names mirror `observedBandsFromForm` (offerComputeCore.cjs) and `variationData` exactly, so
 * the same reader serves the observed side and the effective side and neither can drift into its own
 * dialect. Every field is optional and ABSENCE IS MEANINGFUL: a missing bound is "the source did not
 * say", never zero and never a default.
 */
export interface AgeBand {
  minAgeInYears?: number;
  maxAgeInYears?: number;
  minAgeGradeKey?: string;
  minAgeGradeValue?: number;
  maxAgeGradeKey?: string;
  maxAgeGradeValue?: number;
}

/**
 * How the vendor-observed band relates to the band the VARIATION asserts.
 *
 * - `contradiction` — both sides state, and the observed band is not one the variation allows. THE
 *   defect this surface exists for (KB-2658: carolina-friends-school rows sat on the wrong
 *   variation, unseeable).
 * - `match` — both sides state, and a REAL comparison agreed.
 * - `variation-defers` — the variation sets `allowSessionAgeOverride` and no band of its own is
 *   available to compare against. It has declined to assert anything, so there is nothing to agree
 *   or disagree with. **This is the state that used to print agreement** while comparing the
 *   observed band against a copy of itself (KB-2692).
 * - `observed-silent` — the scrape asserts no band, so the variation's is inherited unchecked.
 * - `unverified` — something is stated, but no independently-sourced age pair exists to compare.
 *   Includes the grade-only row and any row that cannot prove its effective band is not an echo.
 *   Not an error and not agreement.
 * - `absent` — neither side records a band at all. A real state, rendered as words.
 *
 * KB-2692 merged this with `rowMetadataReport`'s parallel five-state enum. Two classifiers over one
 * fact is how the card came to print agreement on the exact row the paste, three files away, was
 * already calling "not compared".
 */
export type BandAgreement = "contradiction" | "match" | "variation-defers" | "observed-silent" | "unverified" | "absent";

/**
 * KB-2658 — do two bounds of the same band actively contradict?
 *
 * Deliberately the same rule as `attributesConflict` (offerWriteCore.cjs): a bound absent on EITHER
 * side is missing information, never evidence of disagreement. That asymmetry is what makes the
 * open-ended rows quiet without this file ever mentioning the number 99. An observed `18–null`
 * against an effective `18–99` compares `null` to `99` on the max bound and yields "cannot tell",
 * so the sentinel needs no special case — measured on the live 2026-08-25 autism-society-of-nc run,
 * where 5 of 16 rows are exactly that shape and a naive inequality flags every one of them.
 *
 * Encoding the sentinel as a literal here would ALSO be wrong the moment a second no-upper-bound
 * convention appears; "absent means unknown" stays true regardless.
 */
function boundsConflict(a: number | undefined, b: number | undefined): boolean {
  if (a == null || b == null) return false;
  return a !== b;
}

const num = (v: unknown): number | undefined => (typeof v === "number" && isFinite(v) ? v : undefined);

/**
 * The bounds that may be COMPARED: ages, and only ages. Never grades.
 *
 * ⚠️ This asymmetry is the whole correctness argument of this file — read before "simplifying" it to
 * treat the two scales alike.
 *
 * On a creation row the effective side's AGE bounds are independently sourced: `offerComputeCore.cjs`
 * writes the observed age over the variation's only when `allowSessionAgeOverride` permits it
 * (`:170-179`), so observed-vs-effective ages genuinely can differ, and a difference is exactly the
 * mis-assignment this ticket exists to surface.
 *
 * The effective side's GRADE bounds are not independent at all. `offerComputeCore.cjs:200-208` copies
 * `observedMin/MaxGradeKey`+`Value` (and the `show*AsPrimary` flags) straight from the observed row,
 * and — unlike the age override — that stamping is **not gated** on `allowSessionAgeOverride`. So on
 * a creation row the effective grade band is a verbatim copy of the observed grade band. Comparing
 * them compares a value against itself: it can only ever return "equal", and it carries no
 * information about what the offer will actually be created with.
 *
 * An earlier version of this file compared whichever scale the row displayed. On a grade-banded row
 * that meant comparing the copy against itself, returning `match`, and DISCARDING the age bounds —
 * so a row whose source said ages 4–5 on a variation asserting 11–16 rendered "the source and the
 * variation agree". That is this ticket's own defect wearing the treatment reserved for a clean row.
 * Grades may be displayed; they may never decide agreement.
 */
function comparableAges(band: AgeBand | undefined): { min?: number; max?: number } {
  const b = band || {};
  return { min: num(b.minAgeInYears), max: num(b.maxAgeInYears) };
}

/**
 * A COMPLETE, integer grade band, or null — the display-only scale.
 *
 * Both bounds are required: a one-sided grade band used to suppress the row's age bounds from both
 * the chip and the comparison, hiding a real upper bound the offer carries.
 *
 * Integers only, matching `gradeValueToKey`'s domain (0 = K, -1/-2 = pre-K, 1–12). Curated
 * `variationData` stores decimalEnum display half-steps for rising bands — "rising 1-2 is stored as
 * 0.5/1.5" — which are not grades and rendered as `Grades 0.5–1.5`. They reach this file only
 * through the effective side, which no `gradeValueToKey` guards; a non-integer therefore falls back
 * to the age band rather than inventing a label.
 */
function gradeBandOf(band: AgeBand | undefined): { min: number; max: number } | null {
  const b = band || {};
  const min = num(b.minAgeGradeValue);
  const max = num(b.maxAgeGradeValue);
  if (min == null || max == null) return null;
  if (!Number.isInteger(min) || !Number.isInteger(max)) return null;
  return { min, max };
}

/**
 * The conventional no-upper-bound sentinel. A variation whose max is 99 asserts no ceiling, so an
 * observed max below it is not disagreement. Measured on the live 2026-08-25 autism-society-of-nc
 * run, 5 of 16 rows are exactly that shape and a naive inequality flags every one — which trains a
 * reviewer to skip the line (KB-2653 Finding 6).
 */
const NO_CEILING = 99;

/**
 * Does the variation assert an upper bound at all?
 *
 * ONE predicate, applied on BOTH comparison paths, and that is the point of it existing as a
 * function. KB-2692's first cut inlined the sentinel into the containment branch only — where it is
 * unreachable, because `escapesHigh` can consult it only when the OBSERVED max exceeds 99 and no
 * child age band does. So the guard was dead code, its test passed with the guard deleted, and the
 * equality path meanwhile LOST the unconditional `vMax < 99` that the retired `classifyBands`
 * applied: an observed 18–40 against a variation's 18–99 went from `consistent` to a printed
 * ⚠️ CONTRADICTION claiming the offer "creates Ages 18–99", which is a ceiling the variation never
 * asserted. Sharing the predicate makes the equality path exercise it, so deleting it now fails
 * tests instead of passing them.
 *
 * The exposed population: `ageGroupVariationSchema.ts:224` defaults `allowSessionAgeOverride` to
 * `false`, so any 99-ceiling variation authored through the pro form lands on exactly the path that
 * had lost the guard. In-repo that population measures 0 (all 13 sentinel-carrying variations under
 * `scripts/prog-recon` also set the flag `true`), but form-authored variations are not visible from
 * a read-only worktree, so the zero is a statement about what I could see, not about production.
 */
function assertsCeiling(max: number | undefined): boolean {
  return max != null && max < NO_CEILING;
}

/**
 * KB-2706 — was a bound one side asserts actually CHECKED against the other, or merely not
 * contradicted by a side that said nothing?
 *
 * `boundsConflict` deliberately answers `false` for "a bound is absent on either side", because a
 * missing bound is never evidence of DISAGREEMENT. The caller then read `differs === false` as
 * agreement — and those are not the same claim. "Nothing contradicts this" and "this was compared"
 * differ exactly when one side is silent, which is where the false clean bills lived:
 *
 *   observed 6–12 | variation 6–_    → the variation asserts no max at all; the source states 12.
 *                                      Nothing compared them. Rendered "the source and the
 *                                      variation agree on the age band."
 *   observed 18–_ | variation 18–40  → the mirror. The variation asserts a REAL ceiling of 40 and
 *                                      the source is silent above 18, so an observed max of 50 —
 *                                      a session on the wrong variation — is indistinguishable
 *                                      from one of 30. Also rendered as agreement.
 *
 * **The test is not "did both sides state it" — it is "could the unstated value change the
 * verdict".** That distinction is what keeps the open-ended rows quiet, and it is why this is not
 * simply `assertsCeiling` applied twice:
 *
 *  - Variation max ≥ `NO_CEILING` → it asserts no upper bound, so NO observed max can escape it.
 *    The comparison is complete even though the source stated nothing. Flagging it is the KB-2653
 *    Finding 6 regression — a report that cries mismatch on a third of its rows trains the reader
 *    to skip the line. (`boundsConflict`'s docblock reports this shape as 5 of 16 rows on the
 *    2026-08-25 autism-society-of-nc run. Cited, not re-measured: that run is not reachable from
 *    this worktree and the calibration set is frozen, so it is that docblock's figure, not mine.)
 *  - Variation max ABSENT → unknown, not unbounded. `boundsConflict`'s own docblock calls an absent
 *    bound "missing information". A variation that turns out to assert 10 would be escaped by an
 *    observed 12, so a stated observed max is genuinely unchecked here.
 *
 * The asymmetry is real and load-bearing: the variation is the PERMITTING side, so only ITS
 * unboundedness can close the question. An observed band open above a silent variation is still
 * unchecked, because the variation may yet assert a ceiling the source escapes.
 *
 * The min bound carries no sentinel — there is no "unbounded below" convention — so exactly one
 * side stating it always leaves the verdict undetermined.
 */
type UncomparedBound = { bound: "lower" | "upper"; silent: "source" | "variation" };

/**
 * KB-2706 round 2 (O3) — reports BOTH bounds when both went unchecked.
 *
 * It used to `return` on the first one, so `observed Ages 6+ | variation Ages up to 12` said only
 * "the variation records no lower bound" — while the ceiling was equally unchecked (the variation
 * asserts a real 12, the source is silent above 6). Naming one unknown and stopping invites the
 * reader to conclude the other was checked, which is this ticket's defect in miniature: a statement
 * that is true as far as it goes and misleading because of where it stops.
 */
function uncomparedBounds(o: { min?: number; max?: number }, c: { min?: number; max?: number }): UncomparedBound[] {
  const out: UncomparedBound[] = [];
  // Min: no sentinel, so one-sided is always undetermined.
  if ((o.min == null) !== (c.min == null)) out.push({ bound: "lower", silent: o.min == null ? "source" : "variation" });
  // Max: both stated → compared (a disagreement is `boundsConflict`'s job, not this one).
  // Neither states it → neither asserts an upper bound, so there is nothing to check.
  // Exactly one states it → complete only when the VARIATION is the side asserting no ceiling.
  const maxOneSided = (o.max != null) !== (c.max != null);
  if (maxOneSided && !(c.max != null && !assertsCeiling(c.max))) {
    out.push({ bound: "upper", silent: o.max == null ? "source" : "variation" });
  }
  return out;
}

function uncomparedBound(o: { min?: number; max?: number }, c: { min?: number; max?: number }): UncomparedBound | null {
  return uncomparedBounds(o, c)[0] ?? null;
}

/**
 * KB-2706 — the sentence for an `unverified` row whose cause is an unchecked bound.
 *
 * Driven by `uncomparedBound`, the SAME call that decides the verdict, so the reason a reader is
 * given cannot drift from the reason the verdict was reached. The alternative — a second predicate
 * behind the rendering — is how the paste and the card came to disagree about one row in the first
 * place (see `classifyBandAgreement`'s header).
 *
 * The parenthetical on the variation-silent case is doing real work, not hedging. "Records nothing"
 * and "asserts no ceiling" are the two states this fix turns on, they are one keystroke apart in the
 * data (absent vs `99`), and they produce OPPOSITE verdicts — `unverified` here, `match` on the row
 * above. An operator who cannot tell which one they are looking at cannot act on either.
 */
function uncomparedBoundOf(item: BandComparable): UncomparedBound[] {
  // ⚠️ THE VERDICT IS NOT A SUFFICIENT GATE, and an earlier version of this comment claimed it was.
  //
  // That version said routing through `classifyBandAgreement` made the wrong-sentence class
  // "unreachable rather than merely fixed". It is not: `unverified` is reached by TWO different
  // routes, and only one of them is an unchecked bound. The other is the fall-through at the end of
  // the classifier — the grade-only row, and the row whose comparison side states nothing at all.
  // On those, `uncomparedBound` sees one side with ages and one side without and reports a
  // one-sided bound, which is true of the numbers and false of the row.
  //
  // Executed, round 1: `{observedBand: Grades 3–4, effectiveBand: Ages 8–12, flag: false}` rendered
  // "The source records no lower bound" one clause after printing "The source states Grades 3–4".
  // The two clauses contradict each other in the same sentence. A grade band defeats the
  // `observed-silent` gate, so the verdict is `unverified` and the old gate passed it straight
  // through. It also blanked a `variation: Grades 1–3` the reviewer needs.
  //
  // So the real precondition is the one `classifyBandAgreement` itself applies before it ever
  // consults the bounds: BOTH sides must state something on the COMPARABLE (age) scale. That is the
  // `observedStates && comparisonStates` guard, and replicating it here is what makes this sentence
  // reachable only from the route it describes. The lesson is narrower than "gate on the verdict":
  // gate on the same precondition as the branch you are explaining.
  const o = comparableAges(item.observedBand);
  const { band, independent } = comparisonSide(item);
  const c = comparableAges(band);
  if (o.min == null && o.max == null) return [];
  if (c.min == null && c.max == null) return [];
  if (!independent) return [];
  if (item.variationAllowsAgeOverride === true) return [];
  if (classifyBandAgreement(item) !== "unverified") return [];
  return uncomparedBounds(o, c);
}

/**
 * KB-2706 round 2 — the uncompared-bound explanation, for any surface that renders this verdict.
 *
 * Exported because the PASTE needs it and did not have it. `AGREEMENT_TEXT.unverified` reads "no
 * independently-sourced variation band is recorded for this row, so nothing could be compared",
 * which was true of every row that reached `unverified` BEFORE this ticket. It is false of the
 * population this ticket adds: on `observed 0–5 | variation up to 5` the variation's band very much
 * IS recorded — one bound of it simply went unchecked. Executed on the round-1 delta.
 *
 * That is B1's defect one surface over: a new population routed into an existing sentence written
 * for a different one. The card was fixed by giving the new population its own branch; the paste
 * needs the same sentence, not a second wording of it — hence one exported definition rather than a
 * copy, which is the rule this file already applies to `isNarrowedWithinVariation`.
 *
 * Returns `null` when the row's `unverified` came from any other route, so a caller that falls back
 * to the flat text is still correct for the population that text was written for.
 */
export function uncomparedBoundNote(item: BandComparable): string | null {
  const us = uncomparedBoundOf(item);
  if (us.length === 0) return null;
  // ⚠️ The WHOLE sentence, both band values included — not just the explanatory clause.
  //
  // The first cut returned only the clause and let each surface name the two bands itself. The paste
  // then reached for `formatBand`, which PREFERS GRADE KEYS, and rendered "the source states grades
  // 1–3 and the variation asserts grades 1–3 … no upper bound could be checked" on a row whose
  // uncompared bound is on the AGE scale — two identical grade bands followed by a claim that a
  // comparison failed. Incoherent on its face, and produced by the fix for the previous incoherence.
  //
  // The bands are therefore formatted HERE, once, by the same age-leading formatters the verdict
  // itself uses. A surface that re-derives them can pick a different scale from the one that was
  // compared; a surface handed the finished sentence cannot.
  const observedText = formatAgeBand(item.observedBand);
  const assertedAges = formatAgeOnly(comparisonSide(item).band);
  // EVERY unchecked bound, not just the first. Naming one and stopping is the miniature of this
  // ticket's own defect: true as far as it goes, and it lets the reader conclude the other was
  // checked (round 2, O3).
  const explanation = us.map(uncomparedBoundSentence).join(" ");
  return `The source states ${observedText}${assertedAges ? `; the assigned variation asserts ${assertedAges}` : ""}. ${explanation}`;
}

function uncomparedBoundSentence(u: UncomparedBound): string {
  if (u.silent === "source") {
    return `The source records no ${u.bound} bound, so it could not be checked against the one the variation asserts.`;
  }
  // The sentinel clause is scoped to the UPPER bound, where the sentinel exists. `uncomparedBound`'s
  // own docblock says the min bound carries no such convention, so appending it to a missing floor
  // invited the reader to think a min of 99 meant something — a non-sequitur in the one sentence
  // whose job is to say precisely what is unknown.
  const sentinelNote = u.bound === "upper" ? ` — and a variation that means "no ceiling" records 99, which this one does not` : "";
  return `The variation records no ${u.bound} bound at all, so the source's could not be checked against it${sentinelNote}.`;
}

/**
 * KB-2706 AC-5 — the comparison COMPLETED, but the two bands are not the same band.
 *
 * Reachable on exactly one shape, and the narrowness is the point: the source states a real ceiling
 * and the variation asserts none (`18–40` under an open `18–99`). A min disagreement founds a
 * contradiction and a one-sided bound is now `unverified`, so nothing else survives to here.
 *
 * That row is a genuine `match` — every bound either side asserts was checked, and 40 cannot escape
 * an unbounded ceiling — but "the source and the variation agree on the age band" is false of it:
 * 18–40 and "18 and up" are not the same band. The verdict stays; the sentence changes.
 *
 * Exported so the card and the metadata paste read ONE definition, the same reason
 * `isNarrowedWithinVariation` is exported. Recomputing it per surface is how the two came to
 * disagree about a HIGH-severity field in the first place.
 */
export function isUnderOpenCeiling(item: BandComparable): boolean {
  if (classifyBandAgreement(item) !== "match") return false;
  const o = comparableAges(item.observedBand);
  const c = comparableAges(comparisonSide(item).band);
  return assertsCeiling(o.max) && !assertsCeiling(c.max);
}

/**
 * The band this row may be compared AGAINST, and whether that comparison is independent.
 *
 * KB-2692 — the whole correctness argument now lives here. There are three ways a row can supply the
 * variation's side, and only two of them are evidence:
 *
 *  - `variationBand` (full-N callable) — read from `campLocations.variations[]`, where the
 *    pre-override band survives. Always independent. The variation's willingness to defer changes
 *    what the offer INHERITS, not what the variation SAYS, so a deferring variation that states a
 *    band still has a band to disagree with.
 *  - `effectiveBand` with `variationAllowsAgeOverride === false` — the pipeline refused the
 *    override, so `preview.variationData` still holds the variation's own band. Independent, and
 *    this is the path every KB-2658 verdict took; its behaviour is unchanged.
 *  - `effectiveBand` with the override ALLOWED, or with the flag unknown — an echo of the observed
 *    row, or unprovably not one. Never evidence.
 */
function comparisonSide(item: BandComparable): { band?: AgeBand; independent: boolean } {
  const fromVariation = comparableAges(item.variationBand);
  if (fromVariation.min != null || fromVariation.max != null) return { band: item.variationBand, independent: true };
  if (item.variationAllowsAgeOverride === false) return { band: item.effectiveBand, independent: true };
  return { band: item.effectiveBand, independent: false };
}

/**
 * Did this row's `match` come from CONTAINMENT rather than from equality?
 *
 * A containment match is not an agreement, and saying it is was a false sentence on the majority
 * row shape: observed 6–9 inside a variation asserting 3–10 rendered "the source and the variation
 * agree on the age band", with `comparisonText` null, so the reviewer learned neither what the
 * variation asserts nor what the offer inherits. 6–9 and 3–10 do not agree. The containment test is
 * the right test — this is about what the surfaces then SAY about it.
 *
 * That shape is the COMMON case, not an edge: every flag-carrying variation in the in-repo bulk-add
 * catalogs sets the flag `true`, so a full-N row narrowing within its variation is the norm. Under
 * KB-2655 the same row read "not compared — open the full item list"; a silent green is a worse
 * regression than the wordy honest version, and it is a weaker instance of the exact defect this
 * ticket exists to kill.
 *
 * Exported so the card and the metadata paste read ONE definition of "was this an equality or a
 * containment" — the same reason there is one classifier. Recomputing it per surface is how the two
 * came to disagree in the first place.
 */
export function isNarrowedWithinVariation(item: BandComparable): boolean {
  if (item.variationAllowsAgeOverride !== true) return false;
  if (classifyBandAgreement(item) !== "match") return false;
  const { band } = comparisonSide(item);
  const observed = formatAgeOnly(item.observedBand);
  const asserted = formatAgeOnly(band);
  return observed != null && asserted != null && observed !== asserted;
}

/** The fields a band verdict is computed from. A row shape, not a pair — see `comparisonSide`. */
export interface BandComparable {
  observedBand?: AgeBand;
  effectiveBand?: AgeBand;
  variationBand?: AgeBand;
  variationAllowsAgeOverride?: boolean;
}

/**
 * KB-2658 / KB-2692 — classify one row on the age scale. THE band verdict; there is no other.
 *
 * The comparison is observed-vs-**what the variation asserts**, never observed-vs-what the offer
 * inherits. Those are the same value until the variation sets `allowSessionAgeOverride`, at which
 * point `offerComputeCore.cjs:170-179` writes the observed age into `variationData` and the second
 * comparison becomes a value against itself — returning agreement on a row nothing checked. `ages`
 * is a HIGH-severity class, so a false clean bill there is worse than any amount of silence.
 *
 * Two rules, because a deferring variation is asserting something weaker than an equality:
 *
 *  - Override refused → the variation asserts THIS band. Exact bounds, unchanged from KB-2658.
 *  - Override allowed → the variation asserts a RANGE its sessions may narrow within (webtrac
 *    `g-camp-am-option1` = 3–10, holding both a 6–9 and a 3–5 group). Narrowing is the flag's whole
 *    purpose and must not flag; ESCAPING the range means the session is on the wrong variation, and
 *    that is the carolina case — observed 7–12 on a variation asserting 11–16.
 *
 * A row that cannot be checked reports so. Agreement is the one verdict a reviewer acts on by not
 * looking, so it is the one verdict this must never guess.
 */
export function classifyBandAgreement(item: BandComparable): BandAgreement {
  const o = comparableAges(item.observedBand);
  const observedStates = o.min != null || o.max != null;
  const { band, independent } = comparisonSide(item);
  const c = comparableAges(band);
  const comparisonStates = c.min != null || c.max != null;

  if (observedStates && comparisonStates) {
    // The sentinel applies on BOTH paths: a variation stating no ceiling has no upper bound to
    // differ from, so its max cannot found a contradiction however the two sides are compared. The
    // retired `classifyBands` applied this unconditionally; scoping it to the containment branch
    // regressed the equality path (see `assertsCeiling`).
    const differs = boundsConflict(o.min, c.min) || (assertsCeiling(c.max) && boundsConflict(o.max, c.max));

    // A DIFFERENCE is self-proving, so it needs no independence argument. The echo makes the two
    // sides EQUAL — `offerComputeCore` writes the observed age into `variationData` verbatim — so
    // two bands that disagree cannot be a value compared against itself, whatever the row records
    // about the override. This asymmetry is what keeps every KB-2658 contradiction standing on
    // pattern docs written before the override flag was projected: only the clean bills go quiet,
    // and the clean bills are the ones that were never checked.
    if (differs && !independent) return "contradiction";

    if (independent) {
      if (item.variationAllowsAgeOverride === true) {
        // Containment, not equality: the variation permits its sessions to narrow within this band.
        const escapesLow = o.min != null && c.min != null && o.min < c.min;
        const escapesHigh = o.max != null && c.max != null && assertsCeiling(c.max) && o.max > c.max;
        // KB-2706 — DELIBERATELY NOT extended to this branch, and the reason is scope, not doubt.
        //
        // The containment path carries the same defect in kind: both `escapes*` tests require BOTH
        // sides to state the bound, so a variation asserting 3–10 against a source silent above 3
        // cannot tell a narrowing session from one escaping at 14, and still renders "within the
        // Ages 3–10 the assigned variation asserts". Executed and confirmed.
        //
        // It is left standing here for two reasons. This is the MAJORITY row shape — measured for
        // this ticket, the `*_bulk_add_shared_programs*.json` catalogs under `scripts/prog-recon`
        // carry the flag 172 times and set it `true` 172 of 172 — so re-verdicting it would move a
        // large population from `match` to `unverified` on a surface whose calibration members are
        // frozen and actively being judged. (In-repo catalogs are not production; form-authored
        // variations are not visible from this worktree, so that is a floor on the affected
        // population, not a measurement of it.) And KB-2692's
        // "cannot escape a bound the variation does not state" pins `match` here on stated
        // reasoning, which is a call to revisit deliberately rather than to overturn in passing on
        // a ticket that did not ask. Raised on KB-2706 as a finding instead.
        return escapesLow || escapesHigh ? "contradiction" : "match";
      }
      // A CONTRADICTION outranks an unchecked bound: a conflict on a bound both sides stated is
      // self-proving, and letting an unknown elsewhere suppress it would hide a live defect behind
      // silence — the failure mode opposite to this ticket's, and the more expensive one.
      if (differs) return "contradiction";
      return uncomparedBound(o, c) ? "unverified" : "match";
    }
    // Equal bands, and nothing proves they were compared rather than copied. Not agreement.
    return item.variationAllowsAgeOverride === true ? "variation-defers" : "unverified";
  }

  // Nothing on either scale, either side → the row genuinely records no band.
  if (!observedStates && !comparisonStates && !gradeBandOf(item.observedBand) && !gradeBandOf(band)) return "absent";
  // The variation declined to assert a band AND none is available to compare against. Distinct from
  // `unverified`: this row's comparison is not missing, it is structurally absent.
  if (!independent && item.variationAllowsAgeOverride === true) return "variation-defers";
  // The scrape said nothing AT ALL, so whatever the offer inherits is inherited unchecked. Gated on
  // the grade band too: a row stating grades but no ages HAS spoken, it just did not speak on the
  // comparable scale, and calling that "the scrape asserts no age band" would misdescribe a source
  // that named its eligibility in the convention it actually uses (KB-2107).
  if (!observedStates && !gradeBandOf(item.observedBand) && comparisonStates) return "observed-silent";
  // Something is stated, but no independently-sourced age pair lines up. Includes the grade-only
  // row, whose effective grades are the copy described on `comparableAges` and prove nothing, and
  // any row whose effective band cannot be shown not to be an echo.
  return "unverified";
}

/** "3" / "K" / "PK" — the label for a grade VALUE (0 = K, negatives = pre-K; mirrors gradeValueToKey). */
function gradeLabel(value: number): string {
  if (value === 0) return "K";
  if (value < 0) return "PK";
  return String(value);
}

/** The grade half of a band's display, or null. */
function formatGradeBand(band?: AgeBand): string | null {
  const g = gradeBandOf(band);
  if (!g) return null;
  return g.min === g.max ? `Grade ${gradeLabel(g.min)}` : `Grades ${gradeLabel(g.min)}–${gradeLabel(g.max)}`;
}

/** The age half of a band's display, or null. */
function formatAgeOnly(band?: AgeBand): string | null {
  const { min, max } = comparableAges(band);
  if (min == null && max == null) return null;
  if (min != null && max != null) return min === max ? `Age ${min}` : `Ages ${min}–${max}`;
  // An open-ended band renders `Ages 12+` / `Ages up to 12` rather than `12–` : a dangling dash reads
  // as a band whose other half failed to load, and this surface exists so a reviewer can tell
  // "unbounded" from "unknown" at a glance.
  return min != null ? `Ages ${min}+` : `Ages up to ${max}`;
}

/**
 * KB-2658 — one band as text, or null when it states nothing.
 *
 * AGES LEAD whenever the band states them, because ages are what gets compared and what the offer is
 * created with; a complete grade band rides along as an annotation, since grades are the source's own
 * eligibility convention (KB-2107) and a reviewer checking a "Grades 3-4" camp wants to see it. A row
 * that states only grades shows only grades.
 *
 * Leading with the compared quantity is deliberate: when the chip showed grades while the verdict was
 * computed from ages, the row could read as one scale and be judged on another.
 */
export function formatAgeBand(band?: AgeBand): string | null {
  const ages = formatAgeOnly(band);
  const grades = formatGradeBand(band);
  if (ages && grades) return `${ages} · ${grades}`;
  return ages ?? grades;
}

/**
 * The label for a row carrying no band — words, never a dash that could read as a band.
 *
 * "not RECORDED", not "not stated": a pattern doc written before this projection existed carries no
 * bands either, and the two are indistinguishable from the row alone. "Not stated" is a claim about
 * what the vendor said and would be false for every such row — including every member of a frozen
 * calibration set, which never receives the projection at all.
 */
export const BAND_ABSENT_LABEL = "age not recorded";

/** What the card renders for one row's band. `agreement` drives the styling; the card never re-derives it. */
export interface BandDisplay {
  /** The band the reviewer reads first. */
  text: string;
  agreement: BandAgreement;
  /**
   * The second value to render inline, connector word included, or null. Set whenever the row has two
   * sides worth putting on screen at once — a contradiction, and an uncomparable pair. Fully formed
   * here rather than assembled in the card, so the card formats nothing (AC-3).
   */
  comparisonText: string | null;
  /** Both sides, for callers that lay them out themselves (KB-2655's full-list view). */
  observedText: string | null;
  effectiveText: string | null;
  /** Full sentence for the `title` attribute; also the accessible description of a flagged row. */
  title: string;
}

/**
 * KB-2658 — the ONE band presentation. The card renders this and formats nothing itself; the KB-2655
 * full-list view calls the same function so the two surfaces cannot disagree about a HIGH-severity
 * field.
 *
 * A contradiction leads with the OBSERVED band and names the inherited one, in that order, because
 * the observed band is the evidence and the inherited one is the consequence — "the source says
 * 7–12, this creates 11–16" is the sentence a reviewer needs to reject on.
 */
/**
 * KB-2706 round 2 (B4) — the ONE way this file points at the full item list.
 *
 * There were three copies of "open “Judge individually” to read the variation's own band", and the
 * round-1 fix for that instruction reached exactly one of them, because a sentence duplicated three
 * times is fixed one grep at a time. A constant is fixed once.
 *
 * It is not an instruction, and that is the substance rather than the style. Both remaining routes
 * are rendered INSIDE that list — `PatternItemsList` and `ReviewPatternCard` both render
 * `ItemAgeBand`, whose `title` is this string — so telling the reader to open it is telling them to
 * open the view they are already reading, to find a band that view is currently rendering as
 * absent. The wording therefore describes where the bands live without asserting this row is among
 * the rows that have them.
 */
const FULL_LIST_POINTER = "The full item list shows the variation's own band for rows that carry it.";

/**
 * KB-2706 round 3 (F7) — does this band carry a grade KEY, whether or not it carries the value?
 *
 * Deliberately looser than `gradeBandOf`, and the gap between them is the point: `gradeBandOf` is
 * the COMPARISON predicate (both bounds, integer values, because half a band cannot decide a
 * verdict) while the metadata paste's `formatBand` renders from a key alone, either spelling. A row
 * can therefore print `grades 1–3` and still classify `absent`. This answers the narrow question a
 * sentence needs before it denies anything: is there something on screen to deny?
 */
function hasGradeKey(band?: AgeBand): boolean {
  const b = (band ?? {}) as AgeBand & { minGradeKey?: string; maxGradeKey?: string };
  return Boolean(b.minAgeGradeKey || b.maxAgeGradeKey || b.minGradeKey || b.maxGradeKey);
}

/**
 * KB-2706 round 3 (F2) — does this band's stored upper bound mean "no ceiling" rather than a bound?
 *
 * Exported so the metadata paste asks the SAME question the card asks. `NO_CEILING` and
 * `assertsCeiling` are private to this file by design — the sentinel's meaning is a property of the
 * comparison, not of the renderer — but a surface that prints the variation's band needs to know not
 * to print `99` as something the variation asserted. That is the KB-2692 chip regression, and the
 * paste was still committing it after the card stopped.
 */
export function assertsNoCeiling(band?: AgeBand): boolean {
  const c = comparableAges(band);
  return c.max != null && !assertsCeiling(c.max);
}

export function describeItemAgeBand(item: BandComparable): BandDisplay {
  const agreement = classifyBandAgreement(item);
  const observedText = formatAgeBand(item.observedBand);
  const effectiveText = formatAgeBand(item.effectiveBand);
  const { band: comparisonBand } = comparisonSide(item);
  const grades = formatGradeBand(item.observedBand) ?? formatGradeBand(item.effectiveBand);

  // KB-2692 — the variation defers and asserts no band anyone can read. The row is NOT agreement and
  // NOT a contradiction; it is a comparison that never happened, and it says so in words.
  if (agreement === "variation-defers") {
    return {
      text: observedText ?? (effectiveText as string),
      agreement,
      comparisonText: null,
      observedText,
      effectiveText,
      title: `The assigned variation sets allowSessionAgeOverride, so it asserts no band of its own and this offer takes the observed one. Nothing was compared. ${FULL_LIST_POINTER}`,
    };
  }

  if (agreement === "observed-silent") {
    // NOT `effectiveText`. The band that was compared can now be the VARIATION's own
    // (`comparisonSide`), and a change row carries a variation band with no `effectiveBand` at all —
    // on that row `effectiveText` is null and this chip rendered EMPTY while holding a perfectly
    // good 8–14 to show. That is KB-2658's silence bug reappearing at the edge this ticket created;
    // found by executing the shape, not by reading the diff (/preempt high H1).
    const inheritedText = effectiveText ?? formatAgeBand(comparisonBand);
    return {
      text: inheritedText as string,
      agreement,
      comparisonText: null,
      observedText,
      effectiveText,
      // KB-2706 round 2 — "no source band is RECORDED", never "the source did not state one".
      //
      // This branch's population includes every ages-class CHANGE row, where `readEffectiveBand`'s
      // docblock says the source band "is not carried" at all — so the row cannot support a claim
      // about what the vendor said. It is the same distinction `BAND_ABSENT_LABEL` already makes
      // ("not recorded", never "not stated") and for the same reason: two different realities reach
      // this branch and nothing here can tell them apart, so the sentence must cover both.
      title: `${inheritedText} — inherited from the variation. No source band is recorded on this row, so the inherited one is unverified.`,
    };
  }

  if (agreement === "absent") {
    return {
      text: BAND_ABSENT_LABEL,
      agreement,
      comparisonText: null,
      observedText,
      effectiveText,
      // Deliberately a claim about THIS ROW's data, not about the vendor. A pattern written before
      // the band projection shipped carries no bands either, and nothing here can tell the two apart.
      //
      // KB-2706 round 1, B3's lesson applied here too. This used to end "Open “Judge individually”
      // to read the band straight from the run." — an INSTRUCTION, and the card cannot tell the two
      // populations that reach `absent` apart any better than the paste could: a sample row whose
      // bands were never projected (where the full list does have them) and a row that genuinely
      // records none (where it does not). Sending the second group to a view that holds the same
      // nothing is the defect this ticket exists to remove, and B3 was the reviewer catching exactly
      // that shape on the paste. The clause now describes where the bands live without asserting
      // this row is among the rows that have them.
      //
      // KB-2706 round 3 (F7) — and it must not DENY a grade band this row carries.
      //
      // `absent` is decided by `gradeBandOf`, which requires both grade bounds as integer VALUES.
      // `formatBand` — what the paste renders — needs only a KEY, and either spelling. `variationBand`
      // is a raw cast with no normalisation and `normalizeGradeSpelling` spreads unpaired keys
      // through, so a row carrying `{minAgeGradeKey: g_1, maxAgeGradeKey: g_3}` and no values prints
      // `grades 1–3` in the paste while this title said "No age or grade band is recorded on this
      // row." Commit `30d13205` fixed this sentence's INSTRUCTION clause and left the DENIAL clause,
      // which is the half that is false.
      //
      // Reconciling the two formatters is the follow-up ticket's job (they disagree by design about
      // what a readable band is). What this sentence owes the reader is to stop denying what the row
      // holds, and to say why the thing it holds cannot be compared.
      title: hasGradeKey(item.observedBand) || hasGradeKey(item.variationBand) || hasGradeKey(item.effectiveBand)
        ? "No age band is recorded on this row, and its grade bounds are incomplete — a grade key is stored without its value, so no grade band can be read from them either. “Judge individually” shows the bands straight from the run for rows that carry them."
        : "No age or grade band is recorded on this row. “Judge individually” shows the bands straight from the run for rows that carry them.",
    };
  }

  if (agreement === "contradiction") {
    // Ages only on both halves. The grade band, when present, is identical on the two sides (it is
    // copied — see `comparableAges`), so repeating it here would pad the chip with the one part of
    // the row that cannot disagree, while pushing out the part that does.
    const observedAges = formatAgeOnly(item.observedBand) as string;
    const comparisonAges = formatAgeOnly(comparisonBand) as string;
    // KB-2692 — when the variation DEFERS, the band it asserts and the band the offer inherits are
    // different values, and the reviewer needs both: the assertion is the evidence the session is on
    // the wrong variation, the inherited band is what approving will actually write.
    const inheritedAges = formatAgeOnly(item.effectiveBand);
    const inheritedDiffers = inheritedAges != null && inheritedAges !== comparisonAges;
    return {
      text: observedAges,
      agreement,
      comparisonText: `→ ${comparisonAges}`,
      observedText,
      effectiveText,
      title: inheritedDiffers
        ? `The source states ${observedAges}${grades ? ` (${grades})` : ""}, but the assigned variation asserts ${comparisonAges} — the session is outside it. The variation defers, so this creates ${inheritedAges}. Check the variation.`
        : `The source states ${observedAges}${grades ? ` (${grades})` : ""}, but this creates ${comparisonAges}. Check the variation.`,
    };
  }

  if (agreement === "unverified") {
    // KB-2706 — the newest shape, and it must be told apart from the two below or the fix for
    // "match on a half-comparison" lands the reader in a sentence just as false.
    //
    // Both branches below key on `effectiveText`, because before this ticket every `unverified` row
    // was one whose CREATED band was missing or unprovable. A row that is unverified for an
    // UNCHECKED BOUND is a different row: the variation genuinely asserts a band — it can be read,
    // named, and acted on — and only one bound of it went unchecked. Falling through would print
    // "No band is recorded for what this creates" over a row carrying a perfectly good Ages 6+,
    // which is the calm-wrong-answer shape this ticket exists to remove, re-created one branch over
    // by its own fix. Verified by executing all thirteen rows of the verdict table, not by reading.
    const note = uncomparedBoundNote(item);
    if (note) {
      const assertedAges = formatAgeOnly(comparisonBand);
      return {
        text: observedText as string,
        agreement,
        // Both bands on screen: the reviewer needs to see the bound that IS asserted to judge the
        // one that is not.
        comparisonText: assertedAges ? `variation: ${assertedAges}` : null,
        observedText,
        effectiveText,
        // The shared sentence, verbatim — the paste renders the SAME string. Assembling it here as
        // well is how the two surfaces came to disagree about one row before.
        title: `${note} Not a disagreement, and not agreement.`,
      };
    }
    // Two shapes here now — the source-silent one moved to `observed-silent` above, which names the
    // same situation without borrowing the word this state reserves for "stated but uncheckable".
    // KB-2706 round 2 (B2) — key on the band that was actually COMPARED, not on `effectiveBand`.
    //
    // `comparisonSide` may have chosen `variationBand`, and this branch asked only about
    // `effectiveText`. So a row holding a perfectly good `variationBand: Ages 8–12` printed "No band
    // is recorded for what this creates" and blanked its own `comparisonText`. That is verbatim the
    // failure the `uncomparedBoundOf` branch above documents and guards against — the guard was
    // added to the new branch, and the shape stayed live in the one rows fall past into. It is also
    // KB-2692's empty-chip regression, which `observed-silent` already fixed four branches up with
    // this exact `?? formatAgeBand(comparisonBand)` fallback.
    // ⚠️ KB-2706 round 3 (F6) — the band `comparisonSide` CHOSE, not `effectiveBand` first.
    //
    // Round 2 added the fallback but left `effectiveText` as the preferred value, which is the wrong
    // way round: on a creation row the effective GRADE band is a verbatim copy of the observed one
    // (see `comparableAges`), so `effectiveText` wins and the sentence names the copy while the
    // variation's real `Ages 8–12` — the band `comparisonSide` selected and the verdict was computed
    // against — is dropped entirely. The paste then prints `variation's OWN asserted band: 8–12` two
    // lines above a sentence that never mentions it. B2's fixture omits `effectiveBand`, which is
    // exactly why its guard passed over this.
    //
    // `comparisonBand` is by definition the band the verdict used. Naming anything else is the same
    // class of error as the verdict itself claiming more than it compared.
    const comparedFromComparison = formatAgeBand(comparisonBand);
    const comparedText = comparedFromComparison ?? effectiveText;
    if (!comparedText) {
      return {
        // F8, same fallback as below: nothing readable on either side, so name the state in words
        // rather than rendering `null`.
        text: (observedText ?? BAND_ABSENT_LABEL) as string,
        agreement,
        comparisonText: null,
        observedText,
        effectiveText,
        // KB-2706 round 3 (F8) — `observedText` is nullable, and interpolating it printed the literal
        // string "null" into a ticket paste. It is null exactly when the row's bands are unpaired
        // grade keys, which `normalizeGradeSpelling` lets through.
        title: observedText
          ? `The source states ${observedText}. No band is recorded for what this creates, so it cannot be checked here.`
          : "No comparable band is recorded on either side of this row, so nothing could be checked here.",
      };
    }
    // Both sides speak but nothing independent lines up. Two reasons, and they are worth telling
    // apart in the tooltip: the scales differ (a grade-only row), or the inherited band cannot be
    // shown not to be an echo of the observed one. Put BOTH bands on screen either way — this is the
    // shape where the two sides are least reconcilable, so hiding one behind a tooltip is the worst
    // moment to do it.
    const bothStateAges = formatAgeOnly(item.observedBand) != null && formatAgeOnly(comparisonBand) != null;
    // "this creates" is a claim about the OFFER, and it is only true when the band came from
    // `effectiveBand`. When `comparisonSide` chose `variationBand`, that band is what the variation
    // ASSERTS — what the offer will create is not recorded on the row at all, so naming it as the
    // creation is the same over-claim this ticket exists to remove, in a verb.
    // The verb follows the band that is actually being named (F6): "this creates" is a claim about
    // the OFFER and is only true when the value came from `effectiveBand`.
    const asserts = comparedFromComparison && comparisonBand !== item.effectiveBand ? "the assigned variation asserts" : "this creates";
    // KB-2706 round 3 (F8) — `observedText` is nullable here too. "The source states null" reached
    // the calibration paste verbatim once round 2 made the paste render this title.
    const sourceClause = observedText ? `The source states ${observedText}; ` : "No band is recorded for the source; ";
    return {
      // KB-2706 round 3 (F8) — the CHIP was the other half of this defect and the more visible one:
      // `observedText as string` rendered the literal `null` on the card. Falling back to the band
      // this row CAN read is honest and keeps the chip non-empty (KB-2692's empty-chip regression).
      text: (observedText ?? comparedText) as string,
      agreement,
      comparisonText: `variation: ${comparedText}`,
      observedText,
      effectiveText,
      title: bothStateAges
        ? `${sourceClause}${asserts} ${comparedText}. It is not recorded whether the variation asserted that band or inherited it from this session, so the two cannot be compared here. ${FULL_LIST_POINTER}`
        : `${sourceClause}${asserts} ${comparedText}. They cannot be compared — a grade band on the created offer is copied from the source, so it is not independent evidence, and no age pair is recorded on both sides.`,
    };
  }

  // A containment match is not an equality, and must not borrow the equality sentence. The
  // contradiction branch above already tells its two reasons apart via `inheritedDiffers`; this is
  // the same distinction on the passing side, and it is the majority row shape (see
  // `isNarrowedWithinVariation`). Both bands go on screen: the assertion is what the reviewer needs
  // to sanity-check the variation assignment, and the inherited band is what approving will write.
  if (isNarrowedWithinVariation(item)) {
    const observedAges = formatAgeOnly(item.observedBand) as string;
    // ⚠️ KB-2706 round 3 (F2) — the sentinel is NOT a bound, and this branch printed it as one.
    //
    // `isUnderOpenCeiling`'s docblock says the AC-5 branch "deliberately does NOT print the
    // variation's stored max: that number is the NO_CEILING sentinel, and rendering it as a bound
    // the variation asserted is the KB-2692 chip regression". True — and that guard is UNREACHABLE
    // on a deferring variation, because `isNarrowedWithinVariation` is tested first and both
    // predicates hold on the same row. So the guard protected a path nothing takes while this branch
    // rendered `within the Ages 18–99 the assigned variation asserts` — and per `assertsCeiling`'s
    // own docblock all 13 in-repo sentinel-carrying variations set the flag `true`, i.e. essentially
    // the entire visible sentinel population lands HERE, not on the guarded path.
    //
    // Rather than reorder the two branches (which would move the sentinel problem rather than fix
    // it, and re-verdict rows on the majority shape), the sentinel is handled where the string is
    // built: an open-ended variation is described as open-ended.
    const asserted = comparableAges(comparisonBand);
    const assertsNoCeiling = asserted.max != null && !assertsCeiling(asserted.max);
    const assertedAges = assertsNoCeiling
      ? formatAgeOnly({ minAgeInYears: asserted.min } as AgeBand)
      : (formatAgeOnly(comparisonBand) as string);
    const inheritedAges = formatAgeOnly(item.effectiveBand) ?? observedAges;
    // A variation whose only stated bound IS the sentinel asserts nothing at all to narrow within,
    // so it gets a sentence that says that rather than a band it never stated.
    if (!assertedAges) {
      return {
        text: observedAges,
        agreement,
        comparisonText: null,
        observedText,
        effectiveText,
        title: `The source states ${observedAges}. The assigned variation sets no age bounds — its stored upper bound is the no-ceiling sentinel — so there is nothing here to narrow within. The variation defers, so this creates ${inheritedAges}.`,
      };
    }
    return {
      text: observedAges,
      agreement,
      comparisonText: `within ${assertedAges}`,
      observedText,
      effectiveText,
      title: assertsNoCeiling
        ? `The source states ${observedAges}, within the ${assertedAges} the assigned variation asserts, which sets no upper limit. The variation defers, so this creates ${inheritedAges}.`
        : `The source states ${observedAges}, within the ${assertedAges} the assigned variation asserts. The variation defers, so this creates ${inheritedAges}.`,
    };
  }

  // KB-2706 AC-5 — a completed comparison that is not an equality. The source states a ceiling and
  // the variation asserts none, so every bound was checked and none conflicts — but the two are not
  // the same band, and the equality sentence would be false of the row. Deliberately does NOT print
  // the variation's stored max: that number is the `NO_CEILING` sentinel, and rendering it as a
  // bound the variation asserted is the KB-2692 chip regression ("this creates Ages 18–99").
  if (isUnderOpenCeiling(item)) {
    return {
      text: observedText as string,
      agreement,
      comparisonText: null,
      observedText,
      effectiveText,
      title: `${observedText} — checked against the assigned variation, which asserts no upper limit. Every bound either side states was compared; the two are not the same band.`,
    };
  }

  return {
    text: observedText as string,
    agreement,
    comparisonText: null,
    observedText,
    effectiveText,
    title: `${observedText} — the source and the variation agree on the age band.`,
  };
}

// ── KB-2538 AC7 — the per-org Inbox filter (URL-addressable) ──────────────────────────────────────

/** The query param that scopes the Inbox to one camp org: `?tab=inbox&camp=lune-spark`. */
export const CAMP_FILTER_PARAM = "camp";

/** The active org filter, or null. Whitespace-only is treated as absent so `?camp=` is not a filter
 * on the empty slug (which would render a permanently empty, permanently confusing Inbox). */
export function readCampFilter(params: URLSearchParams): string | null {
  const v = (params.get(CAMP_FILTER_PARAM) || "").trim();
  return v || null;
}

/** The params for "filter to this org", preserving everything else (notably `tab=inbox`).
 *
 * KB-2641 — drops any calibration-set filter. The two scopes are mutually exclusive and the exclusion
 * is enforced HERE, in the URL builders, rather than by precedence in the component: the URL is the
 * single source of truth, so a URL that cannot express "both" is what makes "both" unreachable.
 *
 * KB-3598 round-1 review finding — drops any pattern id/prefix for the identical reason. This is the
 * builder behind `campFilterHref`, which `ReviewPatternCard`'s "all <camp> patterns" link uses from
 * EVERY render site, including inside `IsolatedPatternView`. Without this, clicking that link from the
 * isolated view carried `pattern=<id>` forward into the new URL, so `IsolatedPatternView`'s own
 * intercept (`CampDataInboxDashboard`'s early return) re-triggered on the very next render and the
 * operator never left the single-card view at all — a control that looked like navigation and did
 * nothing. Worked before this PR only because there was no intercept yet to fight. */
export function withCampFilter(params: URLSearchParams, campSlug: string): URLSearchParams {
  const next = withoutPatternIdParam(new URLSearchParams(params));
  next.delete(CALIBRATION_FILTER_PARAM);
  next.set(CAMP_FILTER_PARAM, campSlug);
  return next;
}

/** The params for "clear the filter", preserving everything else. */
export function withoutCampFilter(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(CAMP_FILTER_PARAM);
  return next;
}

/** The card's org-scope link target — a real href, so middle-click and bookmark work (AC7b). */
export function campFilterHref(params: URLSearchParams, campSlug: string): string {
  return `?${withCampFilter(params, campSlug).toString()}`;
}

// ── KB-2641 — the calibration-set Inbox filter (URL-addressable) ──────────────────────────────────

/** The query param that scopes the Inbox to one calibration set: `?tab=inbox&calibrationSet=<setId>`. */
export const CALIBRATION_FILTER_PARAM = "calibrationSet";

/** The active calibration-set filter, or null. Whitespace-only is absent, for the same reason
 * `readCampFilter` treats `?camp=` as absent: a filter on the empty id renders a permanently empty
 * Inbox under a banner claiming a scope. */
export function readCalibrationSetFilter(params: URLSearchParams): string | null {
  const v = (params.get(CALIBRATION_FILTER_PARAM) || "").trim();
  return v || null;
}

/** The params for "filter to this calibration set", preserving everything else (notably `tab=inbox`).
 * Drops the org filter — see `withCampFilter` for why the exclusion lives in the URL builders. */
export function withCalibrationSetFilter(params: URLSearchParams, setId: string): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(CAMP_FILTER_PARAM);
  next.set(CALIBRATION_FILTER_PARAM, setId);
  return next;
}

/** The params for "clear the calibration filter", preserving everything else. */
export function withoutCalibrationSetFilter(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(CALIBRATION_FILTER_PARAM);
  return next;
}

/** HIGH-severity batch-approve needs `min(3, counts.items)` inspected items (mirrors the server floor
 * in recordPatternVerdictCore); LOW has no floor. Returns the number of inspections required to approve. */
export function minInspectForApprove(severity: string, countsItems: number): number {
  return severity === "HIGH" ? Math.min(3, Math.max(0, countsItems)) : 0;
}

export function canBatchApprove(severity: string, countsItems: number, inspectedCount: number): boolean {
  return inspectedCount >= minInspectForApprove(severity, countsItems);
}

/** KB-2179 — the visible instructional line for an unmet min-inspection floor. Pure (no rendering)
 * so it's directly unit-testable. `null` means "render nothing" — AC4: a pattern with no floor
 * (LOW severity, `minInspect === 0`) gets no counter, and a satisfied floor gets no counter either. */
export function describeMinInspectionGate(minInspect: number, inspectedCount: number): string | null {
  if (minInspect <= 0 || inspectedCount >= minInspect) return null;
  if (inspectedCount === 0) return `Inspect at least ${minInspect} offers to unlock the Approve button`;
  return `Inspected ${inspectedCount} of ${minInspect} — open ${minInspect - inspectedCount} more to unlock the Approve button`;
}

// ── KB-2382 — the adopt outcome ("did my approve write anything?") ────────────────────────────────

/** What the server's `adoptOnApprove` stamp says happened. `pending` = the trigger hasn't stamped yet. */
export type AdoptOutcomeTone = "pending" | "applied" | "nothing" | "failed";

export interface AdoptOutcomeLine {
  tone: AdoptOutcomeTone;
  text: string;
}

export const ADOPT_OUTCOME_PENDING: AdoptOutcomeLine = { tone: "pending", text: "Applying your approval…" };

/**
 * Render the server's adopt stamp as one operator-facing line. Pure, so it is unit-tested directly.
 *
 * KB-2382 — this exists because approving in the Inbox used to write NOTHING and say nothing: the
 * trigger stamped its own refusal onto the doc and no surface ever read it. The stamp was diagnosable
 * only by opening Firestore. Every branch below therefore has to be honest in the unhappy direction —
 * a skip and a failure must never render as success, and an absent stamp must not render as either.
 *
 * `null` input means the stamp has not landed yet (the trigger is asynchronous — the verdict callable
 * returns before it runs), which is `pending`, NOT "nothing happened".
 */
export function describeAdoptOutcome(stamp: Record<string, unknown> | null | undefined): AdoptOutcomeLine {
  if (!stamp) return ADOPT_OUTCOME_PENDING;
  const status = String(stamp.status || "").trim();
  const applyMode = String(stamp.applyMode || "").trim();

  if (status === "applied" || status === "partial") {
    const created = Number(stamp.created ?? 0);
    const alreadyExisted = Number(stamp.alreadyExisted ?? 0);
    const appliedCount = Number(stamp.appliedCount ?? 0);
    const failed = Number(stamp.failed ?? 0);

    if (applyMode === "redate") return describeRedateOutcome(stamp);
    if (applyMode === "variation-move") return describeVariationMoveOutcome(stamp);

    if (applyMode === "creation") {
      const parts = [`${created} session${created === 1 ? "" : "s"} created`];
      if (alreadyExisted > 0) parts.push(`${alreadyExisted} already existed`);
      if (failed > 0) parts.push(`${failed} failed`);
      // KB-3639 — rows a creation guard withheld were left out of this approve; the line must say so rather
      // than read as the whole card (review round 2, finding 2). Stated as what THIS approve did, not as a
      // to-do: the pattern is terminal now, and a withheld row adopted later from the run's Review & Accept
      // would leave a to-do stale (review round 3, findings 1 + 3). Tone follows what was written — sessions
      // created is `applied` (round 3, finding 4).
      const withheldNotAdopted = Number(stamp.withheldNotAdopted ?? 0);
      if (withheldNotAdopted > 0) parts.push(`${withheldNotAdopted} withheld by a creation guard, not adopted by this approve`);
      // A partial adopt is NOT a success: the remaining items are still owed, and the pattern is
      // deliberately left without an `applied` stamp so the sweep retries them.
      return { tone: status === "partial" ? "failed" : created > 0 ? "applied" : "nothing", text: `${parts.join(" · ")}` };
    }

    // KB-4870 — before the bare "N offers updated" line, which would read as the whole card over the offers it
    // refused, and before the all-zero line, which would blame the run. Every other counter is kept, so a lock or a
    // rejection beside the refusal is not hidden. The card carries no `applied` record (status `partial`), so it is
    // not done: the adopt backfill re-enters it.
    const skippedVariationSplit = Number(stamp.skippedVariationSplit ?? 0);
    if (skippedVariationSplit > 0) {
      const count = (k: string) => Number(stamp[k] ?? 0);
      const parts: string[] = [];
      if (appliedCount > 0) parts.push(`${appliedCount} offer${appliedCount === 1 ? "" : "s"} updated`);
      parts.push(`${skippedVariationSplit} not applied: on another variation`);
      if (count("skippedLocked") > 0) parts.push(`${count("skippedLocked")} locked, not applied`);
      if (count("skippedRejected") > 0) parts.push(`${count("skippedRejected")} you rejected before, not applied`);
      if (count("skippedNotAppliable") > 0) parts.push(`${count("skippedNotAppliable")} on a field this path cannot write`);
      if (count("skippedNoChange") > 0) parts.push(`${count("skippedNoChange")} already matched`);
      if (count("skippedStale") > 0) parts.push(`${count("skippedStale")} edited since this run`);
      // Tone "failed" whatever was written, as the variationKey move card's refused-offer line and a creation
      // `partial` are: offers are still owed, so this is not a success.
      return {
        tone: "failed",
        text:
          `${appliedCount > 0 ? "" : "Nothing applied — "}${parts.join(" · ")}. This card's variationData names a different variation than those offers' variationKey, ` +
          `so it was not written: one variation's data is never stored under another's key, and a variationKey change moves through its own card. ` +
          `This card stays open; running the adopt backfill re-checks those offers.`,
      };
    }
    if (appliedCount > 0) {
      return { tone: "applied", text: `${appliedCount} offer${appliedCount === 1 ? "" : "s"} updated` };
    }
    // appliedCount 0 on a successful field-change apply: the skip counters (KB-2382 fix round) say
    // WHICH no-op this was. CAS-stale ("edited since this run") is a signal worth surfacing distinctly
    // — a human's curated edit outran the run and was deliberately kept.
    const skippedStale = Number(stamp.skippedStale ?? 0);
    const skippedNoChange = Number(stamp.skippedNoChange ?? 0);
    const skippedNotAppliable = Number(stamp.skippedNotAppliable ?? 0);
    const skippedLocked = Number(stamp.skippedLocked ?? 0);
    const skippedRejected = Number(stamp.skippedRejected ?? 0);
    // KB-3473 — checked FIRST among the appliedCount-0 branches, and stated in its own words, because a
    // lock skip is a DELIBERATE protection, not a failure. Without this branch an all-locked pattern
    // (appliedCount 0, every other counter 0) falls through to the all-zero "run artifact missing" line
    // below and blames the run for a director's decision — the exact KB-3239 shape, one reason over.
    // Tone "nothing", not "failed": the operator's approval was correctly declined by a lock they can see;
    // it is not a dead end to investigate. The pattern IS terminal (won't retry), which the wording says.
    //
    // ⚠️ Gated on `skippedNotAppliable === 0` so a co-present not-appliable count is NOT masked: the
    // not-appliable branch below carries a distinct dead-end warning ("apply by hand, or raise it if the
    // field should be auto-appliable") that a "nothing / locked" tone would swallow. A pattern pins a
    // single field, so the two counters are not expected to co-occur — this is a guard, not a live case —
    // but if they ever do, the not-appliable dead-end is the higher-cost one to surface. (KB-3473 review F2.)
    if (skippedLocked > 0 && appliedCount === 0 && skippedNotAppliable === 0) {
      const parts = [`${skippedLocked} locked, not applied`];
      if (skippedNoChange > 0) parts.push(`${skippedNoChange} already matched`);
      if (skippedStale > 0) parts.push(`${skippedStale} edited since this run`);
      return {
        tone: "nothing",
        text: `Nothing applied — ${parts.join(" · ")}. A locked field is protected from being overwritten; unlock it first if the vendor value should win.`,
      };
    }
    // KB-3486 — checked among the appliedCount-0 branches for the SAME reason as the locked branch above:
    // without it, an all-rejected pattern (appliedCount 0, every other counter 0) falls through to the
    // all-zero "run artifact missing or unreadable" line below and blames the run for a reviewer's
    // remembered rejection — a confidently-wrong absence render (the KB-3239 shape). Tone "nothing", not
    // "failed": the value was correctly withheld because a human rejected it before; it is not a dead end to
    // investigate. Gated on `skippedNotAppliable === 0` like the locked branch so a co-present not-appliable
    // count (a guard, not an expected case — a pattern pins one field) keeps its higher-cost dead-end line.
    if (skippedRejected > 0 && appliedCount === 0 && skippedNotAppliable === 0) {
      const parts = [`${skippedRejected} you rejected before, not applied`];
      if (skippedNoChange > 0) parts.push(`${skippedNoChange} already matched`);
      if (skippedStale > 0) parts.push(`${skippedStale} edited since this run`);
      return {
        tone: "nothing",
        text: `Nothing applied — ${parts.join(" · ")}. This value was rejected and remembered, so it is not re-applied — your decision is kept.`,
      };
    }
    // KB-3239 — checked BEFORE the stale/no-change line and stated in its own words, because it is a
    // different KIND of outcome. Stale and already-matched both mean the engine compared this offer
    // and declined it; not-appliable means the engine never looked. Folding it into "No offers
    // changed — N already matched" would assert a comparison that never happened.
    //
    // The wording has to carry the dead end, not just the count. The pattern is terminal the moment
    // it is stamped: no sweep retries it, and it will not be re-proposed as `open`. So an operator
    // who reads "nothing applied" and moves on has silently lost their judgement, which is the whole
    // defect. Measured 2026-09-09: 70 approved prod patterns / 1,025 rows in exactly this state.
    if (skippedNotAppliable > 0 && appliedCount === 0) {
      const parts = [`${skippedNotAppliable} item${skippedNotAppliable === 1 ? "" : "s"} on a field this path cannot write`];
      if (skippedNoChange > 0) parts.push(`${skippedNoChange} already matched`);
      if (skippedStale > 0) parts.push(`${skippedStale} edited since this run`);
      return {
        tone: "failed",
        text: `Verdict recorded, nothing applied — ${parts.join(" · ")}. Your approval is on file but the catalog is unchanged, and this pattern will not be re-proposed. Apply the change by hand, or raise it if the field should be auto-appliable.`,
      };
    }
    if (skippedStale > 0 || skippedNoChange > 0) {
      const parts: string[] = [];
      if (skippedNoChange > 0) parts.push(`${skippedNoChange} already matched`);
      if (skippedStale > 0) parts.push(`${skippedStale} edited since this run (curated values kept)`);
      return { tone: "nothing", text: `No offers changed — ${parts.join(" · ")}` };
    }
    // All-zero signature: the engine planned ZERO items — nothing was loaded or compared. That is a
    // lost/empty proposal artifact, not a benign no-op; narrating it as "already matched" would assert
    // a comparison that never ran, on a pattern the applied stamp means no sweep will retry.
    return {
      tone: "failed",
      text: "Nothing was applied — 0 proposal items loaded (run artifact missing or unreadable). The pattern is stamped applied and will not retry; investigate the run before re-approving.",
    };
  }

  if (status === "skipped") {
    const reason = String(stamp.reason || "").trim();
    return { tone: "nothing", text: reason ? `Nothing written — ${reason}` : "Nothing written" };
  }

  if (status === "failed") {
    const error = String(stamp.error || "").trim();
    return { tone: "failed", text: error ? `Adoption failed — ${error}` : "Adoption failed" };
  }

  // An unrecognised status must not read as success. It is a real stamp, so it is not `pending` either.
  return { tone: "failed", text: `Unrecognised adopt status "${status || "(unset)"}" — check the pattern doc` };
}

/**
 * KB-4269 — a `startDate`/`endDate` card re-dates its offers (`adoptDateCardRedate.ts`), so its outcome is
 * not the field-change engine's. Before KB-4269 this card read "Verdict recorded, nothing applied — N items
 * on a field this path cannot write". Each outcome is stated in its own words:
 *  - re-dated: the offers that moved;
 *  - already held: the dates were already right (an earlier approve, or the sibling card's, wrote them);
 *  - waiting: the offer's span also needs its other date card approved, and nothing was written for it;
 *  - not re-dated: a guard refused the row. The first reason is quoted, because the operator cannot act on
 *    a count. Any refused row makes the tone `failed`: something the operator approved did not land.
 */
function describeRedateOutcome(stamp: Record<string, unknown>): AdoptOutcomeLine {
  const n = (k: string) => Number(stamp[k] ?? 0);
  const redated = n("redated");
  const already = n("alreadyRedated");
  const waiting = n("waiting");
  const skipped = n("skippedRedate");
  const offers = (k: number) => `${k} offer${k === 1 ? "" : "s"}`;
  /** The first row's own reason, quoted; marked "first" when there are more. */
  const firstReason = (key: "skips" | "waits", count: number) => {
    const list = Array.isArray(stamp[key]) ? (stamp[key] as Array<{ reason?: unknown }>) : [];
    const r = String(list[0]?.reason ?? "").trim();
    return r ? ` (${count > 1 ? "first: " : ""}${r})` : "";
  };
  const parts: string[] = [];
  if (redated > 0) parts.push(`${offers(redated)} re-dated`);
  if (already > 0) parts.push(`${offers(already)} already ${already === 1 ? "holds" : "hold"} the new dates`);
  if (waiting > 0) parts.push(`${offers(waiting)} waiting on their other date card${firstReason("waits", waiting)}`);
  if (skipped > 0) parts.push(`${offers(skipped)} not re-dated${firstReason("skips", skipped)}`);
  if (skipped > 0) {
    return { tone: "failed", text: `${redated > 0 ? "" : "Nothing re-dated — "}${parts.join(" · ")}` };
  }
  if (redated > 0) return { tone: "applied", text: parts.join(" · ") };
  // The wait reason names the other card and says that approving it re-dates the offer.
  if (waiting > 0) return { tone: "nothing", text: `Nothing re-dated yet — ${parts.join(" · ")}` };
  // Not "no offers changed": the span may have been written moments ago by this card's other date card,
  // which this card's approval helped complete, or by this card's own earlier, interrupted adopt (round 1,
  // finding 5). What is known is only that the dates are in place now.
  if (already > 0) return { tone: "nothing", text: `Dates already in place — ${parts.join(" · ")} (written by this span's other date card, an earlier attempt, or already right)` };
  return { tone: "failed", text: "Nothing re-dated — the stamp records no offer at all. Check the pattern doc." };
}

/**
 * KB-4304 — a `variationKey` card moves its offers (`adoptVariationCardMove.ts`), so its outcome is not the
 * field-change engine's ("Verdict recorded, nothing applied" before KB-4304). Each outcome in its own words:
 *  - moved: the offers now on the target variation;
 *  - already there: the offer already sat on the target (an earlier approve, or the operator tool, moved it);
 *  - not moved: the move core refused the row (a collision on the target key, a held price or length, a
 *    session-owned value, a lock or rejection, an edit since the plan). The first reason is quoted, because the
 *    operator cannot act on a count, and any refused row makes the tone `failed`.
 */
function describeVariationMoveOutcome(stamp: Record<string, unknown>): AdoptOutcomeLine {
  const n = (k: string) => Number(stamp[k] ?? 0);
  const moved = n("moved");
  const already = n("alreadyMoved");
  const skipped = n("skippedMove");
  const offers = (k: number) => `${k} offer${k === 1 ? "" : "s"}`;
  const list = Array.isArray(stamp.skips) ? (stamp.skips as Array<{ reason?: unknown }>) : [];
  const first = String(list[0]?.reason ?? "").trim();
  const parts: string[] = [];
  if (moved > 0) parts.push(`${offers(moved)} moved to the new variation`);
  if (already > 0) parts.push(`${offers(already)} already on it`);
  if (skipped > 0) parts.push(`${offers(skipped)} not moved${first ? ` (${skipped > 1 ? "first: " : ""}${first})` : ""}`);
  // A `partial` stamp means some skipped offer may still move (a race, or a nightly with no answer for it): the card
  // is open, not finished, and must not read like a final refusal (KB-4304 review round 2, finding 2).
  // Says only what holds for every retryable cause (a race, or a nightly with no answer): the backfill is an admin
  // call, not a schedule, and a refused offer beside a retryable one is refused again on the re-plan (round 3, 2–3).
  if (stamp.status === "partial") parts.push("card left open — at least one offer may still move when an admin runs the adopt backfill");
  if (skipped > 0) return { tone: "failed", text: `${moved > 0 ? "" : "Nothing moved — "}${parts.join(" · ")}` };
  if (moved > 0) return { tone: "applied", text: parts.join(" · ") };
  if (already > 0) return { tone: "nothing", text: `Already on the new variation — ${parts.join(" · ")}` };
  return { tone: "failed", text: "Nothing moved — the stamp records no offer at all. Check the pattern doc." };
}

/**
 * Read one pattern's `adoptOnApprove` stamp. `null` = not stamped yet.
 *
 * A plain `getDoc`, polled by the caller, rather than an `onSnapshot` subscription: this service has no
 * realtime listeners anywhere today, and one card awaiting one stamp for a few seconds does not justify
 * introducing the first (and the teardown discipline that comes with it).
 */
export async function fetchAdoptOutcome(patternDocId: string, dbHandle: Firestore = defaultDb): Promise<Record<string, unknown> | null> {
  const snap = await getDoc(doc(dbHandle, REVIEW_PATTERNS_COLLECTION, patternDocId));
  if (!snap.exists()) return null;
  const stamp = (snap.data() as Record<string, unknown> | undefined)?.adoptOnApprove;
  return stamp && typeof stamp === "object" ? (stamp as Record<string, unknown>) : null;
}

// ── KB-2181 — the two-question card ───────────────────────────────────────────────────────────────

/**
 * The closed tag vocabulary for a field issue.
 *
 * The ONE home is `scripts/camp-data/_shared/trustLib.cjs` (`FIELD_ISSUE_FIELD_CLASSES`), which is what
 * the server validator enforces; this is the browser-side copy, because the frontend bundle cannot
 * require a CommonJS script from outside src/. `campDataReviewInbox.test.ts` reads the canonical file
 * and asserts the two are identical, so a divergence fails a test rather than shipping a dropdown that
 * can offer a value the server refuses.
 */
export const FIELD_ISSUE_FIELD_CLASSES = [
  "price",
  "dates",
  "regUrl",
  "url",
  "ages",
  "availability",
  // KB-2815 — same user-harm shape as dates/availability (a wrong pickup time / care-fee window).
  "careWindow",
  "title",
  "description",
  "eligibility",
  "other",
] as const;

// ── KB-2659 — "unable to judge" ───────────────────────────────────────────────────────────────────

/**
 * The closed vocabulary for an operator abstention, and its labels.
 *
 * The ONE home is `scripts/camp-data/_shared/trustLib.cjs` (`OPERATOR_ABSTAIN_REASONS`), which is what
 * the server enforces; this is the browser-side copy, for the same reason `FIELD_ISSUE_FIELD_CLASSES`
 * above has one — the frontend bundle cannot require a CommonJS script from outside src/.
 * `campDataReviewInbox.test.ts` reads the canonical file and asserts the two are identical, so a
 * divergence fails a test rather than shipping a dropdown offering a value the server refuses.
 */
export const OPERATOR_ABSTAIN_REASONS = ["evidence-gone", "evidence-inconclusive", "out-of-scope"] as const;

export type OperatorAbstainReason = (typeof OPERATOR_ABSTAIN_REASONS)[number];

/**
 * Operator-facing wording for each reason. The enum values are what get COUNTED; these are what make
 * the distinction between them legible at the moment of choosing, which is what keeps the counts
 * meaningful — a reason nobody can tell apart from its neighbour is picked at random.
 */
export const ABSTAIN_REASON_LABELS: Record<OperatorAbstainReason, string> = {
  "evidence-gone": "Evidence is gone — the vendor page no longer exists",
  "evidence-inconclusive": "Evidence is there but doesn't settle it",
  "out-of-scope": "Not mine to judge — outside this review's scope",
};

/** The card's label for the abstain action. */
export const ABSTAIN_ACTION_LABEL = "Unable to judge";

/**
 * What a resolved card tells its host about the pattern it just left.
 *
 * `terminal` is the only field because it is the only thing a host has ever needed to know and the
 * one thing it was previously forced to assume. `summarizeCalibrationProgress` counts a member as
 * judged when `isTerminalPatternStatus(p.status)`, so any optimistic progress update has to agree
 * with that definition or the live line and the reloaded line diverge.
 */
export interface PatternResolution {
  /** True when the pattern reached a terminal status. An abstention is deliberately `false`. */
  terminal: boolean;
}

/** An abstention is submittable once a reason is chosen — the server refuses one without. */
export function canSubmitAbstention(reason: string): boolean {
  return (OPERATOR_ABSTAIN_REASONS as readonly string[]).includes(reason);
}

/**
 * Q1's label. A `creation` pattern is proposing sessions that do not exist yet, so the question is
 * whether they should; every other field class is proposing an edit to a session that already exists,
 * where "should this exist" is both answered and not what is being asked.
 */
export function describeExistenceQuestion(fieldClass: string): string {
  return fieldClass === "creation" ? "Session should exist" : "Apply this change";
}

/** Q2's label — the same question regardless of pattern type. */
export const FIELDS_QUESTION_LABEL = "All fields look good";

/** The judge's answer to Q1, as returned by `getCampDataJudgeVerdict`. */
export type JudgeState = "approve" | "reject" | "abstain" | "none";

export interface JudgeVerdict {
  state: JudgeState;
  verdictBy: string | null;
  abstainReason: string | null;
  confidence: number | null;
}

/** The degrade value: no judge row, or the lookup failed. Reads as "no opinion", never as agreement. */
export const NO_JUDGE_VERDICT: JudgeVerdict = { state: "none", verdictBy: null, abstainReason: null, confidence: null };

export type JudgeAgreement = "agreed" | "disagreed" | "abstained";

export interface JudgeAgreementLine {
  agreement: JudgeAgreement;
  text: string;
}

/**
 * How the judge's Q1 answer reads NEXT TO the human's — computed only after the human has answered.
 *
 * "abstained" is a first-class third state, not a failure to be agreement. Per KB-2156 the QC
 * abstention rate is dominated by structural causes, so this is the COMMON case, and rendering it as
 * agreement would inflate the very concordance number that gates tier promotion. `none` (no judge row
 * at all — the judge never looked at this pattern) reads as abstained for the same reason: the honest
 * statement is "no opinion", never "agrees with you".
 */
export function describeJudgeAgreement(judge: JudgeVerdict, humanAnswer: Verdict): JudgeAgreementLine {
  if (judge.state === "none") return { agreement: "abstained", text: "Judge: no verdict for this pattern" };
  if (judge.state === "abstain") {
    return { agreement: "abstained", text: judge.abstainReason ? `Judge: abstained (${judge.abstainReason})` : "Judge: abstained" };
  }
  const label = judge.state === "approve" ? "Approve" : "Reject";
  return judge.state === humanAnswer
    ? { agreement: "agreed", text: `Judge agreed (${label})` }
    : { agreement: "disagreed", text: `Judge disagreed — it said ${label}` };
}

/**
 * Q2's judge line.
 *
 * The QC shadow harness judges Q1 only — it has no field-level opinion, and amendment R5 says so
 * explicitly ("if the judge's field-level opinion is later captured structurally, Q2-vs-Q2 becomes a
 * separate overlap stream"). So this is always an abstention today. It exists as a function rather
 * than a literal because the honest answer is a property of the corpus, not of the card: when a Q2
 * judge verdict starts being recorded, this is the ONE place that changes.
 */
export function describeJudgeFieldsAgreement(): JudgeAgreementLine {
  return { agreement: "abstained", text: "Judge: no field-level verdict is captured yet" };
}

/**
 * Can this verdict be submitted?
 *
 * The note is required whenever the note input is SHOWN (i.e. on a fields-disagree), because an
 * untagged, unexplained defect report is unactionable debt. The server enforces the same rule — this
 * is the affordance, not the guarantee.
 */
export function canSubmitVerdict(params: { fieldsOk: FieldsOk; fieldIssueNote: string }): boolean {
  if (params.fieldsOk !== false) return true;
  return params.fieldIssueNote.trim().length > 0;
}

/**
 * Prefill the field tag from the pattern's own field class — on a CHANGE pattern the field under
 * discussion is already known, so making the operator re-pick it is friction that costs tag coverage
 * (and amendment R6 makes an untagged issue count for nothing). A `creation` pattern is about the
 * sessions rather than one field, so it starts blank; so does any class outside the tag vocabulary.
 */
export function prefillFieldIssueField(fieldClass: string): string {
  return (FIELD_ISSUE_FIELD_CLASSES as readonly string[]).includes(fieldClass) ? fieldClass : "";
}

/** Build the `recordCampDataPatternVerdict` request payload. Batch omits itemIds; single carries them. */
export function buildVerdictPayload(params: {
  patternDocId: string;
  verdict: WireVerdict;
  mode: VerdictMode;
  inspectedItemIds: string[];
  itemIds?: string[];
  itemOutcomes?: Record<string, Verdict>;
  fieldsOk?: FieldsOk;
  fieldIssueField?: string | null;
  fieldIssueNote?: string | null;
  revisedAfterReveal?: boolean;
  channel?: VerdictChannel;
  abstainReason?: string;
  lock?: boolean;
  senderSelection?: { orgId: string } | null;
}): RecordVerdictPayload {
  const base: RecordVerdictPayload = {
    patternDocId: params.patternDocId,
    verdict: params.verdict,
    mode: params.mode,
    inspectedItemIds: [...new Set(params.inspectedItemIds)],
  };
  // KB-2659 — attached ONLY on an abstention. The server refuses `abstainReason` on a decisive verdict
  // (`abstain-reason-without-abstention`), so leaking it through on an approve would turn a stale piece
  // of card state into a rejected submission.
  if (params.verdict === "abstain" && params.abstainReason) base.abstainReason = params.abstainReason;
  if (params.channel !== undefined) base.channel = params.channel;
  if (params.mode === "single") base.itemIds = params.itemIds ?? [];
  // KB-2655 — the mixed ruling's exceptions ride on the batch verdict. An EMPTY map is omitted rather
  // than sent: "the operator ruled on nothing individually" and "the operator did not use the feature"
  // are the same payload, and keeping them the same is what makes the unmixed path provably unchanged.
  if (params.mode === "batch" && params.itemOutcomes && Object.keys(params.itemOutcomes).length > 0) {
    base.itemOutcomes = { ...params.itemOutcomes };
  }
  // KB-2181 — Q2 travels only when it was actually asked. `fieldsOk === undefined` means the caller
  // never posed the question (the veto payload, a single-item verdict), and such a payload must stay
  // identical to its pre-KB-2181 shape rather than claim an unanswered "null".
  if (params.fieldsOk !== undefined) {
    base.fieldsOk = params.fieldsOk;
    const note = (params.fieldIssueNote ?? "").trim();
    // The tag is only meaningful on a reported defect; the server refuses a tag without one.
    base.fieldIssueField = params.fieldsOk === false ? params.fieldIssueField || null : null;
    base.fieldIssueNote = params.fieldsOk === false ? note || null : null;
    base.revisedAfterReveal = params.revisedAfterReveal ?? false;
  }
  // KB-3317 — the director lock rides ONLY on a clean batch reject (the one shape the server's fan-out
  // honours). On an approve the server throws `lock-requires-reject`; on a mixed ruling or a single-mode
  // reject the fan-out's `isCleanBatchReject` gate skips it, so a lock there would silently do nothing —
  // exactly the "offer a control the server ignores" failure this ticket exists to avoid. Omit it in all
  // three cases so the payload stays what the server can act on.
  const hasItemOutcomes = Boolean(params.itemOutcomes && Object.keys(params.itemOutcomes).length > 0);
  if (params.lock === true && params.verdict === "reject" && params.mode === "batch" && !hasItemOutcomes) {
    base.lock = true;
  }
  // KB-3597 — the sender card's chosen org rides only on a batch approve, the one shape the server takes.
  // A choice left selected when the operator rejects instead must not turn the reject into a refused call.
  const selectedOrgId = params.senderSelection?.orgId?.trim();
  if (selectedOrgId && params.verdict === "approve" && params.mode === "batch") {
    base.senderSelection = { orgId: selectedOrgId };
  }
  return base;
}

/** Rotate the displayed sample window over the fetched sampleItems (≤5) — the "resample ↻" affordance.
 * Pure + deterministic (rotation offset), so a test can assert the displayed subset changed. */
export function rotateSample<T>(items: T[], showCount: number, rotation: number): T[] {
  if (items.length === 0) return [];
  const k = Math.min(showCount, items.length);
  const start = ((rotation % items.length) + items.length) % items.length;
  const out: T[] = [];
  for (let i = 0; i < k; i++) out.push(items[(start + i) % items.length]);
  return out;
}

/** A stable per-item key for inspection tracking + single-mode verdicts. */
export function itemKey(item: { observedId?: string; offerId?: string; itemId?: string }): string {
  return String(item.itemId || item.observedId || item.offerId || "");
}

/** The admin session link for an item — the offer route when an offerId is present, else null (a
 * bare observed session has no admin offer page; the url/regUrl links still render). */
export function sessionLinkFor(item: { offerId?: string }): string | null {
  return item.offerId ? `https://kidsbooked.com/session/${item.offerId}` : null;
}

/** KB-1895 — the marketing-site admin preview route for a PROPOSED (not-yet-created) session. */
export const SESSION_PREVIEW_URL_BASE = "https://kidsbooked.com/session/preview/new";
/** KB-2545 — the marketing-site admin preview route for a proposed EDIT to an existing session. */
export const SESSION_EDIT_PREVIEW_URL_BASE = "https://kidsbooked.com/session/preview/edit";

/**
 * KB-1895 / KB-2545 — the right session chip for an item.
 *
 * - A creation pattern's offer does NOT exist yet, so `sessionLinkFor` would produce a dead link (the
 *   would-be docId); those items open the marketing site's KB-1895 admin preview instead — the REAL
 *   session page rendered from the run artifact's rebuild-faithful row.
 * - A change pattern whose field is determinable (`patternFieldOf`) AND appliable
 *   (`isAppliableOfferField`) opens the KB-2545 edit-preview instead of the live page — it renders the
 *   session as it WOULD look after the apply, which is strictly more useful than the current state for
 *   reviewing a pending change. Gated on appliability so the chip never promises a write the apply
 *   would skip as `field-not-appliable` (mirrors the KB-2545 route's own AC5 gate).
 * - A change pattern whose field can't be determined (legacy/malformed `groupKeyText`) falls back to
 *   the pre-KB-2545 live-session chip — unchanged, so an older pattern doc never loses its only link.
 * - A change pattern with a determinable but NON-appliable field gets no chip at all — a live-session
 *   link there would silently promise a preview capability this field doesn't have, and the plain
 *   `session` link added no information the operator didn't already have from the card itself.
 *
 * Null → no chip.
 */
export function sessionOrPreviewLinkFor(
  pattern: Pick<ReviewPattern, "fieldClass" | "campSlug" | "latestRunId" | "groupKeyText">,
  item: { offerId?: string },
): { href: string; label: string } | null {
  if (pattern.fieldClass === "creation") {
    if (!item.offerId || !pattern.campSlug || !pattern.latestRunId) return null;
    return {
      href: `${SESSION_PREVIEW_URL_BASE}/${pattern.campSlug}/${pattern.latestRunId}/${item.offerId}`,
      label: "preview",
    };
  }
  const field = patternFieldOf(pattern);
  if (field === null) {
    const href = sessionLinkFor(item);
    return href ? { href, label: "session" } : null;
  }
  if (!isAppliableOfferField(field)) return null;
  if (!item.offerId || !pattern.campSlug || !pattern.latestRunId) return null;
  return {
    href: `${SESSION_EDIT_PREVIEW_URL_BASE}/${pattern.campSlug}/${pattern.latestRunId}/${item.offerId}/${field}`,
    label: "preview",
  };
}

/** Map a pattern `sampleItems` entry or an artifact proposal row into the shared InboxItem shape. */
export function toInboxItem(raw: Record<string, unknown>, enrich?: Record<string, unknown>): InboxItem {
  const e = enrich || {};
  const s = (...cands: unknown[]): string | undefined => {
    for (const c of cands) if (typeof c === "string" && c) return c;
    return undefined;
  };
  const n = (...cands: unknown[]): number | undefined => {
    for (const c of cands) if (typeof c === "number" && isFinite(c)) return c;
    return undefined;
  };
  const offerId = s(raw.offerId, raw.docId, e.docId);
  const observedId = s(raw.observedId, e.observedId);
  // KB-1894 — an artifact creation row carries its computed price on the preview block (KB-1712).
  const preview = (raw.preview && typeof raw.preview === "object" ? raw.preview : {}) as Record<string, unknown>;
  const price = n(raw.price, preview.priceRegular, e.priceRegular);
  const observedPriceRegular = n(raw.observedPriceRegular, e.observedPriceRegular);
  return {
    itemId: String(observedId || offerId || raw.id || ""),
    offerId,
    observedId,
    // KB-1890 — a change-proposal row carries only docId/from/to; enrich from the changedOffers audit row.
    title: s(raw.title, raw.displayTitle, e.displayTitle),
    startDateIso: s(raw.startDateIso, e.startDateIso),
    endDateIso: s(raw.endDateIso, e.endDateIso),
    locationId: s(raw.locationId, e.locationId),
    locationText: s(raw.locationText, e.locationText),
    url: s(raw.url, e.url),
    regUrl: s(raw.regUrl, e.regUrl),
    variationKey: s(raw.variationKey, e.variationKey),
    price,
    // KB-2658's readers handle BOTH shapes (a pre-projected sampleItem band and the raw observed*
    // fields), so this ticket does not add a second extraction for those two.
    observedBand: readObservedBand(raw),
    effectiveBand: readEffectiveBand(raw, preview, e),
    // KB-2655 — the variation's OWN band and its override flag. Only the full-N callable produces these
    // (it reads them from the variation doc, the only place the PRE-override band survives), so they are
    // passed straight through rather than re-derived. See `getPatternItemsCore.PatternItemRow`.
    variationBand: (raw.variationBand as AgeBand | undefined) || undefined,
    // KB-2692 — the callable's value first, then the row's own. `offerComputeCore` spreads the whole
    // matched variation into `variationData`, so the FLAG survives the overwrite that destroys the
    // pre-override band. Reading it here is what gives a sample row — which can never carry
    // `variationBand` — an honest verdict instead of a comparison against itself.
    variationAllowsAgeOverride: readAllowsAgeOverride(raw, preview),
    numDays: n(raw.numDays, raw.numDaysCalc, preview.numDays, e.numDays),
    observedPriceRegular,
    // KB-3718 — a price on screen carries its level, and so does a row that RECORDS one (a "none stated"
    // creation has no price to show but its level is still a fact) — the same rule as the Run-details
    // `priceLevelForEntry`. Read from the row first, then its enrichment (the changedOffers audit row).
    ...(price !== undefined || observedPriceRegular !== undefined || recordsPriceLevel(raw) || recordsPriceLevel(e)
      ? { priceLevel: readPriceLevel(raw, e) }
      : {}),
    // KB-2874 — one of FOUR review-flag projections (round 1 finding 1). Carried, never
    // derived: this surface cannot see the vendor calendar, and re-deriving "is this a holiday" from
    // the row's dates here would be a SECOND producer of a rule the adapter already owns — the exact
    // drift this file's other notes warn about. An empty array is normalized away so absence stays
    // absence rather than becoming a "checked and cleared" claim.
    reviewFlags: readReviewFlags(raw),
    // KB-3317 — the value's standing. Present only on rows whose source carries it (pattern sampleItems,
    // full-page callable rows); an artifact-exploded row from `loadPatternItems` carries none, so its
    // `provenance` is `undefined` and the card renders no sub-line — never a fabricated one.
    provenance: readProvenance(raw),
    from: raw.from,
    to: raw.to,
  };
}

/**
 * KB-3317 — read the item's field PROVENANCE off a sample row (or a full-page callable row).
 *
 * Returns `undefined` for anything that is not a provenance object with a `canonical` winner, so an old
 * pre-KB-3301 doc, an artifact-exploded row, or a malformed block all degrade to "no standing" — the card
 * then renders no sub-line, rather than a confident "no prior judgement" over a value whose standing was
 * never loaded (the exact failure this ticket exists to avoid). Non-string primitives coerce to `null`,
 * and each `basis` display field is kept only when it is genuinely a string — a null/absent basis value
 * is dropped, never rendered as "against vendor null".
 */
export function readProvenance(raw: Record<string, unknown>): ItemProvenance | undefined {
  const p = raw.provenance;
  if (!p || typeof p !== "object" || Array.isArray(p)) return undefined;
  const po = p as Record<string, unknown>;
  const canon = po.canonical;
  if (!canon || typeof canon !== "object" || Array.isArray(canon)) return undefined;
  const c = canon as Record<string, unknown>;
  const strOrNull = (v: unknown): string | null => (typeof v === "string" ? v : null);
  const canonical = {
    valueHash: strOrNull(c.valueHash),
    sourceClass: strOrNull(c.sourceClass),
    actorId: strOrNull(c.actorId),
    at: strOrNull(c.at),
    cid: strOrNull(c.cid),
  };
  // A canonical winner with NO identity (no sourceClass AND no valueHash) is "no winner" — the deriver
  // returns null for exactly that (`provenanceFromFieldRecord` bails on an absent `winner`). Treat it as
  // no-standing rather than rendering "set by (unknown editor) · no prior judgement" over a winner that
  // has no identity — that would assert a standing the data does not support (round-1 review nit).
  if (canonical.sourceClass === null && canonical.valueHash === null) return undefined;
  const out: ItemProvenance = {
    policy: strOrNull(po.policy),
    canonical,
  };
  const basis = po.basis;
  if (basis && typeof basis === "object" && !Array.isArray(basis)) {
    const b = basis as Record<string, unknown>;
    const bo: ItemBasis = { cid: strOrNull(b.cid), valueHash: strOrNull(b.valueHash), judgedAt: strOrNull(b.judgedAt) };
    if (typeof b.value === "string") bo.value = b.value;
    if (typeof b.runId === "string") bo.runId = b.runId;
    if (typeof b.vendorType === "string") bo.vendorType = b.vendorType;
    if (typeof b.observedAt === "string") bo.observedAt = b.observedAt;
    out.basis = bo;
  }
  return out;
}

/**
 * KB-2874 — read the adapter's review flags off a sample row or a raw artifact proposal row.
 *
 * Returns `undefined` for anything that is not a non-empty array of objects, so a malformed or absent
 * value degrades to "this row carries no flags" rather than throwing inside a list render. Note the
 * asymmetry that matters: `undefined` here means *unknown or none*, and the CARD is what states the
 * measured count — a row-level absence must never be rendered as "checked, nothing found".
 */
function readReviewFlags(raw: Record<string, unknown>): CampDataReviewFlag[] | undefined {
  const flags = raw.reviewFlags;
  if (!Array.isArray(flags) || flags.length === 0) return undefined;
  const kept = flags.filter((f): f is CampDataReviewFlag => Boolean(f) && typeof f === "object" && typeof (f as { kind?: unknown }).kind === "string");
  return kept.length > 0 ? kept : undefined;
}

/** A band with at least one stated bound, or undefined — never an empty object (absence is a state). */
function bandOrUndefined(band: AgeBand): AgeBand | undefined {
  return Object.keys(band).length > 0 ? band : undefined;
}

/**
 * KB-2692 — one grade spelling, normalised at the boundary.
 *
 * A pattern doc's `sampleItems` carry the pipeline's `minAgeGradeKey` spelling (`derivePatternDocs`
 * mirrors `observedBandsFromForm`); the full-N callable's `observedBandOf` emits `minGradeKey`. Both
 * were typed as `AgeBand`, which TypeScript merged into a union of the two spellings — so it
 * compiled green while `gradeBandOf`, reading only the `minAge*` half, saw nothing on any callable
 * row and the grade annotation silently disappeared from the "Judge individually" list.
 *
 * Normalising here rather than renaming the wire field keeps this reader compatible with rows
 * already in flight from a deployed callable, which a rename would not be.
 */
function normalizeGradeSpelling(band: Record<string, unknown>): AgeBand {
  const out: AgeBand = { ...(band as AgeBand) };
  const num = (v: unknown): number | undefined => (typeof v === "number" && isFinite(v) ? v : undefined);
  const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
  if (out.minAgeGradeKey == null && str(band.minGradeKey) != null && num(band.minGradeValue) != null) {
    out.minAgeGradeKey = str(band.minGradeKey);
    out.minAgeGradeValue = num(band.minGradeValue);
  }
  if (out.maxAgeGradeKey == null && str(band.maxGradeKey) != null && num(band.maxGradeValue) != null) {
    out.maxAgeGradeKey = str(band.maxGradeKey);
    out.maxAgeGradeValue = num(band.maxGradeValue);
  }
  return out;
}

/**
 * KB-2692 — does the row's assigned variation defer to the session's own age?
 *
 * Two sources, in precedence order, and the second is the one that matters. The full-N callable
 * reads the flag off the variation DOCUMENT and sends it as `variationAllowsAgeOverride`; every
 * other row — a pattern doc's `sampleItems`, a raw artifact row — carries it inside
 * `preview.variationData`, because `offerComputeCore` builds that map by spreading the entire
 * matched variation and the overwrite it then performs touches only the band fields.
 *
 * `undefined` is a THIRD state, not a `false`: a pattern doc written before this projection existed
 * records nothing either way, and a row that cannot say whether its band was compared or echoed must
 * report `unverified`, never agreement. Coercing absent to `false` here would restore exactly the
 * false clean bill this ticket exists to remove, on precisely the rows nobody has re-derived yet.
 *
 * ⚠️ SIBLING — THREE readers of this one flag, not two. All of them:
 *   1. this one — the row, at render time, returning `undefined` when unstated
 *   2. `allowsAgeOverrideOfRow` (`scripts/camp-data/_shared/derivePatternDocs.cjs`) — the row, at
 *      pattern-derivation time, returning `null` when unstated
 *   3. `variationAllowsAgeOverrideOf` (`functions/src/camp-data/getPatternItemsCore.ts`) — the
 *      variation DOCUMENT, in the Cloud Function, returning `undefined` when unstated
 *
 * Three exist because all three run in different build worlds with no shared import — the same
 * reason `observedBandOfRow` is duplicated across them. They MUST agree on the absent-is-not-false
 * rule: if any returns `false` for a silent row, the row gains a flag it did not earn and this
 * reader believes it completely. Change one, change all three.
 *
 * (KB-2692's first cut named only the first two and missed #3, which was inlined and DID coerce
 * absent to `false`. Counting the homes wrong is how a fact acquires a fourth one.)
 */
function readAllowsAgeOverride(raw: Record<string, unknown>, preview: Record<string, unknown>): boolean | undefined {
  if (typeof raw.variationAllowsAgeOverride === "boolean") return raw.variationAllowsAgeOverride;
  const vd = (preview.variationData && typeof preview.variationData === "object" ? preview.variationData : {}) as Record<string, unknown>;
  return typeof vd.allowSessionAgeOverride === "boolean" ? vd.allowSessionAgeOverride : undefined;
}

/**
 * KB-2658 — the source-stated band, from EITHER shape the row can arrive in.
 *
 * Two shapes, one reader: a sample item carries the pre-projected `observedBand` (derivePatternDocs
 * built it at write time), while an artifact row carries the six raw `observed*` fields. Reading both
 * here is what lets the sample rows and the "Judge individually" rows render the same band from one
 * code path — two readers is how the exploded list and the card end up disagreeing.
 *
 * Grade key and value are taken only as a COHERENT PAIR, matching `observedBandsFromForm`: a key
 * without its value is reachable upstream and is not something to assert about.
 */
function readObservedBand(raw: Record<string, unknown>): AgeBand | undefined {
  const pre = raw.observedBand;
  if (pre && typeof pre === "object") return bandOrUndefined(normalizeGradeSpelling(pre as Record<string, unknown>));
  const num = (v: unknown): number | undefined => (typeof v === "number" && isFinite(v) ? v : undefined);
  const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
  const band: AgeBand = {};
  const minAge = num(raw.observedMinAge);
  const maxAge = num(raw.observedMaxAge);
  if (minAge != null) band.minAgeInYears = minAge;
  if (maxAge != null) band.maxAgeInYears = maxAge;
  const minGradeKey = str(raw.observedMinGradeKey);
  const minGradeValue = num(raw.observedMinGradeValue);
  if (minGradeKey != null && minGradeValue != null) {
    band.minAgeGradeKey = minGradeKey;
    band.minAgeGradeValue = minGradeValue;
  }
  const maxGradeKey = str(raw.observedMaxGradeKey);
  const maxGradeValue = num(raw.observedMaxGradeValue);
  if (maxGradeKey != null && maxGradeValue != null) {
    band.maxAgeGradeKey = maxGradeKey;
    band.maxAgeGradeValue = maxGradeValue;
  }
  return bandOrUndefined(band);
}

/**
 * KB-2658 — the band the offer will actually carry.
 *
 * Creation row → `preview.variationData`, which is the variation AFTER the pipeline applied (or
 * refused) the session-age override.
 *
 * Change row → `minAge`/`maxAge` from the `changedOffers` enrich. Those are computed from the
 * FRESHLY BUILT payload (`observed-to-offers.cjs:527` reads `vd.minAgeEffective ?? vd.minAgeInYears`
 * off the payload the call site at `:1417` just assembled), so this is the band the row PROPOSES,
 * not the one the offer carries today. That is the right value for a field named `effectiveBand` —
 * it means "what this write produces" on both row kinds — but note the consequence: on an
 * `ages`-class change pattern the chip shows the NEW band while reading as unverified, because the
 * source-stated band is not carried on a change row and there is nothing to compare it against.
 *
 * Both are somebody else's computation, read verbatim — this function decides nothing.
 */
function readEffectiveBand(raw: Record<string, unknown>, preview: Record<string, unknown>, e: Record<string, unknown>): AgeBand | undefined {
  const pre = raw.effectiveBand;
  if (pre && typeof pre === "object") return bandOrUndefined(pre as AgeBand);
  const num = (v: unknown): number | undefined => (typeof v === "number" && isFinite(v) ? v : undefined);
  const str = (v: unknown): string | undefined => (typeof v === "string" && v ? v : undefined);
  const vd = (preview.variationData && typeof preview.variationData === "object" ? preview.variationData : {}) as Record<string, unknown>;
  const band: AgeBand = {};
  const minAge = num(vd.minAgeInYears) ?? num(e.minAge);
  const maxAge = num(vd.maxAgeInYears) ?? num(e.maxAge);
  if (minAge != null) band.minAgeInYears = minAge;
  if (maxAge != null) band.maxAgeInYears = maxAge;
  // Grades are shown as the offer's primary band only when the pipeline said so — the same
  // `show*AsPrimary` gate the Flutter card reads. Without that gate a variation carrying an
  // incidental grade key would out-rank the age band the offer is actually created with.
  if (vd.showMinAgeGradeAsPrimary === true) {
    const key = str(vd.minAgeGradeKey);
    const value = num(vd.minAgeGradeValue);
    if (key != null && value != null) {
      band.minAgeGradeKey = key;
      band.minAgeGradeValue = value;
    }
  }
  if (vd.showMaxAgeGradeAsPrimary === true) {
    const key = str(vd.maxAgeGradeKey);
    const value = num(vd.maxAgeGradeValue);
    if (key != null && value != null) {
      band.maxAgeGradeKey = key;
      band.maxAgeGradeValue = value;
    }
  }
  return bandOrUndefined(band);
}

// ── Firestore query (the composite index shipped in KB-1867 serves this) ──────────────────────────

/** Default open-pattern page size. Caps reads/load at ~pageSize instead of the whole collection (KB-1893). */
export const INBOX_PAGE_SIZE = 50;

/** An opaque page cursor — the last Firestore doc snapshot of a page, passed to the next page's query. */
export type InboxCursor = QueryDocumentSnapshot;

export interface ReviewPatternPage {
  patterns: ReviewPattern[];
  /** Cursor for the next page (pass as `startAfter`); null when this was the last page. */
  cursor: InboxCursor | null;
  hasMore: boolean;
}

/**
 * Fetch ONE page of open review patterns (KB-1893 — cursor pagination so a load reads ~pageSize docs,
 * not the whole collection). Ordered by the KB-1867 composite index (severityRank asc, counts.items
 * desc). `orderPatternsForInbox` (stale-to-bottom) is applied WITHIN the page; the cursor is taken from
 * the RAW Firestore order so paging stays consistent across page boundaries.
 */
export async function fetchOpenReviewPatterns(opts: { pageSize?: number; startAfter?: InboxCursor | null; dbHandle?: Firestore } = {}): Promise<ReviewPatternPage> {
  const pageSize = opts.pageSize ?? INBOX_PAGE_SIZE;
  const dbHandle = opts.dbHandle ?? defaultDb;
  const base = [
    where("status", "==", "open"),
    orderBy("severityRank", "asc"),
    orderBy("counts.items", "desc"),
    ...(opts.startAfter ? [startAfter(opts.startAfter)] : []),
    limit(pageSize),
  ];
  const snap = await getDocs(query(collection(dbHandle, REVIEW_PATTERNS_COLLECTION), ...base));
  const rawDocs = snap.docs;
  const rows = rawDocs.map((d) => ({ id: d.id, ...(d.data() as CampDataReviewPatternDocV1) })) as ReviewPattern[];
  const hasMore = rawDocs.length === pageSize;
  return {
    patterns: orderPatternsForInbox(rows),
    cursor: hasMore ? rawDocs[rawDocs.length - 1] : null,
    hasMore,
  };
}

/**
 * KB-2538 AC7e — the cap on one org's open-pattern fetch. An org's open patterns are distinct
 * (field × shape) combinations, so tens is the realistic ceiling; this exists so a pathological org
 * cannot turn a filter click into an unbounded read. Exceeding it LOGS and caps rather than paging,
 * because paging an unordered equality query is not stable — see `fetchOpenReviewPatternsForCamp`
 * for why an `orderBy` is not available here.
 */
export const ORG_PATTERN_CAP = 200;

export interface CampPatternResult {
  patterns: ReviewPattern[];
  /** True when the org has MORE than ORG_PATTERN_CAP open patterns and this list was cut. */
  truncated: boolean;
}

// ── KB-2572 — resolved (non-open) patterns for ONE run ────────────────────────────────────────────

export interface ResolvedRunPatternResult {
  patterns: ReviewPattern[];
  /** True when this run has MORE than `ORG_PATTERN_CAP` patterns sharing its `latestRunId` and this list was cut. */
  truncated: boolean;
}

/**
 * KB-2572 — the terminal patterns (approved/rejected/suppressed) attributed to ONE run, so Run details
 * can say "you already acted on this" instead of falling straight to speculative empty-state copy.
 *
 * Two equality filters — `campSlug` + `latestRunId` — merge via single-field indexes exactly like
 * `fetchOpenReviewPatternsForCamp` above; no `orderBy`, no composite index, no change to that function.
 * Filtered to non-open client-side (the whole point is patterns the open query would never return).
 *
 * `code-review#2` (KB-2572) — reuses `ORG_PATTERN_CAP` as a defensive `limit()`. An earlier version of
 * this doc comment argued no limit was needed because the result is "structurally bounded by field class
 * × change shape" — that undercounted: `changeGroupKey`'s `groupKeyText` also encodes the LOCATION
 * (`patternFieldOf` reads the field from `parts[2]`, so `parts[0..1]` are location scope), so the real
 * bound is locations × field classes × change shapes — hundreds on a large multi-location camp, not a
 * small constant. And `derivePatternDocs.cjs`'s nightly "touch" stamps the SAME `latestRunId` on every
 * pattern that run's audit touched, so on a big run they'd all match at once. The cap (+1, mirroring the
 * sibling, to distinguish a full page from an overflowing one) keeps this query as index-free and bounded
 * as `fetchOpenReviewPatternsForCamp` above.
 *
 * Exact only when `runId` is the LATEST run that touched a matching pattern: `derivePatternDocs.cjs`'s
 * "touch" transition keeps advancing `latestRunId` on a TERMINAL pattern too (KB-TL-7 — never reopen,
 * but still track recurrence), so a pattern resolved on this run can point at a LATER run once the next
 * nightly refresh sees the same `changeGroupKey` again. Same limitation `fetchRunProposalPatterns`'s
 * `fallback-latest-run` tier already documents. Callers MUST NOT trust a resolved pattern's `verdict`/
 * `suppression` timestamp at face value for THIS reason — see `describeResolvedPattern`'s `runCreatedAtIso`
 * parameter, which catches the case where the verdict predates this run (code-review#1).
 *
 * `code-review round 2 #2` — filters to non-open BEFORE applying the `ORG_PATTERN_CAP` cut, not after.
 * The old ordering (cap the raw fetch, THEN filter) discarded whatever fell past position 200 — status
 * unknown — before ever checking whether it was exactly the resolved pattern this call exists to find.
 * Filtering first spends every fetched doc on the ones this query actually wants; a truncation therefore
 * only ever discards ALREADY-open docs, never a resolved one that happened to land at the tail of the
 * fetch. This does NOT make `truncated` moot: the underlying query still has no `orderBy` (deliberately,
 * to stay index-free), so on a camp whose matching set exceeds `ORG_PATTERN_CAP` + 1, Firestore's OWN
 * page selection — decided before any of this runs — can still be skewed toward open docs, missing
 * resolved ones that exist beyond what was fetched at all. `truncated` is the caller's signal for that
 * residual case; `fetchRunProposalPatterns` MUST NOT let a truncated, empty `patterns` render as "this
 * run was never resolved" (see `RunProposalsPanel`'s `run-proposals-resolved-unknown` branch).
 */
export async function fetchResolvedReviewPatternsForRun(campSlug: string, runId: string, dbHandle: Firestore = defaultDb): Promise<ResolvedRunPatternResult> {
  const snap = await getDocs(
    query(
      collection(dbHandle, REVIEW_PATTERNS_COLLECTION),
      where("campSlug", "==", campSlug),
      where("latestRunId", "==", runId),
      limit(ORG_PATTERN_CAP + 1),
    ),
  );
  const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as CampDataReviewPatternDocV1) })) as ReviewPattern[];
  const truncated = rows.length > ORG_PATTERN_CAP;
  if (truncated) {
    console.warn(`[camp-data run-proposals] ${campSlug}/${runId} has more than ${ORG_PATTERN_CAP} patterns sharing this latestRunId — showing the first ${ORG_PATTERN_CAP}.`);
  }
  const nonOpen = rows.filter((p) => p.status !== "open");
  const capped = nonOpen.length > ORG_PATTERN_CAP ? nonOpen.slice(0, ORG_PATTERN_CAP) : nonOpen;
  return { patterns: capped, truncated };
}

export type ResolvedPatternTone = "approved" | "rejected" | "suppressed" | "resolved";

export interface ResolvedPatternLine {
  tone: ResolvedPatternTone;
  /** "2 new sessions — approved Aug 22, 2:03 PM EDT" (or without the "— …" clause when no timestamp is known). */
  text: string;
}

/** The verdict timestamp as a local-ET wall-clock label, mirroring `formatApplyAtLabel`'s style. */
export function formatVerdictTimestamp(atIso: string): string {
  const at = Date.parse(atIso);
  if (!isFinite(at)) return "at an unknown time";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(at));
}

/**
 * KB-2572 code-review#3 — the ONE noun-phrase deriver for "what does this pattern represent", shared
 * between the pre-resolution action label (`RunProposalsPanel`'s row text) and the post-resolution
 * verdict line (`describeResolvedPattern` below) — so the SAME pattern reads with the SAME words whether
 * it's still open or already resolved. Before this they diverged: the open row said "3 price updates"
 * and the resolved line said "3 sessions" — which reads as 3 NEW sessions created, when what was
 * actually approved was a price edit on 3 EXISTING sessions. On a ticket about legibility, that mismatch
 * is itself a defect.
 */
export function describeProposalNounPhrase(p: Pick<ReviewPattern, "fieldClass" | "groupKeyText" | "counts">): string {
  const n = p.counts?.items ?? 0;
  if (p.fieldClass === CREATION_FIELD_CLASS) return `${n} new session${n === 1 ? "" : "s"}`;
  const field = patternFieldOf(p);
  // "session update(s)" — not "sessions update(s)": only the trailing noun pluralizes.
  return `${n} ${field ?? "session"} update${n === 1 ? "" : "s"}`;
}

/**
 * KB-2572 AC3 — one resolved pattern's verdict line: outcome, session count, and when. Pure (no
 * rendering), so it's directly unit-testable.
 *
 * The timestamp source differs by HOW the pattern was resolved: a human verdict (approved/rejected)
 * stamps `verdict.atIso`; the KB-2041 backfill's one-off suppression stamps `suppression.atIso` instead
 * (no human clicked approve/reject, so there is no `verdict` to read). Neither present → the line still
 * states the verdict and count, just without a "— approved …" clause rather than inventing a time.
 *
 * `code-review#1` (KB-2572, HIGH) — `runCreatedAtIso` is this run's own `createdAtIso` (the caller
 * already has it — `ReviewAndAccept`'s own `getDoc` on the run doc). Because a TERMINAL pattern's
 * `latestRunId` keeps advancing on every later run that re-touches the same `changeGroupKey`
 * (`fetchResolvedReviewPatternsForRun`'s own doc comment), a pattern resolved on an EARLIER run can end
 * up pointing at THIS run's id by the time this run's details page is viewed — rendering this run's
 * page with an earlier run's verdict AND an earlier run's timestamp, as though this run's own proposals
 * were "just" approved. If the verdict/suppression timestamp predates this run's own creation, it
 * cannot be THIS run's resolution — say so, rather than asserting a false "resolved" claim on the one
 * surface whose entire purpose is saying what actually happened. `runCreatedAtIso: null` (caller hasn't
 * loaded the run doc yet) degrades to the pre-fix behavior — trusting the timestamp — since there is
 * nothing to compare against; callers should have the real value by the time this branch renders.
 */
export function describeResolvedPattern(
  p: Pick<ReviewPattern, "status" | "counts" | "fieldClass" | "groupKeyText" | "verdict"> & { suppression?: { atIso?: string } },
  runCreatedAtIso: string | null,
): ResolvedPatternLine {
  const label = describeProposalNounPhrase(p);
  const tone: ResolvedPatternTone =
    p.status === "approved" ? "approved" : p.status === "rejected" ? "rejected" : p.status === "suppressed" ? "suppressed" : "resolved";
  const atIso = p.verdict?.atIso || p.suppression?.atIso || null;
  if (!atIso) return { tone, text: `${label} — ${tone}` };
  const when = formatVerdictTimestamp(atIso);
  const verdictMs = Date.parse(atIso);
  const runMs = runCreatedAtIso ? Date.parse(runCreatedAtIso) : NaN;
  const predatesThisRun = isFinite(verdictMs) && isFinite(runMs) && verdictMs < runMs;
  const text = predatesThisRun ? `${label} — ${tone} ${when} (on an earlier run)` : `${label} — ${tone} ${when}`;
  return { tone, text };
}

/**
 * KB-2538 AC7 — every OPEN pattern for one camp org, for the in-place Inbox filter.
 *
 * `campSlug` IS the org handle (camp-data slugs are always org slugs, and the pattern doc carries no
 * `orgId` — see `refreshableFrom`), so this equality pair is exactly "all open proposals for this
 * campOrganization".
 *
 * ⚠️ EQUALITY-ONLY, NO `orderBy` — deliberately (AC7e). Firestore serves an equality-only query by
 * merging the auto-created single-field indexes; adding an `orderBy` would demand a composite index,
 * and no diff can ship one: this collection's composite index is in NEITHER repo's source control.
 * pro has no `firestore.indexes.json` at all, and kb-ff's (the home for this project's index file)
 * has no `campDataReviewPatterns` entry — grep `severityRank` there and the KB-1867 index serving
 * `fetchOpenReviewPatterns` is absent, so it was created out of band. A new one is therefore a
 * manual, out-of-system step. The Inbox's own ordering is applied client-side by
 * `orderPatternsForInbox`, exactly as the unfiltered Inbox already does within a page.
 */
export async function fetchOpenReviewPatternsForCamp(campSlug: string, dbHandle: Firestore = defaultDb): Promise<CampPatternResult> {
  const snap = await getDocs(
    query(
      collection(dbHandle, REVIEW_PATTERNS_COLLECTION),
      where("campSlug", "==", campSlug),
      where("status", "==", "open"),
      // +1 so a full page is distinguishable from an overflowing one without a second query.
      limit(ORG_PATTERN_CAP + 1),
    ),
  );
  const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as CampDataReviewPatternDocV1) })) as ReviewPattern[];
  const truncated = rows.length > ORG_PATTERN_CAP;
  if (truncated) {
    console.warn(`[camp-data inbox] ${campSlug} has more than ${ORG_PATTERN_CAP} open patterns — showing the first ${ORG_PATTERN_CAP}.`);
  }
  return { patterns: orderPatternsForInbox(truncated ? rows.slice(0, ORG_PATTERN_CAP) : rows), truncated };
}

/**
 * KB-2914 round 3 finding 6 — name the fields that are ACTUALLY missing, not the pair.
 *
 * `isRenderablePattern` is `!!campSlug && !!changeGroupKey`, so either one alone triggers the
 * `unrenderable` state — but the copy said "it carries no camp and no change group", which for a doc
 * with a camp is a confident, specific, false statement about its own subject. That is the same shape
 * as "It belongs to undefined", the defect `describeOutsideListReason` was extracted to prevent,
 * recreated in the copy for the state added to fix a different one.
 */
export function describeUnrenderableFields(pattern: Pick<ReviewPattern, "campSlug" | "changeGroupKey">): string {
  const missing: string[] = [];
  if (!pattern.campSlug) missing.push("no camp");
  if (!pattern.changeGroupKey) missing.push("no change group");
  if (missing.length === 0) return "it is incomplete";
  return `it carries ${missing.join(" and ")}`;
}

export type PatternLookup =
  | { kind: "found"; pattern: ReviewPattern }
  | { kind: "absent" }
  | { kind: "unrenderable"; pattern: ReviewPattern }
  | { kind: "unknown"; reason: string };

/**
 * Resolve ONE pattern by doc id, independent of any list query.
 *
 * ⚠️ WHY THIS EXISTS, MEASURED: `fetchOpenReviewPatternsForCamp` caps at `ORG_PATTERN_CAP` (200) and
 * slices. `ymca-of-the-triangle` holds **283** open patterns, so a deep link to
 * `?selectedPatternId=fa32d1bb…` addressed a doc the camp query provably never returned — measured
 * read-only against prod, 201 rows with `truncated=true` and the subject absent. The deep link is a
 * contract a *list* query cannot honour, because list position is not something the link controls.
 *
 * **No index is required — this is `getDoc` on a document path, not a query**, so it is unaffected by
 * the missing-composite-index constraint documented on `fetchOpenReviewPatternsForCamp`. That
 * property is the whole reason this can still answer when the list query cannot (round 1 finding 4),
 * so the sentence carrying it lives on this function. (Round 2 finding 8: it had drifted onto
 * `describeOutsideListReason` below, where two docblocks stacked and the second one won.)
 *
 * ⚠️ THE RETURN TYPE IS THE POINT — do not collapse it. Each member is a state that must not print
 * another's sentence:
 *
 *   - `found`        — the read succeeded, the doc is here, and it is renderable.
 *   - `absent`       — the read SUCCEEDED and the doc genuinely does not exist.
 *   - `unrenderable` — the doc EXISTS but is a ghost: the audit draw's non-atomic merge-write can
 *                      resurrect a doc carrying only `auditRequested`, with no `campSlug` and no
 *                      `changeGroupKey`. Round 2 finding 4: this by-id read is the ONLY path that can
 *                      reach one — `partitionInboxSections` filters it out and the `status == "open"`
 *                      query cannot return it — and without this gate it rendered a card with zero
 *                      items whose "Approve all 0" / "Reject all 0" were ENABLED and would write a
 *                      verdict against a phantom. It is neither found nor absent, and saying either
 *                      would be false.
 *   - `unknown`      — the read FAILED. We know nothing. Never render this as "not found".
 *
 * This function therefore does NOT throw: swallowing the error at the call site is exactly how
 * `absent` and `unknown` collapse back together.
 */
export async function fetchReviewPatternById(patternId: string, dbHandle: Firestore = defaultDb): Promise<PatternLookup> {
  try {
    const snap = await getDoc(doc(dbHandle, REVIEW_PATTERNS_COLLECTION, patternId));
    if (!snap.exists()) return { kind: "absent" };
    const pattern = { id: snap.id, ...(snap.data() as CampDataReviewPatternDocV1) } as ReviewPattern;
    if (!isRenderablePattern(pattern)) return { kind: "unrenderable", pattern };
    return { kind: "found", pattern };
  } catch (e) {
    return { kind: "unknown", reason: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * KB-3598 AC2 — resolve an UNAMBIGUOUS prefix (≥8 hex chars) to the one pattern it names, or say why
 * it could not.
 *
 * ⚠️ NO INDEX REQUIRED, and this is checked, not assumed (per the brief's own instruction to confirm
 * before building). `documentId()` range filters (`>=`, `<`) query Firestore's automatic `__name__`
 * index, which every collection carries with no `firestore.indexes.json` entry — the same property
 * `fetchReviewPatternById`'s docblock cites for a plain `getDoc`, extended here to a RANGE rather than
 * an exact match. `firestore.rules:485-488` grants `allow read` (covering both `get` and `list`) to
 * `isAdminUser()` with no query-shape restriction, so this needs no rules change either.
 *
 * The upper bound `prefix + ""` is the standard Firestore prefix-range idiom — `` sorts
 * after every realistic character, so `[prefix, prefix+"")` is exactly "starts with prefix".
 *
 * Capped at `PREFIX_LOOKUP_CAP` for the same defensive reason as `ORG_PATTERN_CAP` — this is a bound
 * on how many rows a mistyped or very short prefix can pull back, not an expected ceiling: 8 hex chars
 * is 32 bits of keyspace, so two real docs sharing a prefix that short is already a coincidence: the
 * cap is not expected to bind in practice.
 */
const PREFIX_LOOKUP_CAP = 25;

export type PatternPrefixLookup =
  | { kind: "found"; pattern: ReviewPattern }
  | { kind: "unrenderable"; pattern: ReviewPattern }
  | { kind: "ambiguous"; matches: ReviewPattern[] }
  | { kind: "absent" }
  | { kind: "unknown"; reason: string };

export async function fetchReviewPatternsByIdPrefix(prefix: string, dbHandle: Firestore = defaultDb): Promise<PatternPrefixLookup> {
  try {
    const col = collection(dbHandle, REVIEW_PATTERNS_COLLECTION);
    const q = query(col, where(documentId(), ">=", prefix), where(documentId(), "<", prefix + ""), limit(PREFIX_LOOKUP_CAP));
    const snap = await getDocs(q);
    const docs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as CampDataReviewPatternDocV1) }) as ReviewPattern);
    if (docs.length === 0) return { kind: "absent" };
    // Round 1 (nit) — the single-match branch below already routes one ghost to `unrenderable` rather
    // than `found`, because a ghost card has nothing to show and nothing to judge. A prefix that
    // matches SEVERAL docs must apply the same screen before deciding what "ambiguous" means: an
    // unscreened ghost among `matches` renders a picker row with `m.campSlug`/`m.fieldClass` both
    // undefined (IsolatedPatternView's ambiguous-match row) — a row that leads nowhere useful, for a
    // choice the operator was never going to make anyway.
    const renderable = docs.filter(isRenderablePattern);
    if (renderable.length > 1) return { kind: "ambiguous", matches: renderable };
    const pattern = renderable[0] ?? docs[0];
    if (!isRenderablePattern(pattern)) return { kind: "unrenderable", pattern };
    return { kind: "found", pattern };
  } catch (e) {
    return { kind: "unknown", reason: e instanceof Error ? e.message : String(e) };
  }
}

// ── KB-2641 — calibration-set members + roster-based progress ─────────────────────────────────────

/** C1 (KB-2587) writes the set header here; the frozen roster is its `items` subcollection. */
export const CALIBRATION_SETS_COLLECTION = "campDataCalibrationSets";
/** One doc per member, keyed by `patternDocId`. THIS is the roster — see `fetchCalibrationRosterSize`. */
export const CALIBRATION_SET_ITEMS_SUBCOLLECTION = "items";
/**
 * C1's selection stamp on each member pattern doc — the only field it writes to
 * `campDataReviewPatterns`.
 *
 * `derivePatternDocs.cjs` declares this literal's one home for the SCRIPTS side, and the builder
 * imports it there rather than re-declaring. This is a second declaration on purpose: the client
 * bundle does not depend on `scripts/` or `functions/` (the same reason `VerdictChannel` is kept
 * loose here rather than imported from the server's enum). The suite closes the rename hole the only
 * way a second declaration can be closed — by READING `derivePatternDocs.cjs` and asserting the two
 * literals still agree, so a scripts-side rename fails here instead of quietly emptying the filter.
 */
export const CALIBRATION_STAMP_FIELD = "calibrationSetId";

/**
 * Cap on the member fetch. A set is the whole eligible population of one field class (70 today), so
 * this is a defensive bound in the spirit of `ORG_PATTERN_CAP`, not an expected ceiling. Exceeding it
 * is surfaced, never swallowed — a truncated member list under a whole-roster denominator would make
 * the progress line describe a set the operator cannot actually reach.
 */
export const CALIBRATION_MEMBER_CAP = 500;

/** One row of the "which set?" picker (AC-3). `memberCount` is the roster count, not a header claim. */
export interface CalibrationSetSummary {
  setId: string;
  builtAtIso: string;
  fieldClass: string;
  memberCount: number;
}

/**
 * AC-2 — progress against the FROZEN ROSTER, never against what the filtered query returned.
 *
 * `rosterSize` is fixed at build time. `judged` counts members that have gone terminal, which is
 * exactly the set that has LEFT the `status == "open"` query — so a naive "count what I rendered"
 * would run 70 → 69 → 68 and could not tell "63 left" from "the filter broke" from "I'm done".
 *
 * `unaccounted` is roster members the member query did not return at all (a pattern doc deleted, or
 * its stamp cleared out of band). It is normally 0. It is carried rather than folded into `remaining`
 * because "not yet judged" and "no longer reachable" are different facts, and a surface that silently
 * merges them re-creates the ambiguity this whole denominator exists to remove.
 *
 * `complete` is false when the member fetch hit `CALIBRATION_MEMBER_CAP`. It exists because
 * `unaccounted` is ONLY meaningful over a complete fetch — see `summarizeCalibrationProgress`.
 */
export interface CalibrationSetProgress {
  rosterSize: number;
  judged: number;
  remaining: number;
  unaccounted: number;
  /** False when the member fetch was capped, so `judged` is a floor rather than an exact count. */
  complete: boolean;
}

export interface CalibrationMemberResult {
  /** The set's still-open members, in Inbox order — what the filtered list renders. */
  patterns: ReviewPattern[];
  progress: CalibrationSetProgress;
  /** True when the set has more members than CALIBRATION_MEMBER_CAP and the fetch was cut. */
  truncated: boolean;
}

/**
 * Pure (unit-tested) — derive the progress line from the roster size and the member docs the query
 * returned. Split out from the fetch so the arithmetic can be pinned without Firestore.
 */
export function summarizeCalibrationProgress(
  rosterSize: number,
  members: ReviewPattern[],
  { complete = true }: { complete?: boolean } = {},
): CalibrationSetProgress {
  const judged = members.filter((p) => isTerminalPatternStatus(p.status)).length;
  const open = members.length - judged;
  return {
    rosterSize,
    judged,
    remaining: Math.max(0, rosterSize - judged),
    // Only derivable from a COMPLETE member fetch. Over a capped one the shortfall between the roster
    // and what came back IS the cap — calling it "no longer reachable" would be a false claim standing
    // directly beside the banner that correctly calls it a cap, on the one surface whose whole job is
    // being honest about what is left. Suppressed rather than guessed.
    unaccounted: complete ? Math.max(0, rosterSize - judged - open) : 0,
    complete,
  };
}

/**
 * AC-2 — the progress line, as prose. `null` when there is no roster to describe.
 *
 * A zero roster does NOT read "0 of 0 judged": that is the same sentence a completed set would show
 * on a surface whose entire job is telling a sitting how far it has left to go. C1 refuses to build an
 * empty set at all, so a zero here means the roster is unreadable (a partial write, wrong id, denied
 * read) — which is a different thing to say, and this says it.
 */
export function describeCalibrationProgress(progress: CalibrationSetProgress): string | null {
  const { rosterSize, judged, remaining, unaccounted } = progress;
  if (rosterSize <= 0) return "roster unavailable — progress cannot be counted for this set";
  const base = `${judged.toLocaleString()} of ${rosterSize.toLocaleString()} judged · ${remaining.toLocaleString()} remaining`;
  return unaccounted > 0 ? `${base} (${unaccounted.toLocaleString()} no longer reachable)` : base;
}

/**
 * The roster size for one set: a `count()` over its `items` subcollection.
 *
 * Deliberately NOT the set header's `population.eligible`. That number is a claim ABOUT the roster
 * written alongside it; the subcollection IS the roster, and counting it cannot drift from the thing
 * the denominator is supposed to describe. Using one source everywhere also means the picker's member
 * count and the progress line's `M` can never disagree on screen.
 *
 * Billed at ~1 read per 1000 index entries, so this is one read for any realistic set.
 */
export async function fetchCalibrationRosterSize(setId: string, dbHandle: Firestore = defaultDb): Promise<number> {
  const res = await getCountFromServer(
    collection(dbHandle, CALIBRATION_SETS_COLLECTION, setId, CALIBRATION_SET_ITEMS_SUBCOLLECTION),
  );
  return Number(res.data().count ?? 0);
}

/**
 * AC-3 — the sets that exist, discovered rather than hardcoded. A hardcoded set id would make the
 * second calibration exactly as annoying as the first, and calibration recurs by design (C1 AC-6:
 * every prompt edit mints a new rater and re-measures against the same items).
 *
 * Newest first, so the set an operator most likely means is the one they see first.
 */
export async function fetchCalibrationSets(dbHandle: Firestore = defaultDb): Promise<CalibrationSetSummary[]> {
  const snap = await getDocs(collection(dbHandle, CALIBRATION_SETS_COLLECTION));
  const rows = await Promise.all(
    snap.docs.map(async (d) => {
      const data = (d.data() || {}) as { builtAtIso?: string; population?: { fieldClass?: string } };
      return {
        setId: d.id,
        builtAtIso: String(data.builtAtIso || ""),
        fieldClass: String(data.population?.fieldClass || ""),
        memberCount: await fetchCalibrationRosterSize(d.id, dbHandle),
      };
    }),
  );
  return rows.sort((a, b) => (a.builtAtIso < b.builtAtIso ? 1 : a.builtAtIso > b.builtAtIso ? -1 : 0));
}

/**
 * AC-1/AC-2 — one calibration set's members, plus the roster-based progress line.
 *
 * ⚠️ EQUALITY-ONLY, NO `orderBy`, for the same reason `fetchOpenReviewPatternsForCamp` is (AC7e):
 * Firestore serves an equality-only query by merging auto-created single-field indexes, while an
 * `orderBy` would demand a composite index — and no diff can ship one, because this collection's
 * composite index is in neither repo's source control. Inbox ordering is applied client-side by
 * `orderPatternsForInbox`, exactly as every other Inbox query already does.
 *
 * ⚠️ The query does NOT filter on `status`. It must return the set's TERMINAL members too — they are
 * the numerator. Adding `where("status","==","open")` here is the exact defect AC-2 exists to catch:
 * it would still render correctly and still be equality-only, and the progress line would silently
 * become a count of what happens to be on screen.
 *
 * The stamp survives judging: the nightly deriver no-ops entirely on a stamped doc (`mergePatternDoc`
 * returns `frozen` before the terminal/open split), so a judged member keeps both its verdict and its
 * `calibrationSetId`.
 */
export async function fetchCalibrationSetMembers(setId: string, dbHandle: Firestore = defaultDb): Promise<CalibrationMemberResult> {
  const [snap, rosterSize] = await Promise.all([
    getDocs(
      query(
        collection(dbHandle, REVIEW_PATTERNS_COLLECTION),
        where(CALIBRATION_STAMP_FIELD, "==", setId),
        // +1 so a full page is distinguishable from an overflowing one without a second query.
        limit(CALIBRATION_MEMBER_CAP + 1),
      ),
    ),
    fetchCalibrationRosterSize(setId, dbHandle),
  ]);
  const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() as CampDataReviewPatternDocV1) })) as ReviewPattern[];
  const truncated = all.length > CALIBRATION_MEMBER_CAP;
  if (truncated) {
    console.warn(`[camp-data inbox] calibration set ${setId} has more than ${CALIBRATION_MEMBER_CAP} members — showing the first ${CALIBRATION_MEMBER_CAP}.`);
  }
  const members = truncated ? all.slice(0, CALIBRATION_MEMBER_CAP) : all;
  return {
    patterns: orderPatternsForInbox(members.filter((p) => !isTerminalPatternStatus(p.status))),
    progress: summarizeCalibrationProgress(rosterSize, members, { complete: !truncated }),
    truncated,
  };
}

/** KB-1875 — cap on the audit-sample fetch. At a 2–5% rate a day's draw is far below this. */
export const AUDIT_SAMPLE_CAP = 200;

/**
 * KB-1875 (AC5) — the ADR's audit floor, mirrored client-side for the scorecard's standing warning.
 *
 * Re-declared rather than imported because `functions/` is a separate package the Vite bundle does not
 * reach. A test pins it against `auditSampleCore`'s value so the two cannot drift — the same
 * cross-package constant-parity guard this repo already uses for the queue/reaper constants.
 */
export const AUDIT_SAMPLE_RATE_FLOOR = 0.02;

/**
 * Is the configured rate below the floor?
 *
 * Warning only — never a clamp. The ADR is explicit that the rate "can be raised freely, lowered below
 * 2% only by editing the config with a documented reason": the rule is cultural and VISIBLE. Silently
 * overruling a deliberate operator choice would hide the very fact the warning exists to surface.
 */
export function isBelowAuditFloor(rate: number | null | undefined): boolean {
  return typeof rate === "number" && Number.isFinite(rate) && rate < AUDIT_SAMPLE_RATE_FLOOR;
}

/**
 * KB-1875 — today's `dateIso` in ET, matching the key the draw seeds and stamps with.
 *
 * ET rather than the viewer's locale on purpose: the draw runs on an ET schedule, so a browser in a
 * different zone must not ask for a day the draw never produced and render an empty audit section that
 * looks like "nothing was drawn today".
 */
export function auditSampleDateIsoToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/**
 * KB-1875 — how far back the audit fetch reaches.
 *
 * The draw is keyed by day, but the two things the fetch surfaces are NOT day-scoped facts:
 *
 *   - A DISAGREEMENT is the highest-signal item in the system, and the amendment makes a confirmed
 *     HIGH-severity one halt publishing. Keyed to a single `dateIso` it would be pinned to the top of
 *     "Needs your eyes" until ET midnight and then silently vanish from every Inbox surface — the
 *     ledger row survives and feeds the statistic, but the operator-facing alarm switches itself off
 *     overnight. "Recorded but invisible" is exactly the failure the pin exists to prevent.
 *   - An UNANSWERED audit is an audit still owed. Dropping it at midnight makes a missed day
 *     indistinguishable from an honoured one, which is the same silence the floor exists to break.
 *
 * A window rather than unbounded because the section is a work queue, not an archive; the durable
 * record is the ledger.
 */
export const AUDIT_LOOKBACK_DAYS = 14;

/** KB-1875 — the earliest `dateIso` the audit fetch reaches, in the same ET day-key space as the draw. */
export function auditSampleSinceDateIso(now: Date = new Date(), days: number = AUDIT_LOOKBACK_DAYS): string {
  return auditSampleDateIsoToday(new Date(now.getTime() - Math.max(0, days) * 86_400_000));
}

/**
 * KB-1875 — the drawn patterns in the lookback window, whatever their status.
 *
 * A SEPARATE query from `fetchOpenReviewPatterns` because the audit population is not the open one: in
 * DELEGATE the agent has already decided and applied these, so their status is terminal and the
 * `status == "open"` inbox query returns none of them. Auditing only the open patterns would measure
 * exactly the items the operator was going to see anyway — an audit of nothing.
 *
 * ⚠️ ONE FIELD ONLY — this collection's composite indexes are in NEITHER repo's source control, so a
 * query needing one cannot ship in a diff (`fetchOpenReviewPatternsForCamp` above hit the same wall,
 * and KB-2583 hit it again on the claim path). The range and the `orderBy` are on the SAME field, which
 * every automatic single-field index — `(field ASC, __name__ ASC)` — already serves. Do not add a
 * second field to either clause without shipping a paired kb-ff index PR.
 */
export async function fetchAuditSamplePatterns(sinceDateIso: string, dbHandle: Firestore = defaultDb): Promise<ReviewPattern[]> {
  if (!sinceDateIso) return [];
  const snap = await getDocs(
    query(
      collection(dbHandle, REVIEW_PATTERNS_COLLECTION),
      where("auditRequested.dateIso", ">=", sinceDateIso),
      orderBy("auditRequested.dateIso"),
      limit(AUDIT_SAMPLE_CAP + 1),
    ),
  );
  const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as CampDataReviewPatternDocV1) })) as ReviewPattern[];
  if (rows.length > AUDIT_SAMPLE_CAP) {
    // Loud rather than silent: a truncated audit sample understates the floor, and "fewer cards" is
    // indistinguishable from "a smaller draw" to the operator looking at the section.
    console.warn(`[camp-data inbox] audit sample since ${sinceDateIso} exceeds ${AUDIT_SAMPLE_CAP} — showing the first ${AUDIT_SAMPLE_CAP}.`);
    return rows.slice(0, AUDIT_SAMPLE_CAP);
  }
  return rows;
}

/** KB-1875 — the day's draw record, written by `campDataAuditSampleDraw`. */
export interface AuditSampleRecord {
  dateIso: string;
  rate: number | null;
  candidateCount: number | null;
  selectedCount: number | null;
  usedDefaultRate: boolean;
}

export const AUDIT_SAMPLES_COLLECTION = "campDataAuditSamples";

/**
 * KB-1875 (AC5) — read the day's draw record so the Inbox can state the rate and warn below the floor.
 *
 * Returns null rather than throwing: the record is absent on any day the draw has not yet run, which is
 * ordinary (the job is scheduled for 06:45 ET) and must not read as a breach.
 */
export async function fetchAuditSampleRecord(dateIso: string, dbHandle: Firestore = defaultDb): Promise<AuditSampleRecord | null> {
  if (!dateIso) return null;
  const snap = await getDoc(doc(dbHandle, AUDIT_SAMPLES_COLLECTION, dateIso));
  if (!snap.exists()) return null;
  const d = snap.data() as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  return {
    dateIso,
    rate: num(d.rate),
    candidateCount: num(d.candidateCount),
    selectedCount: num(d.selectedCount),
    usedDefaultRate: d.usedDefaultRate === true,
  };
}

export interface ReviewInboxTotals {
  /** EVERY open pattern — the shelf included. The denominator, not the action count. */
  patternCount: number;
  itemCount: number;
  /**
   * KB-3036 — the server count of `status=="open" AND expiry.allEnded==true`: the shelf's size.
   *
   * ⛔ `null` means the count could NOT be read, and it must NEVER be rendered as 0. A shelf reported
   * as empty because its query failed is indistinguishable from an inbox with no expired work, and it
   * would silently overstate the action queue by however many patterns it could not see (§6a-bis).
   * `describeInboxHeaderCounts` is the one place allowed to turn this into a sentence.
   */
  expiredCount: number | null;
  /** Why `expiredCount` is null. Null when the count succeeded. */
  expiredCountError: string | null;
}

/**
 * The Inbox header totals via aggregation (KB-1893) — NOT a full read. `count()` needs no composite
 * index (single-field `status`); `sum("counts.items")` needs a `(status, counts.items)` index and is
 * BEST-EFFORT so the header degrades to "N patterns" (itemCount 0) if that index is still building.
 * Billed at ~1 read per 1000 index entries, so the header stays accurate without reading every doc.
 *
 * KB-3036 adds the shelf count as a THIRD aggregate, on the same best-effort contract as the sum but
 * with a louder failure: an unreadable item total degrades a decoration, an unreadable shelf count
 * would corrupt the action count.
 *
 * ⚠️ **Verified by execution against prod, 2026-09-03T03:41Z (Admin SDK, read-only).** The two
 * equality clauses need NO composite index — Firestore merges single-field indexes for equality-only
 * queries — and the measurement carries its own positive control, because a shape returning 0 over an
 * empty population proves nothing about whether it is servable:
 *
 *   count(status=="open")                              → 1935  (449ms, no index error)
 *   count(status=="open" AND severity=="HIGH")         → 1111  (100ms, no index error)   ← the control
 *   count(status=="open" AND expiry.allEnded==true)    →    0  (158ms, no index error)
 *
 * The 0 is a statement about the ROLLOUT, not about the docs: at that moment not one of the 2,358
 * documents in the collection carried an `expiry` block at all, because the KB-3034 writer had not yet
 * reached them. The control is what makes "no index error" mean something here.
 *
 * ⛔ NEVER `where("expiry.allEnded","!=",true)` for the live side, and there are now two independent
 * reasons. The semantic one: Firestore `!=` does not match documents where the field is ABSENT, so
 * every unstamped — i.e. every live — doc would vanish from the count, which today is all 1,935 of
 * them. The mechanical one, measured in the same pass: that query does not execute at all without a
 * composite index (`FAILED_PRECONDITION`). Total-minus-shelf is the correct form; it is also the only
 * one that is free.
 *
 * ⚠️ THE SERVER COUNT CANNOT EXPRESS `!calibrationSetId`, so it counts a frozen calibration member
 * that carries an expired stamp, while `isShelvedAtReadTime` — and therefore the rendered shelf — does
 * not. **If that population is ever non-zero the header over-counts the shelf by exactly that
 * number**, and under-counts "to act on" by the same. Measured 2026-09-03: 0, and the honest reading
 * of that 0 is narrow. `count(open AND allEnded==true AND calibrationSetId != null)` is not servable
 * without a composite index, so it was read the equivalent way — 68 members across 2 sets, 13 of them
 * open, **0 carrying an `expiry` block** — and 0 was the answer for every doc in the collection, so
 * this measurement is currently indistinguishable from "the writer has not run yet". What is
 * structural rather than circumstantial is the writer: `mergePatternDoc` returns `{op:"frozen"}` on
 * any doc carrying `calibrationSetId` BEFORE the create/refresh branches that carry `expiry`
 * (`derivePatternDocs.cjs`, the `CALIBRATION_STAMP_FIELD` guard), so it cannot stamp a member. The
 * reachable path is the other order — a doc stamped `expiry` on a nightly run and frozen into a set
 * afterwards — and nothing prevents that. KB-3035's backfill is said to skip members; that file does
 * not exist on this base, so it is an inherited claim here, not one this ticket executed.
 */
export async function fetchReviewInboxTotals(dbHandle: Firestore = defaultDb): Promise<ReviewInboxTotals> {
  const q = query(collection(dbHandle, REVIEW_PATTERNS_COLLECTION), where("status", "==", "open"));
  const countRes = await getCountFromServer(q);
  const patternCount = Number(countRes.data().count ?? 0);
  let itemCount = 0;
  try {
    const aggRes = await getAggregateFromServer(q, { itemCount: sum("counts.items") });
    itemCount = Number(aggRes.data().itemCount ?? 0);
  } catch {
    // the (status, counts.items) sum index is still building — itemCount stays 0, header shows patterns only
  }
  let expiredCount: number | null = null;
  let expiredCountError: string | null = null;
  try {
    const shelfRes = await getCountFromServer(
      query(collection(dbHandle, REVIEW_PATTERNS_COLLECTION), where("status", "==", "open"), where("expiry.allEnded", "==", true)),
    );
    expiredCount = Number(shelfRes.data().count ?? 0);
  } catch (e) {
    // Left NULL on purpose — see the field's docblock. The header says so rather than showing 0.
    expiredCountError = (e as { message?: string })?.message || "the expired-pattern count query failed";
  }
  return { patternCount, itemCount, expiredCount, expiredCountError };
}

/**
 * KB-4785 — the cap on `fetchOpenUnstampedPatterns`. The population is every open card the shelf
 * aggregate did NOT count, which is To act on plus the read-time shelf (94 on 2026-10-21). Past the cap
 * the read is reported as truncated and the caller falls back to the loaded-page count, never to a guess.
 */
export const UNSTAMPED_OPEN_CAP = 500;

export interface UnstampedOpenPatterns {
  patterns: ReviewPattern[];
  /** True when more than `UNSTAMPED_OPEN_CAP` matched and this list was cut. */
  truncated: boolean;
}

/**
 * KB-4785 — every open card stamped `expiry.allEnded == false`: the cards `fetchReviewInboxTotals`'
 * shelf aggregate did not count, read whole so the read-time shelf (`isShelvedAtReadTime`, both arms)
 * can judge them without the operator paging down to them.
 *
 * Why it exists: the KB-4311 stale arm needs each camp's newest successful run, which no aggregate can
 * join, so before this the tab counted a stale card only once its page loaded. Stale cards sink to the
 * bottom of the order, so on 2026-10-21 "To act on (88)" stood over a full-predicate count of 21.
 *
 * Equality-only on the same two fields as the shelf aggregate, so it needs no new index. A doc with no
 * `allEnded` boolean at all is not returned; those stay on the loaded-page path, as before.
 */
export async function fetchOpenUnstampedPatterns(dbHandle: Firestore = defaultDb): Promise<UnstampedOpenPatterns> {
  const snap = await getDocs(
    query(
      collection(dbHandle, REVIEW_PATTERNS_COLLECTION),
      where("status", "==", "open"),
      where("expiry.allEnded", "==", false),
      limit(UNSTAMPED_OPEN_CAP + 1),
    ),
  );
  const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as CampDataReviewPatternDocV1) })) as ReviewPattern[];
  const truncated = rows.length > UNSTAMPED_OPEN_CAP;
  return { patterns: truncated ? rows.slice(0, UNSTAMPED_OPEN_CAP) : rows, truncated };
}

/**
 * KB-4785 — the read-time shelf over the WHOLE unstamped population, not just the loaded page: the ids
 * of open cards the shelf aggregate did not count but `splitShelvedPatterns` shelves today. The same
 * partition the dashboard applies to a loaded page (veto window and audit draw keep precedence), so a
 * card is shelved here exactly when it would be once its page loaded.
 */
export function shelvedUncountedIds(
  patterns: ReviewPattern[],
  todayEtDay: string | null,
  campRuns?: ReadonlyMap<string, ShelfCampRun>,
): Set<string> {
  const { needsReview } = partitionInboxSections(patterns.filter((p) => p.status === "open"));
  const { shelved } = splitShelvedPatterns(needsReview, todayEtDay, campRuns);
  return new Set(shelved.filter((p) => !isCountedAsExpiredByServer(p)).map((p) => p.id));
}

/** KB-3036 — the header's two numbers, already turned into the sentences the header renders. */
export interface InboxHeaderCounts {
  /** The action-queue phrase. Claims "to act on" ONLY when the subtraction was actually possible. */
  primaryText: string;
  /** The shelf phrase — a count, or a statement that the count could not be read. Never blank. */
  expiredText: string;
  /** True when the shelf size is UNKNOWN. The caller must not render 0, and must not imply a total. */
  expiredUnavailable: boolean;
  /** The action-queue size, or null when it could not be derived. Exposed for pins, not for display. */
  toActOn: number | null;
}

/**
 * KB-3036 — "N to act on · M expired", derived from the SERVER aggregates and nothing else.
 *
 * ⛔ Neither number may be computed from the loaded page. `INBOX_PAGE_SIZE` is 50, and the expired
 * population is not evenly spread through the order: the 2026-09-02 census found 535 expired patterns
 * carrying no `staleAsOfRunId`, so `orderPatternsForInbox` does not sink them and they sit among the
 * freshest work rather than at the end. A page-derived split would therefore report a shelf of at
 * most 50 over a shelf of hundreds, and would move every time the operator pressed "Load more".
 *
 * §6a-bis — the three failure paths, and what each one RENDERS:
 *   - totals unread          → "pattern count unavailable" (never the page length dressed as a total)
 *   - shelf count unread     → "N open · expired count unavailable". NOT "N to act on": with the
 *                              subtraction impossible, calling the whole open set the action queue
 *                              asserts a shelf of zero, which is the claim we just failed to check.
 *   - both read              → the real sentence.
 *
 * `Math.max(0, …)` guards the one benign inconsistency: `patternCount` and `expiredCount` are two
 * separate server round-trips, so a verdict landing between them can make the shelf momentarily
 * larger than the total. Clamping renders "0 to act on", which is true; a negative would not be.
 */
export function describeInboxHeaderCounts(totals: ReviewInboxTotals | null | undefined): InboxHeaderCounts {
  if (!totals) {
    return { primaryText: "pattern count unavailable", expiredText: "expired count unavailable", expiredUnavailable: true, toActOn: null };
  }
  if (totals.expiredCount == null) {
    return {
      primaryText: `${totals.patternCount.toLocaleString()} open`,
      expiredText: "expired count unavailable",
      expiredUnavailable: true,
      toActOn: null,
    };
  }
  const toActOn = Math.max(0, totals.patternCount - totals.expiredCount);
  return {
    primaryText: `${toActOn.toLocaleString()} to act on`,
    expiredText: `${totals.expiredCount.toLocaleString()} expired`,
    expiredUnavailable: false,
    toActOn,
  };
}

// ── verdict callable client ───────────────────────────────────────────────────────────────────────

export async function recordPatternVerdict(payload: RecordVerdictPayload): Promise<RecordVerdictResult> {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const fn = httpsCallable<RecordVerdictPayload, RecordVerdictResult>(functions, RECORD_VERDICT_CALLABLE);
  const res = await fn(payload);
  return res.data;
}

// ── KB-2656 — reporting an error of OMISSION ──────────────────────────────────────────────────────

/**
 * The card is good at errors of COMMISSION — every question it asks is about content that WAS proposed,
 * and Reject is how a wrong proposal is caught. This is the other direction: sessions that exist on the
 * vendor's site and were never proposed at all.
 *
 * The report is INDEPENDENT of the verdict, deliberately: it is its own callable, and nothing here
 * touches `recordPatternVerdict`. Flagging ten missing sessions says nothing about whether the fifty-eight
 * proposed ones were right, and the operator must be able to say both.
 */
export const REPORT_OMISSION_CALLABLE = "reportCampDataOmission";

/**
 * Bound on the omission note.
 *
 * The ONE home is `scripts/camp-data/_shared/trustLib.cjs` (`OMISSION_NOTE_MAX`), which is what the
 * server enforces; this is the browser-side copy, for the same reason `FIELD_ISSUE_FIELD_CLASSES`
 * above has one — the frontend bundle cannot require a CommonJS script from outside src/.
 * `campDataReviewInbox.test.ts` reads the canonical file and asserts the two are identical, so a
 * divergence fails a test rather than shipping a textarea that accepts prose the server refuses.
 */
export const OMISSION_NOTE_MAX = 2000;

export interface ReportOmissionPayload {
  patternDocId: string;
  note: string;
  evidenceUrl?: string | null;
  /**
   * The run this card is DISPLAYING. The server files the report against the run the operator actually
   * looked at, and refuses when the pattern has advanced underneath the loaded card — which it does:
   * the deriver's refresh branch advances `latestRunId` and rewrites the judgeable content, and this
   * card already renders `showing run <id>` and detects that a newer run exists.
   */
  expectedRunId?: string | null;
}

export interface ReportOmissionResult {
  fieldIssueId: string;
  campSlug: string;
  vendorType: string;
  runId: string;
  /** `writer:<vendor>:creation` — the scope charged. Null only when the camp carries no vendorType. */
  fieldScopeKey: string | null;
  /**
   * Is the vendor's promotion ACTUALLY frozen now? **Not derivable from `fieldScopeKey`** — that stays
   * non-null on a resolved row, so gating the card's freeze copy on it asserted a freeze that a
   * duplicate report deliberately does not apply. Always render from this.
   */
  frozen: boolean;
  /** An omission was already on record for this (camp, run) and this observation joined it. */
  appendedToExisting: boolean;
  /** How many observations the row now carries — 1 on a first report. */
  observationCount: number;
  reopened: boolean;
  /** This exact note+URL was already recorded — a retry, not a new observation. */
  duplicate: boolean;
}

export async function reportCampDataOmission(payload: ReportOmissionPayload): Promise<ReportOmissionResult> {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const fn = httpsCallable<ReportOmissionPayload, ReportOmissionResult>(functions, REPORT_OMISSION_CALLABLE);
  const res = await fn(payload);
  return res.data;
}

/**
 * Can this omission report be submitted?
 *
 * Mirrors `canSubmitVerdict`'s rule for a Q2 defect, and for the same reason the server states: a report
 * with no prose freezes a vendor's creation promotion while telling whoever has to unfreeze it nothing
 * about what to look for. The browser check is a courtesy that keeps the button honest — the server is
 * what enforces it.
 */
export function canSubmitOmission(note: string): boolean {
  const trimmed = note.trim();
  return trimmed.length > 0 && trimmed.length <= OMISSION_NOTE_MAX;
}

/**
 * Is this a URL the server will accept? http(s) only — the value is stored and rendered back to an
 * admin, so a `javascript:`/`data:` value would be a stored-XSS carrier reaching a trusted surface.
 *
 * An EMPTY string is valid because the URL is optional. Callers must not conflate "no URL given" with
 * "URL rejected": they render differently, and treating the first as the second would block a legitimate
 * report from someone who has the observation but not a link.
 */
export function isValidOmissionEvidenceUrl(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return true;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

// ── KB-2655 — every proposed row for one pattern (the full-N list) ────────────────────────────────

/** The admin-gated callable that returns ALL rows for a pattern; see `getPatternItemsCore` for why the
 * artifact path is resolved server-side from the pattern doc rather than named by this client. */
export const PATTERN_ITEMS_CALLABLE = "getCampDataPatternItems";

export interface PatternItemsPage {
  items: InboxItem[];
  /** Total rows for this pattern — the N the card's "×N" claims. */
  total: number;
  /** Next `offset` to request, or null when the last page has been read. */
  nextOffset: number | null;
  /** Bucket-relative path the rows were read from. Never a signed URL — it is pasted into tickets. */
  artifactPath: string;
}

/**
 * The audit-artifact path for a pattern, derived from its own doc.
 *
 * The callable returns the path it actually read and that value wins; this exists for the rows rendered
 * BEFORE any read — the card's sample rows, whose `metadata` copy must still cite where the evidence
 * lives, which is its most valuable field.
 *
 * ⚠️ This composes the same string as `runArtifactRelPath(campSlug, runId, AUDIT_ARTIFACT)` rather than
 * calling it, and that is a deliberate, guarded exception to one-home. Importing it here would drag
 * `runArtifacts` — and through it `campDataArtifactService` and firebase — into every consumer of this
 * module, and the nine test files that stub `runArtifacts` with a bare `{ fetchJsonArtifact }` would all
 * start throwing on a function their stub does not provide. `campDataReviewInbox.test.ts` pins the two
 * spellings against each other, so a change to either fails the suite rather than drifting silently.
 */
export function patternArtifactPath(pattern: Pick<ReviewPattern, "campSlug" | "latestRunId">): string | null {
  if (!pattern.campSlug || !pattern.latestRunId) return null;
  return `_ai_parse_audit/${pattern.campSlug}/runs/${pattern.latestRunId}/${AUDIT_ARTIFACT}`;
}

export const RUNS_COLLECTION = "campDataRuns";

/**
 * Per-camp provenance read state, keyed by camp slug. The Inbox renders one card per pattern and
 * several patterns commonly share a camp, so without this each card fires its own `campDataRuns`
 * query on one page load. Every call for a camp queues behind the one before it on the same state, so
 * the concurrent burst on first paint costs one read, and a later card only reads what the earlier
 * ones did not.
 *
 * Entries expire after `PROVENANCE_TTL_MS`. A never-invalidated cache would be wrong for the case this
 * ticket exists to serve: a run that lands mid-sitting is exactly the newer run the reviewer needs told
 * about, and a page left open for hours would keep answering from before it existed.
 */
const PROVENANCE_TTL_MS = 5 * 60 * 1000;
const campRunProvenanceCache = new Map<string, { at: number; state: ProvenanceReadState }>();

/** KB-4361 — newest-first page size for the provenance read. */
export const PROVENANCE_PAGE_SIZE = 20;
/**
 * KB-4361 — the most run docs read for one camp, in either order (about two and a half years of a
 * camp running twice a day). A read that stops here without the coverage a caller asked for says so:
 * one more undatable run, or a {@link CampRunProvenanceCoverageError} for a "newer than" count.
 */
export const PROVENANCE_SCAN_CEILING = 2000;
/** Bound on the two count probes; a count reads one index entry per thousand, so this is ≤ 100 reads. */
export const PROVENANCE_COUNT_LIMIT = 100_000;

/**
 * KB-4361 — the read could not reach back to the instant a caller counts newer runs from (the
 * ceiling stopped it). Rejected rather than answered, so `ProblemReportCard` renders "could not check
 * for newer runs" instead of a count that silently left runs out. Not evicted from the cache: the
 * ceiling is a fact about the camp, and re-reading it on every render would change nothing.
 */
export class CampRunProvenanceCoverageError extends Error {
  constructor(campSlug: string, newerThanIso: string) {
    super(`run history for "${campSlug}" could not be read back to ${newerThanIso} within ${PROVENANCE_SCAN_CEILING} runs`);
    this.name = "CampRunProvenanceCoverageError";
  }
}
/** The only `updatedAtIso` / end-date shape whose sort position is its instant — what every run-doc writer stores. */
const CANONICAL_RUN_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/;
/** Covers the fractional-second sort quirk (`"…05Z"` sorts above `"…05.900Z"`) with room to spare. */
const PROVENANCE_SETTLE_MARGIN_MS = 2000;

/**
 * What a caller will read off the result, so the read can stop at the newest run instead of the
 * camp's whole history.
 */
export interface CampRunProvenanceNeeds {
  /** Run ids the caller looks up in `endedAtIsoByRunId`. Any the newest pages did not reach is read by doc id. */
  runIds?: ReadonlyArray<string | null | undefined>;
  /** The caller counts runs newer than this instant, so the read goes back at least this far. */
  newerThanIso?: string | null;
}

interface ProvenanceReadState {
  campSlug: string;
  dbHandle: Firestore;
  /** Every run doc read so far, paged or by id, keyed by doc id. */
  docs: Map<string, Record<string, unknown>>;
  /** Doc ids read by id that do not exist. */
  missing: Set<string>;
  /** Newest-first paging (`orderBy updatedAtIso desc`). */
  cursor: QueryDocumentSnapshot | null;
  /** The raw `updatedAtIso` of the last doc paged — every unread DATE-LED doc sorts at or below it. */
  lastUpdatedAtIso: string | null;
  paged: number;
  exhausted: boolean;
  /** Whole-history paging in doc-id order — the order, and the doc set, of the pre-KB-4361 read. */
  wholeCursor: QueryDocumentSnapshot | null;
  wholePaged: number;
  wholeExhausted: boolean;
  /**
   * Whether every run doc of the camp has a string `updatedAtIso` in the ISO date range — the only
   * docs the newest-first order can place. `null` until probed. When false, a targeted read falls back
   * to the whole history: a doc with no `updatedAtIso`, a null or non-string one, `""`, or a string
   * outside the range is invisible to, or sorts below, any settle point, and may be newer or undatable.
   */
  allDateLed: boolean | null;
  /** Serializes the reads of every call on this camp. */
  chain: Promise<void>;
}

/** Test seam — drops the memoized reads so each test starts from a clean cache. */
export function __resetCampRunProvenanceCache(): void {
  campRunProvenanceCache.clear();
}

/**
 * First of these that actually PARSES as an instant. Truthiness is not enough: a malformed string
 * still compares lexicographically, so `"9999-garbage"` would win a "newest" comparison and produce a
 * confident claim resting on something that is not a date.
 */
function firstParseableIso(...candidates: Array<string | null | undefined>): string | null {
  for (const c of candidates) {
    const s = String(c || "").trim();
    if (s && !Number.isNaN(Date.parse(s))) return s;
  }
  return null;
}

/** What one camp's run history tells us about the provenance of a card. Read-only, derived, never stored. */
export interface CampRunProvenance {
  /** Newest run id we can see for this camp, or null when the camp has no readable run docs. */
  newestRunId: string | null;
  /** Best-known timestamp per run id — see the ranking chain in `fetchCampRunProvenance`. */
  endedAtIsoByRunId: Record<string, string>;
  /**
   * Runs for this camp we could not date at all. NON-ZERO MEANS THE RANKING IS NOT TRUSTWORTHY: an
   * undatable run could be newer than the one on the card, so "no newer run" cannot be concluded and
   * the card must say it could not check rather than fall silent.
   */
  undatableRuns: number;
}

/**
 * KB-2764 — the camp's run history, so a card can state the age of the snapshot it is showing and
 * whether a newer run exists.
 *
 * ⚠️ READ-ONLY BY CONSTRUCTION, and that is the point. A frozen calibration member takes no write at
 * all (`mergePatternDoc` returns `{op:"frozen"}`); this ticket is disclosure, not refresh. The
 * disclosure is derived here at read time and never persisted back onto the pattern doc — which is why
 * `ReviewPatternCard.staleness.test.tsx`'s "takes no write of any kind while rendering" control holds
 * structurally rather than by assertion alone.
 *
 * ⚠️ NEWEST-FIRST, A PAGE AT A TIME (KB-4361) — and the `orderBy` is what makes the `limit` safe.
 *
 * Without an `orderBy`, a `limit` returns an ARBITRARY subset: capping the read would answer "the
 * newest run" from whichever docs Firestore happened to return — a wrong answer that looks exactly
 * like a right one. So the two go together; you cannot add the limit alone. The read pages
 * `where campSlug == X orderBy updatedAtIso desc limit PROVENANCE_PAGE_SIZE` through the live
 * `campDataRuns (campSlug ASC, updatedAtIso DESC)` index (declared in kb-ff
 * `firebase/firestore.indexes.json`; pro carries none). Until KB-4361 it read the camp's whole history
 * on every card's first paint (~365 docs a year per camp).
 *
 * A call WITH `needs` first asks two index-served counts: the camp's run docs whose `updatedAtIso`
 * is a string from `"1970-"` up to `"2100"`, then all of them. If they differ, some doc cannot be
 * placed by the newest-first order (no `updatedAtIso`, null, `""`, or a non-ISO string), so the read
 * takes the whole history instead. Otherwise it
 * stops when nothing unread can change what the caller reads off the result:
 *   · `newestRunId` — when a datable, finished run is newer than the last doc paged. Every unread doc
 *     sorts at or below that doc's `updatedAtIso`, and a run doc's dates never exceed its own
 *     `updatedAtIso` (the publisher writes `updatedAtIso` = the run's end; the lease reaper and the
 *     Run history UI write both in one update).
 *   · `needs.newerThanIso` — when the last doc paged is older than it, so every run newer is read.
 *     A read the ceiling stops short of it REJECTS ({@link CampRunProvenanceCoverageError}).
 *   · `needs.runIds` — any not yet read is read by doc id rather than by widening the scan.
 * A call with no `needs` reads the whole history in doc-id order — the docs, the order and the answer
 * of the pre-KB-4361 read — one query up to the ceiling.
 *
 * Not covered: an `updatedAtIso` inside the ISO range whose sort position is not its instant — one
 * that is not a real instant (`"2026-13-45…"`), or an offset-bearing one (`"2026-10-07T00:00:00-12:00"`
 * is 12:00Z but sorts as midnight) — on a run below the settle point. Every writer stores a Z-suffixed
 * value; the count probe cannot tell these from one.
 *
 * Newest is decided on TIME, never on the run id's sort order. Ranking walks a three-step fallback
 * chain, measured against 400 prod run docs:
 *
 *   `execution.runEndedAtIso`  — the authoritative fact, present on 364/400
 *   `updatedAtIso`             — present on 400/400, so it closes the 36-doc gap above
 *   `runIdToIso(runId)`        — the id's own timestamp prefix, for anything the two above miss
 *
 * A run that survives all three undated is COUNTED, not silently dropped: an undatable run may be
 * newer than the one on the card, so its existence makes "no newer run" an unsafe conclusion. Dropping
 * it would let the card fall silent over an unknown — the exact defect this disclosure exists to remove.
 * A read that hits `PROVENANCE_SCAN_CEILING` short of the newest run counts one more, for the same
 * reason.
 */
export async function fetchCampRunProvenance(
  campSlug: string,
  dbHandle: Firestore = defaultDb,
  needs?: CampRunProvenanceNeeds,
): Promise<CampRunProvenance> {
  const empty: CampRunProvenance = { newestRunId: null, endedAtIsoByRunId: {}, undatableRuns: 0 };
  if (!campSlug) return empty;

  let entry = campRunProvenanceCache.get(campSlug);
  if (!entry || Date.now() - entry.at >= PROVENANCE_TTL_MS) {
    entry = { at: Date.now(), state: newProvenanceState(campSlug, dbHandle) };
    campRunProvenanceCache.set(campSlug, entry);
  }
  const held = entry;
  const work = held.state.chain.then(() => readCampRunProvenance(held.state, needs));
  held.state.chain = work.then(
    () => undefined,
    () => undefined,
  );
  // A rejected read must not be cached — otherwise one transient failure pins every card for this
  // camp to "could not check" until the TTL expires. Evicted by identity, so a stale read failing late
  // cannot throw away a fresh entry a later call already installed. A coverage rejection is not a
  // failure: the state is good, and re-reading it would hit the same ceiling.
  return work.catch((e) => {
    if (!(e instanceof CampRunProvenanceCoverageError) && campRunProvenanceCache.get(campSlug) === held) campRunProvenanceCache.delete(campSlug);
    throw e;
  });
}

function newProvenanceState(campSlug: string, dbHandle: Firestore): ProvenanceReadState {
  return {
    campSlug,
    dbHandle,
    docs: new Map(),
    missing: new Set(),
    cursor: null,
    lastUpdatedAtIso: null,
    paged: 0,
    exhausted: false,
    wholeCursor: null,
    wholePaged: 0,
    wholeExhausted: false,
    allDateLed: null,
    chain: Promise.resolve(),
  };
}

/** The doc-id → run-id split, and the date the ranking reads, for one run doc. */
function provenanceRowOf(docId: string, raw: Record<string, unknown>) {
  // Doc id is `<slug>__<runId>` (double underscore). The slug itself may contain single underscores,
  // so split on the separator rather than on the last underscore.
  const runId = docId.includes("__") ? docId.slice(docId.indexOf("__") + 2) : docId;
  const data = (raw || {}) as {
    execution?: { runEndedAtIso?: string | null } | null;
    updatedAtIso?: string | null;
    status?: string | null;
  };
  return { runId, data };
}

/** True when the last doc paged is older than `iso` by the margin, so no unread doc is newer than `iso`. */
function pagedPast(state: ProvenanceReadState, iso: string | null): boolean {
  if (state.exhausted) return true;
  const last = String(state.lastUpdatedAtIso ?? "");
  if (!iso || !CANONICAL_RUN_ISO.test(last)) return false;
  return Date.parse(last) + PROVENANCE_SETTLE_MARGIN_MS <= Date.parse(iso);
}

/**
 * The newest datable finished run among the docs read so far, ranked exactly as the derivation below
 * ranks — by the date string — so a newer-by-string run can never sit unread. Only a canonical date
 * settles: a non-canonical one's string rank says nothing about its instant.
 */
function newestReadEndedAt(state: ProvenanceReadState): string | null {
  let newest = "";
  for (const [docId, raw] of state.docs) {
    const { runId, data } = provenanceRowOf(docId, raw);
    if (!runId || String(data.status || "") === "in_progress") continue;
    const endedAt = firstParseableIso(data.execution?.runEndedAtIso, data.updatedAtIso, runIdToIso(runId));
    if (endedAt && endedAt > newest) newest = endedAt;
  }
  return newest && CANONICAL_RUN_ISO.test(newest) ? newest : null;
}

async function pageOnce(state: ProvenanceReadState): Promise<void> {
  const want = Math.min(PROVENANCE_PAGE_SIZE, PROVENANCE_SCAN_CEILING - state.paged);
  const constraints: QueryConstraint[] = [where("campSlug", "==", state.campSlug), orderBy("updatedAtIso", "desc"), limit(want)];
  if (state.cursor) constraints.push(startAfter(state.cursor));
  const snap = await getDocs(query(collection(state.dbHandle, RUNS_COLLECTION), ...constraints));
  for (const d of snap.docs) state.docs.set(d.id, (d.data() || {}) as Record<string, unknown>);
  state.paged += snap.docs.length;
  const last = snap.docs[snap.docs.length - 1];
  if (last) {
    state.cursor = last;
    const lastData = (last.data() || {}) as { updatedAtIso?: unknown };
    state.lastUpdatedAtIso = typeof lastData.updatedAtIso === "string" ? lastData.updatedAtIso : null;
  }
  if (snap.docs.length < want) state.exhausted = true;
}

/**
 * The whole history in doc-id order — every doc, with or without `updatedAtIso`, exactly the set and
 * order the pre-KB-4361 equality-only read returned. One query up to the ceiling. `where ==` plus a
 * `__name__` order is served by the single-field `campSlug` index.
 */
async function readWholeHistory(state: ProvenanceReadState): Promise<void> {
  while (!state.wholeExhausted && state.wholePaged < PROVENANCE_SCAN_CEILING) {
    const want = PROVENANCE_SCAN_CEILING - state.wholePaged;
    const constraints: QueryConstraint[] = [where("campSlug", "==", state.campSlug), orderBy("__name__"), limit(want)];
    if (state.wholeCursor) constraints.push(startAfter(state.wholeCursor));
    const snap = await getDocs(query(collection(state.dbHandle, RUNS_COLLECTION), ...constraints));
    for (const d of snap.docs) state.docs.set(d.id, (d.data() || {}) as Record<string, unknown>);
    state.wholePaged += snap.docs.length;
    state.wholeCursor = snap.docs[snap.docs.length - 1] ?? state.wholeCursor;
    if (snap.docs.length < want) state.wholeExhausted = true;
  }
}

/**
 * The `updatedAtIso` range the newest-first order can place: an ISO date from 1970 up to (not
 * including) 2100. A value outside it — `"1-bad"`, an epoch-ms string, `"10/07/2026"` — sorts below
 * every real date while saying nothing about its instant.
 */
const DATE_LED_FROM = "1970-";
const DATE_LED_BELOW = "2100";

/**
 * Does every run doc of the camp have an ISO-range string `updatedAtIso`? Two counts through the same
 * indexes the reads use: those with `DATE_LED_FROM <= updatedAtIso < DATE_LED_BELOW`, then all docs
 * (`campSlug ==`). A doc with no `updatedAtIso`, a null or non-string one, `""`, or a string outside
 * the range is in the second and not the first.
 *
 * ⚠️ IN THAT ORDER, ONE AFTER THE OTHER. A run created between the two counts then lands in "all"
 * only, and the mismatch falls back to the whole history — the safe direction. Counted the other way
 * round, or concurrently, a new run could offset an unplaceable doc and make the counts agree.
 */
async function probeDateLed(state: ProvenanceReadState): Promise<boolean> {
  if (state.allDateLed !== null) return state.allDateLed;
  const runs = collection(state.dbHandle, RUNS_COLLECTION);
  const dateLed = await getCountFromServer(
    query(
      runs,
      where("campSlug", "==", state.campSlug),
      where("updatedAtIso", ">=", DATE_LED_FROM),
      where("updatedAtIso", "<", DATE_LED_BELOW),
      orderBy("updatedAtIso", "desc"),
      limit(PROVENANCE_COUNT_LIMIT),
    ),
  );
  const all = await getCountFromServer(query(runs, where("campSlug", "==", state.campSlug), limit(PROVENANCE_COUNT_LIMIT)));
  state.allDateLed = all.data().count === dateLed.data().count;
  return state.allDateLed;
}

async function readCampRunProvenance(state: ProvenanceReadState, needs: CampRunProvenanceNeeds | undefined): Promise<CampRunProvenance> {
  const canPage = () => !state.exhausted && state.paged < PROVENANCE_SCAN_CEILING;
  const newerThan = needs?.newerThanIso && !Number.isNaN(Date.parse(needs.newerThanIso)) ? needs.newerThanIso : null;
  const settledNewest = () => pagedPast(state, newestReadEndedAt(state));

  if (state.wholeExhausted) {
    // An earlier call on this camp already read every doc; there is nothing left to read.
  } else if (!needs || !(await probeDateLed(state))) {
    await readWholeHistory(state);
  } else {
    while (!settledNewest() && canPage()) await pageOnce(state);
    if (newerThan) while (!pagedPast(state, newerThan) && canPage()) await pageOnce(state);
  }

  // Covered: the whole history was read, or the newest-first read provably reached the point.
  // `pagedPast` is only trusted when every doc is date-led — otherwise a doc may sit below it.
  const covered = (reached: () => boolean) => state.wholeExhausted || (state.allDateLed === true && reached());
  if (newerThan && !covered(() => pagedPast(state, newerThan))) throw new CampRunProvenanceCoverageError(state.campSlug, newerThan);
  // A call with no needs answers for the whole history, so only a whole read that finished covers it —
  // a settled newest page from an earlier targeted call does not.
  const short = needs ? !covered(settledNewest) : !state.wholeExhausted;

  const byId = [...new Set((needs?.runIds ?? []).map((r) => String(r ?? "").trim()).filter(Boolean))]
    .map((runId) => `${state.campSlug}__${runId}`)
    .filter((docId) => !state.docs.has(docId) && !state.missing.has(docId));
  const snaps = await Promise.all(byId.map((docId) => getDoc(doc(state.dbHandle, RUNS_COLLECTION, docId))));
  snaps.forEach((s, i) => {
    if (s.exists()) state.docs.set(byId[i], (s.data() || {}) as Record<string, unknown>);
    else state.missing.add(byId[i]);
  });

  return deriveCampRunProvenance(state.docs, short ? 1 : 0);
}

/**
 * The ranking, over every run doc read. Iterated in doc-id order — the order the pre-KB-4361
 * equality-only read returned — so a tie on the date string resolves to the same run it always did.
 */
function deriveCampRunProvenance(docs: ReadonlyMap<string, Record<string, unknown>>, unseenRuns: number): CampRunProvenance {
  const endedAtIsoByRunId: Record<string, string> = {};
  let newestRunId: string | null = null;
  let newestEndedAt = "";
  let undatableRuns = unseenRuns;

  for (const docId of [...docs.keys()].sort()) {
    const { runId, data } = provenanceRowOf(docId, docs.get(docId)!);
    if (!runId) continue;

    // A run still IN FLIGHT has not produced a newer snapshot yet, and it may still fail. Ranking it
    // would announce "a newer run exists" pointing at a run with no patterns — and because an
    // in-progress run has the freshest `updatedAtIso` of any run for the camp, it would outrank every
    // completed one. Excluded from ranking, and deliberately NOT counted as undatable either: a run
    // that has not finished is not a newer snapshot the reviewer is missing.
    if (String(data.status || "") === "in_progress") continue;

    // `runEndedAtIso` is the authoritative fact; `updatedAtIso` closes the ~9% of run docs that lack
    // it; the id's own prefix is the last resort. Each candidate must PARSE — accepting on truthiness
    // let a malformed-but-lexicographically-high string win the newest comparison and render a
    // confident claim built on a string that is not a date.
    const endedAt = firstParseableIso(data.execution?.runEndedAtIso, data.updatedAtIso, runIdToIso(runId));
    if (!endedAt) {
      undatableRuns += 1;
      continue;
    }

    endedAtIsoByRunId[runId] = endedAt;
    if (endedAt > newestEndedAt) {
      newestEndedAt = endedAt;
      newestRunId = runId;
    }
  }

  return { newestRunId, endedAtIsoByRunId, undatableRuns };
}

/**
 * KB-2764 — the run id encodes its own timestamp for worker-produced runs
 * (`2026-08-25T07-20-09Z`: the date is ISO, the time uses `-` because `:` is illegal in a doc id).
 * This is the FALLBACK for a run whose doc we could not read; `execution.runEndedAtIso` is preferred
 * wherever it exists. Returns null for anything that does not match — a locally-produced UUID must
 * read as "date unknown", never as a confident wrong date.
 */
export function runIdToIso(runId: string | null | undefined): string | null {
  if (!runId) return null;
  // The trailing `-<8 hex>` is the DOMINANT production shape, not an edge case: of 400 run ids
  // sampled from prod, 389 carry it and only 11 (all from before 2026-07-11) use the bare form.
  // The bare id quoted in KB-2764's description is an abbreviation, and an earlier revision of this
  // function matched only that — which would have read "run date unknown" on ~97% of real runs.
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})Z(?:-[0-9a-f]{6,})?$/.exec(runId.trim());
  if (!m) return null;
  const iso = `${m[1]}T${m[2]}:${m[3]}:${m[4]}Z`;
  return Number.isNaN(Date.parse(iso)) ? null : iso;
}

/**
 * Whole CALENDAR days (UTC) between two instants — not 24-hour periods.
 *
 * The card prints a date and a "N days ago" qualifier side by side, so they must agree. Flooring
 * elapsed milliseconds disagrees with the printed date across a UTC midnight: a run at 23:00 read at
 * 01:00 the next day is 2 hours old, which floors to 0 and prints "(today)" beside YESTERDAY's date.
 * Both halves are derived from the same UTC calendar day so they cannot contradict each other.
 *
 * Negative when `thenIso` is in the future.
 */
export function daysBetween(thenIso: string, nowIso: string): number | null {
  const then = Date.parse(thenIso);
  const now = Date.parse(nowIso);
  if (Number.isNaN(then) || Number.isNaN(now)) return null;
  const utcDay = (ms: number) => {
    const d = new Date(ms);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  };
  return Math.round((utcDay(now) - utcDay(then)) / 86_400_000);
}

/**
 * KB-2764 — "how long ago" in plain words, with NO valence.
 *
 * `deriveStalePatch` (`derivePatternDocs.cjs:452`) refuses to stamp a frozen calibration member
 * precisely because de-emphasizing a card mid-calibration "changes how carefully it gets read — which
 * is a way of nudging one rater that the other never sees." That constraint governs this string too:
 * it states a fact and never editorialises. No "⚠️", no "stale", no "out of date", no urging.
 */
export function describeSnapshotAge(runIso: string | null, nowIso: string): string {
  if (!runIso) return "run date unknown";
  const days = daysBetween(runIso, nowIso);
  const when = formatRunDate(runIso);
  if (days === null) return "run date unknown";
  // A run dated in the FUTURE is an anomaly (clock skew, a mis-stamped doc). Reporting it as "today"
  // would launder the anomaly into the most reassuring reading available, which is the failure mode
  // this whole surface exists to remove. Say what was actually read.
  //
  // Tested on the raw INSTANT, not on `days`: a run stamped three hours ahead lands on the same UTC
  // calendar day, so a `days < 0` test alone would miss it and print a confident "(today)".
  if (Date.parse(runIso) > Date.parse(nowIso)) return `${when} (dated in the future — clock skew?)`;
  if (days < 0) return `${when} (dated in the future — clock skew?)`;
  if (days === 0) return `${when} (today)`;
  if (days === 1) return `${when} (1 day ago)`;
  return `${when} (${days} days ago)`;
}

/** `2026-08-25T07:20:09Z` → `Aug 25, 2026`. UTC so the label never shifts with the reader's timezone. */
export function formatRunDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "date unknown";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/** One page of a pattern's rows. `patternDocId` is the ONLY thing this client gets to name. */
export async function fetchPatternItemsPage(patternDocId: string, offset = 0): Promise<PatternItemsPage> {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const fn = httpsCallable<
    { patternDocId: string; offset: number },
    { rows: Array<Record<string, unknown>>; total: number; nextOffset: number | null; artifactPath?: string }
  >(functions, PATTERN_ITEMS_CALLABLE);
  const res = await fn({ patternDocId, offset });
  return {
    items: (res.data.rows || []).map((r) => toInboxItem(r)),
    total: Number(res.data.total || 0),
    nextOffset: res.data.nextOffset ?? null,
    artifactPath: String(res.data.artifactPath || ""),
  };
}

/**
 * Every row for a pattern, following `nextOffset` to the end.
 *
 * The list this feeds is the answer to "am I sure nothing is missing?", so it pages to exhaustion rather
 * than stopping at the first page: a list that silently showed the first 500 of 900 would reproduce the
 * exact defect this ticket exists to fix, one order of magnitude up. `maxPages` is a runaway guard against
 * a server that returned a non-advancing cursor, not a display cap — it is far above any real pattern.
 */
export async function fetchAllPatternItems(
  patternDocId: string,
  maxPages = 20,
  // Injected so the paging loop — the thing that decides whether the operator sees all N — is unit
  // testable without a callable. Defaults to the real fetch, so no caller changes.
  fetchPage: (id: string, offset: number) => Promise<PatternItemsPage> = fetchPatternItemsPage,
): Promise<{ items: InboxItem[]; total: number; truncated: boolean; artifactPath: string }> {
  const items: InboxItem[] = [];
  let offset: number | null = 0;
  let total = 0;
  let artifactPath = "";
  for (let page = 0; page < maxPages; page += 1) {
    const res: PatternItemsPage = await fetchPage(patternDocId, offset ?? 0);
    items.push(...res.items);
    total = res.total;
    artifactPath = res.artifactPath || artifactPath;
    offset = res.nextOffset;
    if (offset === null) return { items, total, truncated: false, artifactPath };
  }
  return { items, total, truncated: true, artifactPath };
}

/**
 * The number of rows carrying NO individual ruling yet — the X in "Approve all X remaining".
 *
 * Counted over the loaded rows rather than over `counts.items`, and the difference matters: X labels a
 * button that acts on the rows in front of the operator, so deriving it from a count that the row list
 * might not match would let the label promise an action over rows nobody rendered.
 */
export function countUnruled(items: Array<{ itemId: string }>, rulings: Record<string, Verdict>): number {
  return items.reduce((acc, item) => (rulings[item.itemId] ? acc : acc + 1), 0);
}

/**
 * The EFFECTIVE outcome of every loaded row — explicit rulings, with the remainder verdict filling the
 * rest. This is what the pattern-level roll-up is computed over. It is NOT what goes on the wire.
 */
export function buildItemOutcomes(
  items: Array<{ itemId: string }>,
  rulings: Record<string, Verdict>,
  remainderVerdict: Verdict,
): Record<string, Verdict> {
  const out: Record<string, Verdict> = {};
  for (const item of items) {
    if (!item.itemId) continue;
    out[item.itemId] = rulings[item.itemId] ?? remainderVerdict;
  }
  return out;
}

/**
 * The per-item outcomes that actually travel — the operator's EXPLICIT rulings only, never the
 * remainder-filled set.
 *
 * ⚠️ THIS DISTINCTION IS A CORRECTNESS FIX, NOT AN OPTIMISATION. The server merges
 * `{...doc.itemVerdicts, ...itemOutcomes}` — incoming wins. Sending an entry for every loaded row
 * therefore OVERWRITES per-item verdicts already on the doc: a row rejected via `Judge individually`
 * (which writes `mode:"single"` and leaves the pattern open) gets silently flipped to approve by a later
 * `Approve all X remaining`, and `adoptOnApprove` then creates the row the operator rejected. Verified
 * end to end on the emulator. Sending only what the operator actually said this session leaves every
 * untouched prior verdict standing.
 *
 * It also bounds the map by the exception count rather than by N, which is what keeps a 250-row pattern
 * under the 200-entry `ITEM_VERDICTS_CAP` — the cap whose documented escape ("use a batch verdict") is
 * the very path a mixed ruling takes.
 *
 * Scoped to loaded rows so a stale ruling for a row not in this list cannot be re-asserted.
 */
export function explicitItemOutcomes(
  items: Array<{ itemId: string }>,
  rulings: Record<string, Verdict>,
): Record<string, Verdict> {
  const out: Record<string, Verdict> = {};
  for (const item of items) {
    if (!item.itemId) continue;
    const ruling = rulings[item.itemId];
    if (ruling) out[item.itemId] = ruling;
  }
  return out;
}

/**
 * The rulings already recorded on the pattern, as the card's starting state.
 *
 * `Judge individually` writes per-item verdicts to the server and leaves the pattern OPEN, so a card can
 * legitimately open onto a pattern that already carries rejects — from a previous sitting or from ten
 * seconds ago. Not seeding from them showed those rows as `unruled`, so the operator could not see they
 * had already ruled, and the commit then overwrote the ruling they could not see.
 *
 * Filtered to loaded rows and to values this UI can represent; anything else is left alone on the doc
 * rather than being round-tripped through a control that cannot express it.
 */
export function seedRulingsFromPattern(
  items: Array<{ itemId: string }>,
  itemVerdicts: Record<string, string> | undefined,
): Record<string, Verdict> {
  const out: Record<string, Verdict> = {};
  if (!itemVerdicts) return out;
  for (const item of items) {
    const v = itemVerdicts[item.itemId];
    if (v === "approve" || v === "reject") out[item.itemId] = v;
  }
  return out;
}

/**
 * The pattern-level roll-up for a mixed ruling: the verdict the PATTERN gets.
 *
 * `approve` unless every row was rejected. The pattern-level verdict is what the KB-2638 calibration
 * joins on, so it has to mean something stable — "did this pattern's proposal survive review at all?" A
 * pattern where the operator kept 6 of 8 rows is a pattern whose proposal was substantially right, and
 * recording it as a reject would tell the agreement statistic the agent was wrong when it was mostly
 * right. One where nothing survived is a reject, and there is no third answer to give.
 */
export function rollUpPatternVerdict(outcomes: Record<string, Verdict>): Verdict {
  const values = Object.values(outcomes);
  if (values.length === 0) return "approve";
  return values.some((v) => v === "approve") ? "approve" : "reject";
}

export interface ProbeUrlReachabilityResult {
  reachable: boolean;
}

/** KB-2179 — an inspection click only counts toward the min-inspection floor once this confirms
 * the target is reachable; nearly every chip target is cross-origin, so load success is NOT
 * observable client-side without it. Fails closed on any callable error (network, auth, etc.) —
 * the caller treats a thrown/rejected probe the same as `reachable: false`. */
export async function probeUrlReachability(url: string): Promise<ProbeUrlReachabilityResult> {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const fn = httpsCallable<{ url: string }, ProbeUrlReachabilityResult>(functions, PROBE_URL_REACHABILITY_CALLABLE);
  const res = await fn({ url });
  return res.data;
}

/**
 * KB-2181 — fetch the QC judge's Q1 verdict for a pattern.
 *
 * ⚠️ CALL THIS ONLY AFTER THE OPERATOR HAS ANSWERED Q1. `deriveRaterAgreement.cjs` gates tier
 * promotion on the human and the judge being INDEPENDENT raters; showing the judge's answer first
 * turns that statistic from concordance into compliance. Fetching on demand (rather than shipping the
 * verdict with the card) is what makes the judge's answer physically absent from the DOM beforehand.
 *
 * Degrades to "no verdict" on any callable error: a judge line that fails to load must read as
 * "no opinion", never as agreement.
 */
export async function fetchJudgeVerdict(patternDocId: string, runId: string): Promise<JudgeVerdict> {
  const functions = getFunctions(app, FUNCTIONS_REGION);
  const fn = httpsCallable<{ patternDocId: string; runId: string }, JudgeVerdict>(functions, JUDGE_VERDICT_CALLABLE);
  const res = await fn({ patternDocId, runId });
  return res.data;
}

// ── item load for "Judge individually" (degrade-gracefully; item detail lives in GCS — DEC-053) ────

export interface LoadedItems {
  items: InboxItem[];
  /** true when the full item list was fetched from the artifact; false when we fell back to sampleItems. */
  full: boolean;
}

interface RunAuditShape {
  proposedNew?: Array<Record<string, unknown>>;
  review?: { proposals?: Array<Record<string, unknown>> };
  changedOffers?: Array<Record<string, unknown>>;
}

/**
 * Load the full item list for a pattern from the run AUDIT (proposedNew ∪ review.proposals filtered to
 * this pattern's `changeGroupKey`), enriching each change row with title/dates/venue/links from the
 * matching `changedOffers` row (KB-1890 — a change proposal alone carries only docId/from/to). Falls back
 * to the ≤5 already-enriched `sampleItems` when the artifact is absent (recent runs may not upload —
 * KB-1507) or holds no matching rows.
 */
export async function loadPatternItems(pattern: ReviewPattern): Promise<LoadedItems> {
  const fallback: LoadedItems = { items: (pattern.sampleItems || []).map((s) => toInboxItem(s as Record<string, unknown>)), full: false };
  if (!pattern.latestRunId) return fallback;
  const res = await fetchJsonArtifact<RunAuditShape>(pattern.campSlug, pattern.latestRunId, AUDIT_ARTIFACT);
  if (!res.present || !res.data) return fallback;
  const audit = res.data;
  const rows = [...(audit.proposedNew || []), ...((audit.review && audit.review.proposals) || [])];
  const matching = rows.filter((p) => p && (p as Record<string, unknown>).changeGroupKey === pattern.changeGroupKey);
  if (matching.length === 0) return fallback;
  const changedByDocId = new Map<string, Record<string, unknown>>();
  for (const co of audit.changedOffers || []) {
    const docId = co && (co.docId as string);
    if (docId) changedByDocId.set(String(docId), co);
  }
  return {
    items: matching.map((p) => toInboxItem(p, changedByDocId.get(String((p.offerId as string) || (p.docId as string) || "")))),
    full: true,
  };
}
